import type { Dictionary } from "@/lib/i18n/dictionaries/en";

const de: Dictionary = {
  // ---- /citation-styles/alphabetical/<letter> ----
  styleDirectory: {
    notFoundTitle: "Zitierstil-Verzeichnis nicht gefunden",
    metaTitle: "Zitierstile, die mit {label} beginnen",
    metaDescription:
      "Durchsuchen Sie {count} Citation-Style-Language-Stile, die mit {label} beginnen. Öffnen Sie einen Stil für echte Zitierbeispiele und einen kostenlosen Generator.",
    ogSubtitle: "{count} Stile mit eigenen Beispielen und Generatoren.",
    ogKind: "A–Z-Verzeichnis",
    schemaName: "Zitierstile, die mit {label} beginnen",
    schemaDescription:
      "Alphabetisches Verzeichnis von {count} Citation-Style-Language-Stilen, die mit {label} beginnen.",
    breadcrumbStyles: "Zitierstile",
    heading: "Zitierstile, die mit {label} beginnen",
    intro:
      "Durchsuchen Sie alle {count} Stile in diesem Teil des CSL-Katalogs. Jede Seite enthält die formatierten Beispiele des Stils und einen kostenlosen Zitiergenerator.",
    navLabel: "Zitierstile von A bis Z",
    listHeading: "Alle Stile, die mit {label} beginnen",
  },
  nav: {
    tools: "Werkzeuge",
    guides: "Ratgeber",
    styles: "Alle Zitierstile",
    bibliography: "Mein Literaturverzeichnis",
    search: "Suchen",
    glossary: "Glossar",
    home: "Startseite",
    menu: "Menü",
  },

  home: {
    title: "Kostenloser Zitiergenerator",
    subtitle: "Aktualisiert für 2026",
    intro:
      "Fügen Sie einen DOI, eine URL oder einen BibTeX-Eintrag ein — oder suchen Sie nach einem Buch — und erhalten Sie eine korrekt formatierte Quellenangabe in APA, MLA, Chicago, Vancouver, Harvard und Tausenden weiteren Stilen. Völlig kostenlos, ohne kostenpflichtiges Abonnement und ohne Anmeldung.",
    browseAllStyles: "Alle {count} Zitierstile durchsuchen →",
    featuresHeading: "Alles, was Sie zum Zitieren brauchen",
    featureStyles: "{count} Stile",
    featureStylesDetail: "APA, MLA, Chicago und mehr",
    featureBibliographies: "Mehrere Literaturverzeichnisse",
    featureBibliographiesDetail: "Nach Projekt sortiert",
    featureBookSearch: "Büchersuche",
    featureBookSearchDetail: "Nach Titel oder Autor",
    featureExport: "Überallhin exportieren",
    featureExportDetail: "BibTeX, Word, RIS, CSV",
    featureDarkMode: "Dunkler Modus",
    featureDarkModeDetail: "Schont die Augen",
    featureNoSignup: "Ohne Anmeldung",
    featureNoSignupDetail:
      "Dauerhaft kostenlos, kein kostenpflichtiges Abonnement",
    aboutHeading: "Ein kostenloser Zitiergenerator für Studium und Forschung",
    aboutBody:
      "CitationEasy unterstützt über 10.000 wissenschaftliche Zitierstile. Ob APA für Psychologie, MLA für Literaturwissenschaft, Chicago für Geschichte oder Vancouver für medizinische Fachzeitschriften: Die Formatierung stammt aus der offiziellen Definition des jeweiligen Stils und nicht aus einer Näherung.",
    howItWorksHeading: "So funktioniert es",
    howItWorksStep1:
      "Fügen Sie einen DOI, eine URL, eine ISBN, eine PubMed- oder arXiv-ID oder einen BibTeX-Eintrag ein — oder suchen Sie ein Buch nach Titel.",
    howItWorksStep2:
      "Wählen Sie Ihren Zitierstil. Tippen Sie, um unter allen {count} zu suchen.",
    howItWorksStep3:
      "Kopieren Sie die fertige Quellenangabe oder speichern Sie sie in einem Literaturverzeichnis und exportieren Sie nach Word, BibTeX oder RIS.",
    privacyNote:
      "Ihr Literaturverzeichnis wird in Ihrem eigenen Browser gespeichert. Kein Konto, und nichts wird hochgeladen.",
  },

  hubs: {
    toolsTitle: "Kostenlose Werkzeuge zum Schreiben und Zitieren",
    toolsIntro:
      "{count} Werkzeuge direkt im Browser für das Schreiben und Zitieren: Generatoren für jede Quellenart, Wort- und Zeichenzähler, Lesbarkeitsanalyse, Formatierungshilfen und Konverter. Alle kostenlos und sofort einsatzbereit.",
    stylesTitle: "Alle Zitierstile durchsuchen",
    stylesIntro:
      "Alle {count} Stile des Citation-Style-Language-Projekts, durchsuchbar und filterbar nach Land, Fachgebiet und Disziplin.",
    guidesTitle: "Ratgeber zum Zitieren und wissenschaftlichen Schreiben",
    guidesIntro:
      "Ausführliche Ratgeber dazu, wie man einzelne Quellenarten zitiert, Fundstellen bewertet und besser schreibt.",
    howToCiteTitle: "So zitieren Sie jede Quelle",
    howToCiteIntro:
      "Schritt-für-Schritt-Seiten, die jede gängige Quellenart mit den wichtigsten Zitierstilen verbinden — jeweils mit einem echten ausgearbeiteten Beispiel.",
    faqTitle: "Häufige Fragen zum Zitieren",
    faqIntro:
      "Kurze, verlässliche Antworten auf die Fragen, die Studierende beim Zitieren wirklich stellen.",
    glossaryTitle: "Glossar zum Zitieren",
    glossaryIntro:
      "Verständliche Definitionen der Begriffe, die in Stilhandbüchern und Aufgabenstellungen vorkommen.",
    openHub: "Öffnen",
  },

  styleLanding: {
    metaTitle: "Kostenloser {style}-Zitiergenerator",
    metaDescription:
      "Erstellen Sie {style}-Quellenangaben kostenlos. Fügen Sie einen DOI, eine URL, eine ISBN oder BibTeX ein und erhalten Sie Literaturangabe und Kurzbeleg im korrekten {style}-Format.",
    heading: "{style}-Zitiergenerator",
    intro:
      "Erstellen Sie mit einem Klick korrekte {style}-Quellenangaben für Bücher, Zeitschriftenartikel und Webseiten. Kostenlos und ohne Anmeldung.",
    aboutHeading: "Über {style}",
    whenToUseHeading: "Wann {style} verwendet wird",
    factsHeading: "{style} auf einen Blick",
    factEdition: "Ausgabe",
    factSystem: "Zitiersystem",
    factInText: "Format im Text",
    factReferenceList: "Name des Verzeichnisses",
    factFields: "Üblich in",
    systemAuthorDate: "Autor–Jahr",
    systemNumbered: "Numerisch",
    systemFootnotes: "Fußnoten",
    howToHeading: "So verwenden Sie diesen {style}-Generator",
    howToStep1:
      "Fügen Sie oben eine URL, einen DOI, eine ISBN oder einen BibTeX-Eintrag ein — oder suchen Sie ein Buch nach Titel.",
    howToStep2:
      "{style} ist auf dieser Seite bereits ausgewählt, Sie können also sofort loslegen.",
    howToStep3:
      "Kopieren Sie das Ergebnis in Ihr Dokument oder speichern Sie es und exportieren Sie später die gesamte Liste.",
    relatedHeading: "Weitere Ressourcen zu {style}",
    englishOnlyNote:
      "Ausführliche {style}-Ratgeber und Beispiele nach Quellenart stehen auf Englisch zur Verfügung.",
  },

  // The per-style pages at `/<locale>/citation-style/<slug>` — one per catalog style.
  //
  // The formatted examples the page is built around (the style's own output for a
  // fixed journal article, book and website) and the style's own name stay in
  // `src/data/citation-styles.ts` and `src/data/style-examples-generated.json`. They
  // are language-neutral: a citation rendered by the style's own CSL definition looks
  // the same whatever language the reader speaks, and "translating" one would make the
  // page wrong.
  //
  // The rest of what `src/data/**` supplies to these pages is NOT like that — see the
  // `generic*`, `discipline*` and `internationalLabel` keys below.
  citationStylePage: {
    notFoundTitle: "Zitierstil nicht gefunden",
    metaTitle: "{style}-Zitiergenerator - Kostenloses {style}-Format",
    metaFormatAndFields:
      "{style} ist ein {format}-Stil und wird in {fields} verwendet.",
    metaFormatOnly: "{style} ist ein {format}-Stil.",
    metaFieldsOnly: "{style} wird in {fields} verwendet.",
    metaPromiseNote:
      "Sehen Sie eine echte {style}-Fußnote und erstellen Sie kostenlos Ihre eigene.",
    metaPromiseExamples:
      "Sehen Sie echte {style}-Zitierbeispiele für einen Zeitschriftenartikel, ein Buch und eine Website, und erstellen Sie kostenlos Ihre eigenen.",
    ogKindFormat: "{format}-Stil",
    ogKindDefault: "Zitierstil",
    // Joins the style's discipline list: "psychology, education and nursing".
    disciplineConjunction: "und",
    // ---- Data-derived facts that are nonetheless mail-merged prose ----
    //
    // Every entry in `src/data/citation-styles-generated.ts` carries exactly the
    // two sentences below with their own name spliced in, exactly one region value
    // ("International" — that is `internationalLabel` above, reused rather than
    // duplicated), and discipline tokens drawn from a closed 26-word vocabulary. None
    // of that is a citation, so none of it is language-neutral the way the formatted
    // examples are. Left in `src/data/**` it renders English facts inside translated
    // chrome, which is precisely the hybrid page this tree exists to avoid.
    //
    // The page substitutes the two templates ONLY when a style's stored text is
    // byte-identical to the English template with its own name interpolated, so the
    // ~100 hand-curated styles keep their bespoke English descriptions instead of
    // being flattened into a generic sentence. `{name}` is the style's full name.
    genericDescription:
      "Zitierstil für {name}. Aus dem Repository der Citation Style Language.",
    genericWhenUsed:
      "Für Einreichungen bei oder Zitate in Publikationen, die den Stil {name} verwenden.",

    // The 26 discipline tokens `style-examples-generated.json` uses. Each English
    // value is the exact string the page printed before these keys existed — the raw
    // token with hyphens turned into spaces — so `generic-base` reads "generic base"
    // and `political_science` keeps its underscore. An unknown token (a new one from
    // upstream CSL) falls back to that same prettified form rather than to a key name.
    disciplineAnthropology: "Anthropologie",
    disciplineAstronomy: "Astronomie",
    disciplineBiology: "Biologie",
    disciplineBotany: "Botanik",
    disciplineChemistry: "Chemie",
    disciplineCommunications: "Kommunikationswissenschaft",
    disciplineEngineering: "Ingenieurwissenschaften",
    disciplineGenericBase: "Allgemein",
    disciplineGeography: "Geografie",
    disciplineGeology: "Geologie",
    disciplineHistory: "Geschichte",
    disciplineHumanities: "Geisteswissenschaften",
    disciplineLaw: "Recht",
    disciplineLinguistics: "Linguistik",
    disciplineLiterature: "Literaturwissenschaft",
    disciplineMath: "Mathematik",
    disciplineMedicine: "Medizin",
    disciplinePhilosophy: "Philosophie",
    disciplinePhysics: "Physik",
    disciplinePoliticalScience: "Politikwissenschaft",
    disciplinePsychology: "Psychologie",
    disciplineScience: "Naturwissenschaften",
    disciplineSocialScience: "Sozialwissenschaften",
    disciplineSociology: "Soziologie",
    disciplineTheology: "Theologie",
    disciplineZoology: "Zoologie",

    // Plain-language descriptions of the citation-format classes the CSL definitions
    // declare. `{format}` in the fallback is the raw class name from the data.
    formatClassAuthorDate:
      "ein Autor-Jahr-Stil: Kurzbelege im Text nennen Autor und Jahr, und das Literaturverzeichnis ist alphabetisch nach Autor geordnet",
    formatClassNumeric:
      "ein numerischer Stil: Quellen werden in der Reihenfolge ihrer ersten Nennung nummeriert, und Kurzbelege im Text verwenden diese Nummern",
    formatClassNote:
      "ein Fußnotenstil: Belege erscheinen in Fuß- oder Endnoten statt in Klammern im Text",
    formatClassLabel:
      "ein Kurzform-Stil: Kurzbelege im Text verwenden eine knappe Kennung aus Autorenname und Jahr",
    formatClassAuthor:
      "ein reiner Autor-Stil: Kurzbelege im Text nennen nur den Autor, alle Angaben stehen im Literaturverzeichnis",
    formatClassOther: "ein {format}-Stil",
    breadcrumbStyles: "Zitierstile",
    internationalLabel: "International",
    heading: "{style}-Zitiergenerator",
    intro:
      "Erstellen Sie mit einem Klick korrekte {style}-Quellenangaben für Bücher, Artikel und Websites.",
    examplesHeading: "{style}-Zitierbeispiele",
    examplesIntro: "Dieselben drei Quellen formatiert in {style}:",
    examplesIntroWithFormat:
      "Dieselben drei Quellen formatiert in {style} — {format}:",
    examplesNoteOnlyIntro:
      "{style} ist ein Fußnotenstil: Es gibt kein Literaturverzeichnis, jeder Beleg ist eine Fußnote. Hier ist unser Beispiel-Zeitschriftenartikel, zitiert nach {style}:",
    labelJournalArticle: "Zeitschriftenartikel",
    labelBook: "Buch",
    labelWebsite: "Website",
    labelFootnote: "Fußnote (Zeitschriftenartikel)",
    labelInText: "Beleg im Text (zum Zeitschriftenartikel)",
    aboutHeading: "Über {style}",
    whenToUseLabel: "Wann {style} verwendet wird:",
    commonInLabel: "Üblich in:",
    subjectAreasLabel: "Fachgebiete:",
    formattingRulesLabel: "Formatierungsregeln:",
    parentStylePrefix: "{style} folgt der Formatierung von",
    parentStyleSuffix: ", dem übergeordneten Stil, auf dem er basiert.",
    howToHeading: "So verwenden Sie diesen {style}-Generator",
    howToBody:
      'Fügen Sie oben einen DOI, eine URL zu einem Nachrichtenartikel oder einer Webseite, oder rohen BibTeX-Text ein. Sie können auch ein Buch nach Titel oder Autor suchen und es über die ISBN zitieren. Wählen Sie "{style}" im Stil-Menü (auf dieser Seite bereits vorausgewählt) und klicken Sie dann auf "Beleg erzeugen". Kopieren Sie das Ergebnis in Ihr Literaturverzeichnis oder Ihre Quellenliste. Sie können den erzeugten Eintrag vor dem Kopieren noch bearbeiten (z. B. einen Titel korrigieren oder ein Datum ergänzen).',
    faqHeading: "Häufig gestellte Fragen zu {style}",
    faqWhatIsQ: "Was ist {style}?",
    faqWhenQ: "Wann sollte ich {style} verwenden?",
    faqInTextQ: "Wie sieht ein Beleg im Text nach {style} aus?",
    faqInTextA:
      "{style} ist {format}. Der Beleg für unseren Beispiel-Zeitschriftenartikel sieht im Text so aus: {example}",
    faqReferenceQ:
      "Wie sieht ein Eintrag im Literaturverzeichnis nach {style} aus?",
    faqReferenceA:
      "Ein nach {style} formatierter Zeitschriftenartikel sieht so aus: {example}",
    faqWhereQ: "Wo wird {style} üblicherweise verwendet?",
    faqWhereA: "{style} wird {region} verwendet. {whenUsed}",
    faqRegionDefault: "international",
    faqGenerateQ: "Wie kann ich einen Beleg nach {style} erzeugen?",
    faqGenerateA:
      "Nutzen Sie den kostenlosen Generator auf dieser Seite: Fügen Sie einen DOI, eine URL (z. B. zu einem Nachrichtenartikel) oder BibTeX ein, oder suchen Sie ein Buch nach Titel oder Autor. Wählen Sie {style} als Zitierstil und klicken Sie auf Erzeugen, um eine formatierte Quellenangabe zu erhalten, die Sie in Ihre Arbeit oder Ihr Literaturverzeichnis kopieren können.",
    footerSupports:
      "unterstützt viele Zitierstile aus aller Welt. Unser Generator verwendet Citation Style Language (CSL)-Stile aus dem",
    footerRepoLink: "offiziellen CSL-Repository",
  },

  // The source × style pages at `/<locale>/how-to-cite/<slug>` — 1,290 combinations.
  //
  // Same division as above: the citation templates, the worked examples generated from
  // each style's own CSL definition, the in-text samples and the hand-checked
  // formatting rules stay in the page module and in
  // `src/data/how-to-cite-examples-generated.json`. They are the citations themselves,
  // not prose about them. Source names, style names and editions come from
  // `src/data/how-to-cite.ts` and arrive here as placeholder values.
  crossLinks: {
    generateHeading: "Diese Quellenangabe erstellen",
    generateBody:
      "URL, DOI, ISBN oder die Angaben zur Quelle einfügen und eine fertige {style}-Quellenangabe für {source} erhalten.",
    generateCta: "{tool} öffnen →",
    guideHeading: "Mehr erfahren",
    guideCta: "Den vollständigen Leitfaden lesen: {guide}",
    howToCiteHeading: "So zitieren Sie {source} in jedem Zitierstil",
    howToCiteBody:
      "Formatregeln, Vorlagen und ausgearbeitete Beispiele für die am häufigsten verlangten Stile.",
    howToCiteMore: "Alle Quellentypen und Stile →",
    citeNowHeading: "Jetzt {source} nach {style} zitieren",
    citeNowHowTo: "Formatregeln und ausgearbeitete Beispiele",
    citeNowTool: "Mit dem {tool} erstellen",
    citeNowHub: "{style}-Zitiergenerator",
    hubSourcesHeading: "Häufige Quellen in {style} zitieren",
    hubGuidesHeading: "{style}-Leitfäden",
    hubCompareHeading: "{style} mit anderen Stilen vergleichen",
    moreInCollection: "Weitere Werkzeuge: {collection}",
    seeAllInCollection: "Alle anzeigen: {collection}",
  },

  howToCitePage: {
    notFoundTitle: "Nicht gefunden",
    title: "So zitieren Sie {source} nach {style} ({edition})",
    metaTitle:
      "So zitieren Sie {source} nach {style} [{edition}, aktualisiert 2026]",
    metaDescription:
      "Erfahren Sie, wie Sie {description} im {style}-Format ({edition}) zitieren. Kostenlose Beispiele, Vorlagen und Anleitungen für Belege im Text.",
    keywordCite: "{source} zitieren {style}",
    keywordCitation: "{style} {source} Quellenangabe",
    keywordHowTo: "{source} zitieren",
    keywordExample: "{style} Zitierbeispiel",
    keywordReference: "{source} Quellenangabe {style}",
    // What each style calls the list at the back of the paper, in the two casings the
    // prose needs. MLA says "Works Cited", Chicago and the legal styles "Bibliography".
    refListWorksCited: "Zitierte Werke",
    refListWorksCitedPage: "Zitierte Werke",
    refListBibliography: "Bibliografie",
    refListBibliographyLower: "Bibliografie",
    refListReferenceList: "Literaturverzeichnis",
    refListReferenceListLower: "Literaturverzeichnis",
    backLink: "← Alle Zitierratgeber",
    lead: "Erfahren Sie, wie Sie {description} im {style}-Format ({edition}) zitieren. Dieser Ratgeber enthält das Format für das Literaturverzeichnis, Beispiele für Belege im Text und die wichtigsten Formatierungsregeln.",
    referenceFormatHeading: "{style}-{list}-Format",
    referenceFormatIntro:
      "Hier ist das Standardformat nach {style}, um {source} im Abschnitt „{list}“ anzugeben:",
    noTemplateNote:
      "Folgen Sie für diesen Quellentyp der Standardvorlage von {style}.",
    workedHeading: "{style}-Beispiel für {source}",
    workedIntro:
      "Eine vollständige {style}-Quellenangabe für {source}, formatiert nach der offiziellen {style}-CSL-Stildefinition — derselben Engine, die auch den Generator auf dieser Seite antreibt:",
    workedNoteOnlyIntro:
      "{style} sieht für {source} keinen Eintrag im Abschnitt „{list}“ vor — die Quelle wird nur in einer Fußnote zitiert. Hier ist diese Fußnote, formatiert nach der offiziellen {style}-CSL-Stildefinition:",
    workedOscolaNote:
      "Die auf dieser Website erzeugte OSCOLA-Ausgabe folgt dem CSL-Stil der 4. Auflage, weil im Repository der Citation Style Language noch kein CSL-Stil der 5. Auflage veröffentlicht ist. Passen Sie sie von Hand an: Ersetzen Sie jedes 'ibid' durch den Kurznamen und (n X), verwenden Sie die Gerichtskennungen der 5. Auflage, setzen Sie die Kurzform eines Gesetzes in einfache Anführungszeichen und bevorzugen Sie einen dauerhaften Link oder DOI vor dem Abrufdatum",
    workedCheckedIntro:
      "Eine vollständige {style}-Quellenangabe für {source}, von Hand erstellt und mit den offiziellen {style}-Vorgaben für diesen Quellentyp abgeglichen:",
    workedCheckedNoteOnlyIntro:
      "{style} sieht für {source} keinen Eintrag im Abschnitt „{list}“ vor — die Quelle wird nur in einer Fußnote zitiert. Hier ist diese Fußnote, von Hand erstellt und mit den offiziellen {style}-Vorgaben abgeglichen:",
    workedInTextLabel: "Passender Beleg im Text:",
    inTextHeading: "Beleg im Text nach {style}",
    inTextIntro:
      "Verwenden Sie eines dieser Formate, wenn Sie in Ihrer Arbeit auf diese Quelle verweisen:",
    inTextParenthetical: "Klammerbeleg",
    inTextNarrative: "Fließtextbeleg",
    rulesHeading: "Wichtige Formatierungsregeln für {style}",
    stepsHeading: "Schritt-für-Schritt-Anleitung",
    step1Label: "Quelleninformationen sammeln:",
    step1Body:
      "Sammeln Sie Autorenname, Titel, Erscheinungsdatum sowie URL oder DOI Ihrer Quelle (Quellentyp: {source}).",
    step2Label: "Quellenangabe formatieren:",
    step2Body:
      "Ordnen Sie die Angaben gemäß der oben gezeigten {style}-Vorlage ({edition}).",
    step3Label: "Beleg im Text erstellen:",
    step3Body:
      "Fügen Sie an der Stelle, an der Sie diese Quelle in Ihrer Arbeit erwähnen, einen Klammerbeleg oder Fließtextbeleg ein.",
    step4Label: "Zu {list} hinzufügen:",
    step4Body:
      "Fügen Sie den vollständig formatierten Beleg am Ende Ihrer Arbeit ein.",
    step5Label: "Formatierung überprüfen:",
    step5Body:
      "Prüfen Sie, ob Kursivsetzung, Zeichensetzung, Groß-/Kleinschreibung und hängender Einzug den Vorgaben von {style} entsprechen.",
    ctaHeading: "Diesen Beleg automatisch erzeugen",
    ctaBody:
      "Sparen Sie sich das manuelle Formatieren. Fügen Sie einen DOI, eine URL oder eine ISBN in CitationEasy ein und erhalten Sie in Sekunden einen perfekt formatierten Beleg nach {style}.",
    ctaButton: "Jetzt nach {style} zitieren →",
    otherStylesHeading: "So zitieren Sie {source} in anderen Stilen",
    comboLink: "{source} nach {style}",
    otherSourcesHeading: "Weitere Quellen nach {style} zitieren",
    resourcesHeading: "Weitere Ressourcen",
    resourceGenerator: "Kostenloser Zitiergenerator ({count} Stile)",
    resourceTools: "Kostenlose Schreibwerkzeuge",
    resourceCompare: "Zitierstile vergleichen",
    resourceGuides: "Alle Zitierratgeber",
    resourceCheatSheets: "Spickzettel (APA, MLA, Chicago)",
    resourceGlossary:
      "Glossar zum Zitieren (et al., ibid., DOI, hängender Einzug)",
    // HowTo structured data. Deliberately worded differently from the visible steps
    // above: the schema describes the task generically, the page walks the reader
    // through this particular source and style.
    schemaStep1Name: "Quelleninformationen sammeln",
    schemaStep1Text:
      "Sammeln Sie alle erforderlichen Angaben zu Ihrer Quelle (Quellentyp: {source}), einschließlich Autor, Titel, Datum sowie URL oder DOI.",
    schemaStep2Name: "Nach {style} formatieren",
    schemaStep2Text:
      "Folgen Sie den Formatierungsregeln von {style} ({edition}), um die Bestandteile des Belegs in der richtigen Reihenfolge anzuordnen.",
    schemaStep3Name: "Beleg im Text erstellen",
    schemaStep3Text:
      "Fügen Sie in Ihrer Arbeit einen passenden Beleg im Text im {style}-Format ein.",
    schemaStep4Name: "Zum Literaturverzeichnis hinzufügen",
    schemaStep4Text:
      "Fügen Sie den vollständigen Beleg in {list} am Ende Ihrer Arbeit ein.",
    faqHowQ: "Wie zitiere ich {source} nach {style}?",
    faqHowA:
      "Um {source} nach {style} ({edition}) zu zitieren, geben Sie Autor, Titel, Datum und Fundort der Quelle an. Nutzen Sie unseren kostenlosen Zitiergenerator für die automatische Formatierung.",
    faqFormatQ: "Wie lautet das {style}-Format für {source}?",
    faqFormatA: "Das {style}-Format für {source} lautet: {example}",
    faqFormatFallback:
      "Das {style}-Format verlangt bestimmte Angaben wie Autor, Titel, Datum und Fundort der Quelle in einer festgelegten Reihenfolge.",
    faqInTextQ: "Wie sieht der Beleg im Text für {source} nach {style} aus?",
    faqInTextA:
      "Nach {style} lautet der Beleg im Text für das obige Beispiel {example}",
  },

  // The 43 source types the how-to-cite pages are built from (`SOURCE_TYPES` in
  // `src/data/how-to-cite.ts`), in the five forms those pages print. Each form is a whole
  // phrase, never an article and a noun joined in code: an article agrees with its noun
  // in gender, number and case, elides in French and Italian, and has no counterpart
  // at all in Polish, Russian or Japanese.
  //
  // - `…Name` ("Book") is the type on its own, as a label: `howToCitePage.comboLink`.
  //   English also lower-cases it into the `keyword*` templates.
  // - `…Title` ("a Book") is the type as the object of "cite" in a title or heading:
  //   `title`, `metaTitle`, `workedHeading` and `otherStylesHeading`, plus
  //   `crossLinks.generateBody`, `howToCiteHeading` and `citeNowHeading`. Through
  //   `title` it is also the HowTo name, the OG card and every link to the page.
  // - `…Phrase` ("a book") is the same inside a sentence: `referenceFormatIntro`,
  //   `workedIntro`, `workedNoteOnlyIntro` and the five `faq*` questions and answers.
  // - `…Noun` ("book") is the bare noun inside a sentence, where English puts "your" in
  //   front of it: `step1Body` and `schemaStep1Text`. A translation also uses it in the
  //   `keyword*` templates, which read as search queries.
  // - `…Description` is what the type covers: `{description}` in `lead` and
  //   `metaDescription`.
  //
  // A language with case picks one case per form and words every template above around
  // it — German writes Title and Phrase in the accusative, Polish and Russian write Name
  // and Noun in the nominative. Changing a form's case means re-reading every template
  // that receives it. A key missing here falls back to the English data
  // (`howToCiteSourceForms`), so a new source type renders in English, not as a key.
  // German: Title and Phrase are accusative ("einen Podcast", "die Bibel"); Name and Noun nominative.
  howToCiteSources: {
    websiteName: "Website",
    websiteTitle: "eine Website",
    websitePhrase: "eine Website",
    websiteNoun: "Website",
    websiteDescription: "Webseiten, Online-Artikel und Webinhalte",

    bookName: "Buch",
    bookTitle: "ein Buch",
    bookPhrase: "ein Buch",
    bookNoun: "Buch",
    bookDescription: "gedruckte und elektronische Bücher",

    journalArticleName: "Zeitschriftenartikel",
    journalArticleTitle: "einen Zeitschriftenartikel",
    journalArticlePhrase: "einen Zeitschriftenartikel",
    journalArticleNoun: "Zeitschriftenartikel",
    journalArticleDescription:
      "begutachtete und wissenschaftliche Zeitschriftenartikel",

    youtubeVideoName: "YouTube-Video",
    youtubeVideoTitle: "ein YouTube-Video",
    youtubeVideoPhrase: "ein YouTube-Video",
    youtubeVideoNoun: "YouTube-Video",
    youtubeVideoDescription: "YouTube- und andere Online-Videos",

    podcastName: "Podcast",
    podcastTitle: "einen Podcast",
    podcastPhrase: "einen Podcast",
    podcastNoun: "Podcast",
    podcastDescription: "Podcastfolgen und -serien",

    newspaperArticleName: "Zeitungsartikel",
    newspaperArticleTitle: "einen Zeitungsartikel",
    newspaperArticlePhrase: "einen Zeitungsartikel",
    newspaperArticleNoun: "Zeitungsartikel",
    newspaperArticleDescription:
      "Zeitungsartikel aus Print- und Online-Ausgaben",

    magazineArticleName: "Magazinartikel",
    magazineArticleTitle: "einen Magazinartikel",
    magazineArticlePhrase: "einen Magazinartikel",
    magazineArticleNoun: "Magazinartikel",
    magazineArticleDescription: "Magazinartikel aus Print- und Online-Ausgaben",

    blogPostName: "Blogbeitrag",
    blogPostTitle: "einen Blogbeitrag",
    blogPostPhrase: "einen Blogbeitrag",
    blogPostNoun: "Blogbeitrag",
    blogPostDescription: "Blogbeiträge und Meinungsbeiträge im Netz",

    wikipediaName: "Wikipedia",
    wikipediaTitle: "Wikipedia",
    wikipediaPhrase: "Wikipedia",
    wikipediaNoun: "Wikipedia",
    wikipediaDescription: "Wikipedia-Artikel und Wiki-Einträge",

    pdfName: "PDF",
    pdfTitle: "ein PDF",
    pdfPhrase: "ein PDF",
    pdfNoun: "PDF",
    pdfDescription: "PDF-Dokumente und -Berichte",

    interviewName: "Interview",
    interviewTitle: "ein Interview",
    interviewPhrase: "ein Interview",
    interviewNoun: "Interview",
    interviewDescription:
      "selbst geführte, veröffentlichte und ausgestrahlte Interviews",

    socialMediaName: "Social-Media-Beitrag",
    socialMediaTitle: "einen Social-Media-Beitrag",
    socialMediaPhrase: "einen Social-Media-Beitrag",
    socialMediaNoun: "Social-Media-Beitrag",
    socialMediaDescription:
      "Tweets, Instagram-Posts, Facebook-Beiträge und TikToks",

    governmentDocumentName: "Regierungsdokument",
    governmentDocumentTitle: "ein Regierungsdokument",
    governmentDocumentPhrase: "ein Regierungsdokument",
    governmentDocumentNoun: "Regierungsdokument",
    governmentDocumentDescription:
      "Regierungsberichte, Gesetze und amtliche Dokumente",

    dissertationName: "Dissertation oder Abschlussarbeit",
    dissertationTitle: "eine Dissertation oder Abschlussarbeit",
    dissertationPhrase: "eine Dissertation oder Abschlussarbeit",
    dissertationNoun: "Dissertation oder Abschlussarbeit",
    dissertationDescription: "Dissertationen und Masterarbeiten",

    conferencePaperName: "Konferenzbeitrag",
    conferencePaperTitle: "einen Konferenzbeitrag",
    conferencePaperPhrase: "einen Konferenzbeitrag",
    conferencePaperNoun: "Konferenzbeitrag",
    conferencePaperDescription: "Tagungsbände und Konferenzvorträge",

    lectureNotesName: "Vorlesung oder PowerPoint-Folien",
    lectureNotesTitle: "eine Vorlesung oder PowerPoint-Folien",
    lectureNotesPhrase: "eine Vorlesung oder PowerPoint-Folien",
    lectureNotesNoun: "Vorlesung oder PowerPoint-Folien",
    lectureNotesDescription: "Vorlesungen, Folien und Kursmaterialien",

    webinarName: "Webinar",
    webinarTitle: "ein Webinar",
    webinarPhrase: "ein Webinar",
    webinarNoun: "Webinar",
    webinarDescription:
      "Live-Webinare, aufgezeichnete Webinare und Online-Seminare",

    onlineCourseName: "Online-Kurs",
    onlineCourseTitle: "einen Online-Kurs",
    onlineCoursePhrase: "einen Online-Kurs",
    onlineCourseNoun: "Online-Kurs",
    onlineCourseDescription:
      "Online-Kurse und MOOCs (Coursera, edX, Udemy, FutureLearn)",

    presentationSlidesName: "PowerPoint-Präsentation",
    presentationSlidesTitle: "eine PowerPoint-Präsentation",
    presentationSlidesPhrase: "eine PowerPoint-Präsentation",
    presentationSlidesNoun: "PowerPoint-Präsentation",
    presentationSlidesDescription: "PowerPoint- und andere Präsentationsfolien",

    imageName: "Bild oder Foto",
    imageTitle: "ein Bild oder Foto",
    imagePhrase: "ein Bild oder Foto",
    imageNoun: "Bild oder Foto",
    imageDescription:
      "Fotos, Kunstwerke, Diagramme und Bilder aus dem Internet",

    movieName: "Film",
    movieTitle: "einen Film",
    moviePhrase: "einen Film",
    movieNoun: "Film",
    movieDescription: "einen Film",

    documentaryName: "Dokumentarfilm",
    documentaryTitle: "einen Dokumentarfilm",
    documentaryPhrase: "einen Dokumentarfilm",
    documentaryNoun: "Dokumentarfilm",
    documentaryDescription: "Dokumentarfilme und andere nichtfiktionale Filme",

    tvShowName: "Fernsehsendung",
    tvShowTitle: "eine Fernsehsendung",
    tvShowPhrase: "eine Fernsehsendung",
    tvShowNoun: "Fernsehsendung",
    tvShowDescription: "eine Fernsehsendung oder Serie",

    songName: "Song oder Musikstück",
    songTitle: "einen Song oder ein Musikstück",
    songPhrase: "einen Song oder ein Musikstück",
    songNoun: "Song oder Musikstück",
    songDescription: "einen Song oder eine Musikaufnahme",

    poemName: "Gedicht",
    poemTitle: "ein Gedicht",
    poemPhrase: "ein Gedicht",
    poemNoun: "Gedicht",
    poemDescription:
      "Gedichte, Verse und Lyrik aus Anthologien oder Sammlungen",

    courtCaseName: "Gerichtsentscheidung",
    courtCaseTitle: "eine Gerichtsentscheidung",
    courtCasePhrase: "eine Gerichtsentscheidung",
    courtCaseNoun: "Gerichtsentscheidung",
    courtCaseDescription: "einen Rechtsfall oder eine Gerichtsentscheidung",

    patentName: "Patent",
    patentTitle: "ein Patent",
    patentPhrase: "ein Patent",
    patentNoun: "Patent",
    patentDescription: "ein Patent oder eine Schutzrechtsanmeldung",

    encyclopediaName: "Lexikoneintrag",
    encyclopediaTitle: "einen Lexikoneintrag",
    encyclopediaPhrase: "einen Lexikoneintrag",
    encyclopediaNoun: "Lexikoneintrag",
    encyclopediaDescription: "einen Lexikon- oder Enzyklopädieartikel",

    dictionaryName: "Wörterbucheintrag",
    dictionaryTitle: "einen Wörterbucheintrag",
    dictionaryPhrase: "einen Wörterbucheintrag",
    dictionaryNoun: "Wörterbucheintrag",
    dictionaryDescription:
      "eine Wörterbuchdefinition oder einen Wörterbucheintrag",

    datasetName: "Datensatz",
    datasetTitle: "einen Datensatz",
    datasetPhrase: "einen Datensatz",
    datasetNoun: "Datensatz",
    datasetDescription: "einen Datensatz oder eine Datenbank",

    softwareName: "Software oder App",
    softwareTitle: "eine Software oder App",
    softwarePhrase: "eine Software oder App",
    softwareNoun: "Software oder App",
    softwareDescription: "Software, eine App oder ein Computerprogramm",

    videoGameName: "Videospiel",
    videoGameTitle: "ein Videospiel",
    videoGamePhrase: "ein Videospiel",
    videoGameNoun: "Videospiel",
    videoGameDescription: "Videospiele und interaktive Spielesoftware",

    pressReleaseName: "Pressemitteilung",
    pressReleaseTitle: "eine Pressemitteilung",
    pressReleasePhrase: "eine Pressemitteilung",
    pressReleaseNoun: "Pressemitteilung",
    pressReleaseDescription: "eine Pressemitteilung oder Pressemeldung",

    reportName: "Bericht",
    reportTitle: "einen Bericht",
    reportPhrase: "einen Bericht",
    reportNoun: "Bericht",
    reportDescription: "einen Bericht oder ein Whitepaper",

    preprintName: "Preprint",
    preprintTitle: "ein Preprint",
    preprintPhrase: "ein Preprint",
    preprintNoun: "Preprint",
    preprintDescription:
      "Beiträge auf arXiv, bioRxiv und anderen Preprint-Servern",

    bookChapterName: "Buchkapitel",
    bookChapterTitle: "ein Buchkapitel",
    bookChapterPhrase: "ein Buchkapitel",
    bookChapterNoun: "Buchkapitel",
    bookChapterDescription: "Kapitel und Aufsätze in Sammelbänden",

    chatgptName: "ChatGPT",
    chatgptTitle: "ChatGPT",
    chatgptPhrase: "ChatGPT",
    chatgptNoun: "ChatGPT",
    chatgptDescription: "ChatGPT und andere generative KI-Tools",

    bibleName: "Bibel",
    bibleTitle: "die Bibel",
    biblePhrase: "die Bibel",
    bibleNoun: "Bibel",
    bibleDescription: "die Bibel, heilige Schriften und andere religiöse Texte",

    tedTalkName: "TED-Talk",
    tedTalkTitle: "einen TED-Talk",
    tedTalkPhrase: "einen TED-Talk",
    tedTalkNoun: "TED-Talk",
    tedTalkDescription: "TED-Talks und aufgezeichnete Konferenzvorträge",

    emailName: "E-Mail",
    emailTitle: "eine E-Mail",
    emailPhrase: "eine E-Mail",
    emailNoun: "E-Mail",
    emailDescription: "E-Mails, Briefe und persönliche Mitteilungen",

    redditName: "Reddit- oder Forenbeitrag",
    redditTitle: "einen Reddit- oder Forenbeitrag",
    redditPhrase: "einen Reddit-Beitrag",
    redditNoun: "Reddit-Beitrag",
    redditDescription:
      "Reddit-Threads, AMAs und Beiträge in Online- und Diskussionsforen",

    tweetName: "Beitrag auf X (Twitter)",
    tweetTitle: "einen Beitrag auf X (Twitter)",
    tweetPhrase: "einen Beitrag auf X",
    tweetNoun: "X-Beitrag",
    tweetDescription: "Tweets und Beiträge auf X (ehemals Twitter)",

    mapName: "Karte",
    mapTitle: "eine Karte",
    mapPhrase: "eine Karte",
    mapNoun: "Karte",
    mapDescription:
      "gedruckte Karten, Atlanten und dynamische Karten wie Google Maps",

    claudeName: "Claude",
    claudeTitle: "Claude",
    claudePhrase: "Claude",
    claudeNoun: "Claude",
    claudeDescription: "Claude-Chats und andere KI-Ausgaben von Anthropic",

    geminiName: "Gemini",
    geminiTitle: "Gemini",
    geminiPhrase: "Gemini",
    geminiNoun: "Gemini",
    geminiDescription: "Chats und KI-Ausgaben von Google Gemini",

    copilotName: "Microsoft Copilot",
    copilotTitle: "Microsoft Copilot",
    copilotPhrase: "Microsoft Copilot",
    copilotNoun: "Microsoft Copilot",
    copilotDescription: "Chats und KI-Antworten von Microsoft Copilot",

    perplexityName: "Perplexity",
    perplexityTitle: "Perplexity",
    perplexityPhrase: "Perplexity",
    perplexityNoun: "Perplexity",
    perplexityDescription:
      "KI-Antworten und Threads von Perplexity samt den darin zitierten Quellen",

    aiImageName: "KI-generiertes Bild",
    aiImageTitle: "ein KI-generiertes Bild",
    aiImagePhrase: "ein KI-generiertes Bild",
    aiImageNoun: "KI-generiertes Bild",
    aiImageDescription:
      "mit DALL·E, Midjourney, Gemini und anderen KI-Tools erstellte Bilder",

    tiktokName: "TikTok-Video",
    tiktokTitle: "ein TikTok-Video",
    tiktokPhrase: "ein TikTok-Video",
    tiktokNoun: "TikTok-Video",
    tiktokDescription: "TikTok-Videos und Beiträge von Creatorn",

    instagramName: "Instagram-Beitrag",
    instagramTitle: "einen Instagram-Beitrag",
    instagramPhrase: "einen Instagram-Beitrag",
    instagramNoun: "Instagram-Beitrag",
    instagramDescription: "Instagram-Beiträge, Reels und Storys",

    statuteName: "Gesetz oder Rechtsvorschrift",
    statuteTitle: "ein Gesetz oder eine Rechtsvorschrift",
    statutePhrase: "ein Gesetz oder eine Rechtsvorschrift",
    statuteNoun: "Gesetz oder Rechtsvorschrift",
    statuteDescription:
      "Parlamentsgesetze, Rechtsvorschriften und kodifiziertes Recht",

    isoStandardName: "ISO-Norm",
    isoStandardTitle: "eine ISO-Norm",
    isoStandardPhrase: "eine ISO-Norm",
    isoStandardNoun: "ISO-Norm",
    isoStandardDescription:
      "ISO-, IEC- und andere veröffentlichte technische Normen",

    quranName: "Koran",
    quranTitle: "den Koran",
    quranPhrase: "den Koran",
    quranNoun: "Koran",
    quranDescription: "den Koran, im arabischen Original oder in Übersetzung",

    ebookNoPageNumbersName: "E-Book ohne Seitenzahlen",
    ebookNoPageNumbersTitle: "ein E-Book ohne Seitenzahlen",
    ebookNoPageNumbersPhrase: "ein E-Book ohne Seitenzahlen",
    ebookNoPageNumbersNoun: "E-Book ohne Seitenzahlen",
    ebookNoPageNumbersDescription:
      "Kindle-, EPUB- und Online-E-Books ohne feste Seitenzahlen",

    speechName: "Rede",
    speechTitle: "eine Rede",
    speechPhrase: "eine Rede",
    speechNoun: "Rede",
    speechDescription:
      "Reden, Ansprachen und Predigten, live oder aufgezeichnet",

    letterName: "Brief",
    letterTitle: "einen Brief",
    letterPhrase: "einen Brief",
    letterNoun: "Brief",
    letterDescription:
      "veröffentlichte Briefe, Briefe aus Archiven und offene Briefe",

    secondarySourceName: "Sekundärquelle",
    secondarySourceTitle: "eine Sekundärquelle",
    secondarySourcePhrase: "eine Sekundärquelle",
    secondarySourceNoun: "Sekundärquelle",
    secondarySourceDescription:
      "eine in einem anderen Werk zitierte oder angeführte Quelle",
  },

  // The three browse-by-facet pages under `/<locale>/citation-styles/…` — 32 academic
  // fields, 68 countries and 39 subject categories, 139 pages in one section because
  // they share a shell, a breadcrumb trail and a styles browser.
  //
  // The style NAMES, slugs and the CSL catalog itself stay in `src/data/**`: those are
  // subject matter. What the facets themselves are called is not — "Political Science",
  // "United Kingdom" and "Film & Media" are ordinary nouns that every reader expects in
  // their own language, and there are only 139 of them in total (see the `academicFields`,
  // `regions` and `styleCategories` sections below).
  taxonomyPage: {
    // ---- /citation-styles/field/<slug> ----
    fieldNotFoundTitle: "Nicht gefunden",
    fieldMetaTitle:
      "Zitierstile für {field} - Die besten Stile für Quellenangaben [2026]",
    fieldMetaDescription:
      "Entdecken Sie die am häufigsten verwendeten Zitierstile in {field}. Finden Sie das richtige Format für Ihre Arbeit, Abschlussarbeit oder Veröffentlichung in {fieldLower}.",
    fieldOgTitle: "Zitierstile für {field}",
    fieldOgSubtitle: "Häufig gefordert: {styles}.",
    fieldOgKind: "Nach Disziplin",
    fieldKeywordStyle: "{field}-Zitierstil",
    fieldKeywordReferencing: "{field}-Quellenangaben",
    fieldKeywordBest: "bester Zitierstil für {fieldLower}",
    fieldKeywordBibliography: "Literaturverzeichnis-Format für {fieldLower}",
    fieldKeywordDiscipline: "Zitierstile nach Fachrichtung",
    fieldBackLink: "← Alle Zitierstile",
    fieldHeading: "Zitierstile für {field}",
    fieldRecommendedHeading: "Empfohlene Zitierstile",
    fieldRecommendedIntro:
      "Die folgenden Zitierstile werden häufig in Forschung, Fachzeitschriften und Lehrveranstaltungen in {field} verwendet. Wählen Sie einen Stil aus, um mehr zu erfahren und mit dem Erstellen von Belegen zu beginnen.",
    fieldStyleFallback:
      "Ein Zitierstil, der in {field} und verwandten Fachrichtungen verwendet wird.",
    fieldRelatedHeading: "Verwandte Fachgebiete",
    fieldRelatedIntro:
      "Entdecken Sie Zitierstile aus verwandten akademischen Fachrichtungen:",
    fieldCtaHeading: "Alle Zitierstile entdecken",
    fieldCtaBody:
      "CitationEasy unterstützt {count} Zitierstile aus jedem akademischen Fachgebiet. Durchsuchen Sie die gesamte Sammlung, um genau das Format zu finden, das Sie brauchen.",
    fieldCtaButton: "Alle Stile durchsuchen →",
    fieldFaqWhichQ: "Welcher Zitierstil wird in {field} verwendet?",
    fieldFaqWhichA:
      "Welcher Zitierstil in {field} verwendet wird, hängt von den Vorgaben der jeweiligen Fachzeitschrift, Institution oder Lehrperson ab. Gängige Stile sind unter anderem {styles}. Prüfen Sie stets die Vorgaben Ihrer Aufgabenstellung oder der Zielzeitschrift für das erforderliche Format.",
    fieldFaqSameQ:
      "Verwenden alle Fachzeitschriften in {field} denselben Stil?",
    fieldFaqSameA:
      "Nein, nicht alle Fachzeitschriften in {field} verwenden denselben Zitierstil. Zwar sind bestimmte Stile in diesem Fachgebiet verbreiteter, doch einzelne Zeitschriften können eigene Formatierungsvorgaben haben. Prüfen Sie stets die Einreichungsrichtlinien der Zeitschrift, bevor Sie Ihr Manuskript vorbereiten.",
    fieldFaqChooseQ:
      "Wie wähle ich einen Zitierstil für meine Arbeit in {field}?",
    fieldFaqChooseA:
      "Um einen Zitierstil für Ihre Arbeit in {field} zu wählen, prüfen Sie zunächst, ob Ihre Lehrperson oder die Zielzeitschrift einen bestimmten Stil vorschreibt. Ist das nicht der Fall, verwenden Sie einen der oben aufgeführten, für {field} empfohlenen Stile. CitationEasy unterstützt über {count} Stile und kann Belege in jedem davon kostenlos erstellen.",

    // ---- /citation-styles/country/<slug> ----
    countryNotFoundTitle: "Land nicht gefunden",
    countryMetaTitle: "Zitierstile aus {country} - APA, MLA und mehr",
    countryMetaDescription:
      "Entdecken Sie Zitierstile, die in {country} verwendet werden. Kostenloser Zitiergenerator für Formate aus {country}.",
    countryOgTitle: "In {country} verwendete Zitierstile",
    countryOgKind: "Nach Land",
    countryHeading: "Zitierstile aus {country}",
    countryIntroOne:
      "{count} Zitierstil wird in {country} häufig verwendet oder mit diesem Land in Verbindung gebracht. Wählen Sie ihn aus, um einen eigenen Generator zu öffnen.",
    countryIntroMany:
      "{count} Zitierstile werden in {country} häufig verwendet oder mit diesem Land in Verbindung gebracht. Wählen Sie einen davon aus, um einen eigenen Generator zu öffnen.",
    countryBrowserTitle: "Zitierstile aus {country}",

    // ---- /citation-styles/type/<slug> ----
    typeNotFoundTitle: "Fachgebiet nicht gefunden",
    typeMetaTitle: "Zitierstile für {type} - Kostenloser Generator",
    typeMetaDescription:
      "Zitierstile für {typeLower}. APA, MLA, Vancouver und mehr. Kostenloser Zitiergenerator.",
    typeOgTitle: "Zitierstile für {type}",
    typeOgKind: "Nach Fachgebiet",
    typeHeading: "Zitierstile für {type}",
    typeIntroOne:
      "{count} Zitierstil für {typeLower}. Wählen Sie ihn aus, um einen eigenen Generator mit FAQ zu öffnen.",
    typeIntroMany:
      "{count} Zitierstile für {typeLower}. Wählen Sie einen davon aus, um einen eigenen Generator mit FAQ zu öffnen.",
    typeBrowserTitle: "Zitierstile für {type}",

    // ---- Shared chrome ----
    breadcrumbStyles: "Zitierstile",
    // Title case, because the BreadcrumbList markup says "Citation Styles" while the
    // visible trail says "Citation styles". Two keys rather than one so translating
    // either does not silently change the other.
    breadcrumbStylesSchema: "Zitierstile",
    breadcrumbByCountry: "Nach Land",
    breadcrumbByDiscipline: "Nach Fachgebiet",
    allStylesLink: "← Alle Zitierstile",
    internationalLabel: "International",
  },

  // The 32 academic fields (`ACADEMIC_FIELDS` in src/data/style-pages.ts), keyed by
  // slug. Their one-sentence `description` is next door in `academicFieldDescriptions`.
  academicFields: {
    psychology: "Psychologie",
    education: "Pädagogik",
    sociology: "Soziologie",
    anthropology: "Anthropologie",
    politicalScience: "Politikwissenschaft",
    economics: "Wirtschaftswissenschaften",
    business: "Wirtschaft & Management",
    literature: "Literatur & Sprachen",
    history: "Geschichte",
    philosophy: "Philosophie",
    law: "Recht & Rechtswissenschaften",
    medicine: "Medizin & klinische Wissenschaften",
    nursing: "Pflegewissenschaft & Hebammenwesen",
    publicHealth: "Public Health & Epidemiologie",
    pharmacy: "Pharmazie",
    biology: "Biologie & Lebenswissenschaften",
    chemistry: "Chemie",
    physics: "Physik",
    engineering: "Ingenieurwissenschaften",
    computerScience: "Informatik",
    mathematics: "Mathematik & Statistik",
    environmentalScience: "Umweltwissenschaften",
    earthScience: "Erd- & Geowissenschaften",
    artHistory: "Kunstgeschichte & bildende Kunst",
    music: "Musik & Musikwissenschaft",
    theology: "Theologie & Religionswissenschaft",
    socialWork: "Soziale Arbeit",
    linguistics: "Linguistik",
    architecture: "Architektur & Stadtplanung",
    filmMedia: "Film- & Medienwissenschaft",
    journalism: "Journalismus & Kommunikation",
    criminology: "Kriminologie & Strafjustiz",
  },

  // The same 32 fields' one-sentence ledes — the opening paragraph of every
  // `/citation-styles/field/<slug>` page.
  //
  // Unlike everything above, these are hand-written per field rather than mail-merged
  // from a template, which is why the extraction pass left them in
  // `src/data/style-pages.ts`: translating them is a content job, not a string swap.
  // But "hand-written" describes how they were produced, not where they belong. With
  // the chrome translated and the lede still English, the page is the hybrid CLAUDE.md
  // calls worse than an honest redirect — translated furniture around an English core.
  // `academicFieldDescription()` (src/lib/i18n/data-labels.ts) falls back to the data
  // file's own value, so a field added upstream is untranslated rather than blank.
  academicFieldDescriptions: {
    psychology:
      "Zitierstile, die in der Psychologie, den Verhaltenswissenschaften und der kognitiven Forschung häufig verwendet werden.",
    education:
      "Zitierstile für die Bildungsforschung, Pädagogik und Curriculumforschung.",
    sociology:
      "Zitierstile, die in der soziologischen Forschung und der Gesellschaftstheorie verwendet werden.",
    anthropology:
      "Zitierstile für die anthropologische und ethnografische Forschung.",
    politicalScience:
      "Zitierstile für die Politikwissenschaft, Regierungsforschung und Forschung zur öffentlichen Politik.",
    economics:
      "Zitierstile, die in der Wirtschaftswissenschaft und der ökonometrischen Forschung verwendet werden.",
    business:
      "Zitierstile für die Forschung in Wirtschaft, Management, Marketing und Finanzen.",
    literature:
      "Zitierstile für die Literaturwissenschaft, Linguistik und Sprachforschung.",
    history:
      "Zitierstile für die historische Forschung, Geschichtsschreibung und Archivwissenschaft.",
    philosophy: "Zitierstile für philosophische Texte und die Ethikforschung.",
    law: "Juristische Zitierstile für Gerichtsdokumente, Rechtszeitschriften und die Rechtswissenschaft.",
    medicine:
      "Zitierstile für die medizinische Forschung, klinische Studien und das Gesundheitswesen.",
    nursing:
      "Zitierstile für die Pflegeforschung und die Praxis im Gesundheitswesen.",
    publicHealth:
      "Zitierstile für die Public-Health-Forschung und epidemiologische Studien.",
    pharmacy:
      "Zitierstile für die pharmazeutische und pharmakologische Forschung.",
    biology: "Zitierstile für die biologische Forschung, Ökologie und Genetik.",
    chemistry:
      "Zitierstile für Chemie, Biochemie und die Materialwissenschaft.",
    physics:
      "Zitierstile für Physik, Astrophysik und die angewandte physikalische Forschung.",
    engineering:
      "Zitierstile für die Ingenieurwissenschaften und angewandte Technikforschung.",
    computerScience:
      "Zitierstile für die Informatik, KI-Forschung und Softwaretechnik.",
    mathematics:
      "Zitierstile für Mathematik, angewandte Mathematik und die statistische Forschung.",
    environmentalScience:
      "Zitierstile für die Umweltforschung und Nachhaltigkeitsstudien.",
    earthScience:
      "Zitierstile für Geologie, Geophysik und die geowissenschaftliche Forschung.",
    artHistory:
      "Zitierstile für Kunstgeschichte, visuelle Kultur und Museologie.",
    music:
      "Zitierstile für die Musikforschung, Musiktheorie und Aufführungspraxis.",
    theology:
      "Zitierstile für die theologische und religionswissenschaftliche Forschung.",
    socialWork: "Zitierstile für die Forschung und Praxis der Sozialen Arbeit.",
    linguistics: "Zitierstile für Linguistik, Phonetik und Sprachwissenschaft.",
    architecture:
      "Zitierstile für Architektur, Städtebau und die Planungsforschung.",
    filmMedia:
      "Zitierstile für Film, Fernsehen, Medien- und Kommunikationswissenschaft.",
    journalism:
      "Zitierstile für Journalismus, Medienwissenschaft und Kommunikationsforschung.",
    criminology:
      "Zitierstile für die Kriminologie und die Forschung zur Strafjustiz.",
  },

  // The 68 country/region facets (`REGIONS` in src/data/citation-styles.ts), keyed by
  // slug. `getRegionCountryCode` still keys off the English data value, so translating these
  // moves the label without moving the flag.
  regions: {
    unitedStates: "Vereinigte Staaten",
    unitedKingdom: "Vereinigtes Königreich",
    australia: "Australien",
    canada: "Kanada",
    newZealand: "Neuseeland",
    ireland: "Irland",
    southAfrica: "Südafrika",
    germany: "Deutschland",
    france: "Frankreich",
    spain: "Spanien",
    italy: "Italien",
    netherlands: "Niederlande",
    belgium: "Belgien",
    switzerland: "Schweiz",
    austria: "Österreich",
    portugal: "Portugal",
    luxembourg: "Luxemburg",
    sweden: "Schweden",
    norway: "Norwegen",
    denmark: "Dänemark",
    finland: "Finnland",
    iceland: "Island",
    poland: "Polen",
    czechRepublic: "Tschechien",
    hungary: "Ungarn",
    romania: "Rumänien",
    russia: "Russland",
    ukraine: "Ukraine",
    greece: "Griechenland",
    turkey: "Türkei",
    croatia: "Kroatien",
    slovenia: "Slowenien",
    slovakia: "Slowakei",
    serbia: "Serbien",
    bulgaria: "Bulgarien",
    lithuania: "Litauen",
    latvia: "Lettland",
    estonia: "Estland",
    japan: "Japan",
    china: "China",
    southKorea: "Südkorea",
    india: "Indien",
    singapore: "Singapur",
    hongKong: "Hongkong",
    taiwan: "Taiwan",
    malaysia: "Malaysia",
    indonesia: "Indonesien",
    thailand: "Thailand",
    vietnam: "Vietnam",
    philippines: "Philippinen",
    pakistan: "Pakistan",
    bangladesh: "Bangladesch",
    sriLanka: "Sri Lanka",
    israel: "Israel",
    saudiArabia: "Saudi-Arabien",
    unitedArabEmirates: "Vereinigte Arabische Emirate",
    brazil: "Brasilien",
    mexico: "Mexiko",
    argentina: "Argentinien",
    chile: "Chile",
    colombia: "Kolumbien",
    peru: "Peru",
    venezuela: "Venezuela",
    egypt: "Ägypten",
    nigeria: "Nigeria",
    kenya: "Kenia",
    morocco: "Marokko",
    international: "International",
  },

  // The 39 subject categories (`CATEGORIES` in src/data/citation-styles.ts), keyed by
  // slug. The emoji beside each one stays in the data — it is language-neutral.
  styleCategories: {
    medicine: "Medizin",
    nursing: "Pflegewissenschaft",
    publicHealth: "Public Health",
    pharmacy: "Pharmazie",
    dentistry: "Zahnmedizin",
    veterinary: "Veterinärmedizin",
    psychology: "Psychologie",
    sociology: "Soziologie",
    anthropology: "Anthropologie",
    politicalScience: "Politikwissenschaft",
    economics: "Wirtschaftswissenschaften",
    education: "Pädagogik",
    socialWork: "Soziale Arbeit",
    biology: "Biologie",
    chemistry: "Chemie",
    physics: "Physik",
    environmentalScience: "Umweltwissenschaften",
    earthScience: "Geowissenschaften & Geologie",
    agriculture: "Agrarwissenschaften",
    ecology: "Ökologie",
    engineering: "Ingenieurwissenschaften",
    computerScience: "Informatik",
    mathematics: "Mathematik",
    statistics: "Statistik",
    history: "Geschichte",
    literature: "Literaturwissenschaft",
    philosophy: "Philosophie",
    linguistics: "Linguistik",
    artHistory: "Kunstgeschichte",
    music: "Musik",
    filmMedia: "Film & Medien",
    archaeology: "Archäologie",
    law: "Recht",
    business: "Wirtschaft",
    architecture: "Architektur",
    religion: "Religion & Theologie",
    geography: "Geografie",
    general: "Allgemein / Interdisziplinär",
    other: "Sonstiges",
  },

  // The closed vocabularies `STYLE_LANDING_PAGES` (src/data/style-pages.ts) draws on for
  // the 30 major styles: what each style calls its end-of-paper list, the shape of its
  // in-text citation, its edition, and the disciplines that require it. The comparison
  // pages print all four in a table and the field pages print two of them under every
  // style card, so leaving them in the data means a translated page with an English
  // table in the middle of it.
  //
  // Style NAMES (`name`, `fullName`) are NOT here: a style's official name is what it
  // is called everywhere. The 30 hand-written taglines are, in `styleTaglines` below.
  styleFacts: {
    // `referenceListName` — 6 distinct values across the 30 styles.
    listReferenceList: "Literaturverzeichnis",
    listWorksCited: "Zitierte Werke",
    listReferences: "Quellenverzeichnis",
    listBibliography: "Bibliografie",
    listTableOfAuthorities: "Fundstellenverzeichnis",
    listReferencesCited: "Zitierte Literatur",
    listLowerReferenceList: "Literaturverzeichnis",
    listLowerWorksCited: "zitierte Werke",
    listLowerReferences: "Quellenverzeichnis",
    listLowerBibliography: "Bibliografie",
    listLowerTableOfAuthorities: "Fundstellenverzeichnis",
    listLowerReferencesCited: "zitierte Literatur",

    // `inTextFormat` — 14 distinct values. "Author", "Year" and "Page" are slot names,
    // not part of the citation, so they read as English words on a Spanish page.
    inTextAuthorCommaYear: "(Autor, Jahr)",
    inTextAuthorPage: "(Autor Seite)",
    inTextAuthorYear: "(Autor Jahr)",
    inTextSuperscriptOrBracketed:
      "Hochgestellte oder in Klammern gesetzte Zahlen (1)",
    inTextBracketedNumbers: "Zahlen in Klammern [1]",
    inTextSuperscriptNumbers: "Hochgestellte Zahlen¹",
    inTextSuperscriptOrAuthorYear: "Hochgestellte Zahlen oder (Autor Jahr)",
    inTextKeyBased: "Schlüsselbasierte Verweise",
    inTextSuperscriptOrAuthorCommaYear:
      "Hochgestellte Zahlen oder (Autor, Jahr)",
    inTextFootnotesCaseLegislation:
      "Fußnoten mit Verweisen auf Rechtsprechung und Gesetzgebung",
    inTextFootnotesSignals: "Fußnoten mit Signalwörtern",
    inTextFootnotesPinpoint: "Fußnoten mit genauer Fundstellenangabe",
    inTextParenNumbers: "Zahlen in Klammern (1)",
    inTextItalicNumbers: "Kursive Zahlen (1)",

    // `edition` — 12 distinct values. The ordinal and the word "edition" translate; the
    // handbook name inside "Cite Them Right 11th edition" does not.
    edition4th: "4. Auflage",
    edition5th: "5. Auflage",
    edition7th: "7. Auflage",
    edition8th: "8. Auflage",
    edition9th: "9. Auflage",
    edition11th: "11. Auflage",
    edition18th: "18. Auflage",
    edition22nd: "22. Auflage",
    editionCiteThemRight11th: "Cite Them Right 11. Auflage",
    editionIcmjeStandard: "ICMJE-Standard",
    editionStandard: "Standard",
    editionCurrent: "aktuell",

    // `fields` — the 67 discipline names the 30 styles are tagged with.
    fieldAcoustics: "Akustik",
    fieldAnthropology: "Anthropologie",
    fieldAppliedPhysics: "Angewandte Physik",
    fieldArchaeology: "Archäologie",
    fieldArts: "Kunst",
    fieldAstrophysics: "Astrophysik",
    fieldAustralianLaw: "Australisches Recht",
    fieldBiochemistry: "Biochemie",
    fieldBiology: "Biologie",
    fieldBiomedicalSciences: "Biomedizinische Wissenschaften",
    fieldBiotechnology: "Biotechnologie",
    fieldBusiness: "Wirtschaft",
    fieldCellBiology: "Zellbiologie",
    fieldChemicalEngineering: "Chemieingenieurwesen",
    fieldChemistry: "Chemie",
    fieldClinicalResearch: "Klinische Forschung",
    fieldComputerScience: "Informatik",
    fieldCondensedMatter: "Physik der kondensierten Materie",
    fieldCourtDocuments: "Gerichtsdokumente",
    fieldCriminology: "Kriminologie",
    fieldCulturalStudies: "Kulturwissenschaften",
    fieldDemography: "Demografie",
    fieldEcology: "Ökologie",
    fieldEducation: "Pädagogik",
    fieldElectronics: "Elektronik",
    fieldEngineering: "Ingenieurwissenschaften",
    fieldEnvironmentalScience: "Umweltwissenschaften",
    fieldEpidemiology: "Epidemiologie",
    fieldEthnography: "Ethnografie",
    fieldGenetics: "Genetik",
    fieldGeology: "Geologie",
    fieldGlobalHealth: "Globale Gesundheit",
    fieldHealthSciences: "Gesundheitswissenschaften",
    fieldHistory: "Geschichte",
    fieldHumanities: "Geisteswissenschaften",
    fieldInformationSystems: "Informationssysteme",
    fieldInformationTechnology: "Informationstechnologie",
    fieldInternationalRelations: "Internationale Beziehungen",
    fieldJurisprudence: "Rechtswissenschaft",
    fieldLanguages: "Sprachen",
    fieldLaw: "Recht",
    fieldLawReview: "Rechtszeitschrift",
    fieldLegalStudies: "Rechtswissenschaften",
    fieldLifeSciences: "Lebenswissenschaften",
    fieldLiterature: "Literaturwissenschaft",
    fieldMaterialsScience: "Materialwissenschaft",
    fieldMathematics: "Mathematik",
    fieldMedicine: "Medizin",
    fieldMolecularBiology: "Molekularbiologie",
    fieldMultidisciplinary: "Interdisziplinär",
    fieldMultidisciplinaryScience: "Interdisziplinäre Wissenschaft",
    fieldNaturalSciences: "Naturwissenschaften",
    fieldNursing: "Pflegewissenschaft",
    fieldPharmacy: "Pharmazie",
    fieldPhysics: "Physik",
    fieldPoliticalScience: "Politikwissenschaft",
    fieldPsychology: "Psychologie",
    fieldPublicHealth: "Public Health",
    fieldPublicPolicy: "Öffentliche Politik",
    fieldPublishing: "Verlagswesen",
    fieldScience: "Naturwissenschaft",
    fieldSciences: "Naturwissenschaften",
    fieldSocialSciences: "Sozialwissenschaften",
    fieldSocialWork: "Soziale Arbeit",
    fieldSociology: "Soziologie",
    fieldSoftwareEngineering: "Softwaretechnik",
    fieldTheology: "Theologie",
  },

  // The 30 major styles' hero taglines (`STYLE_LANDING_PAGES[].tagline`), keyed by
  // slug.
  //
  // Per-page hand-written copy, not a template — which is why they outlived the
  // extraction pass in `src/data/style-pages.ts`. Three page types print them: the
  // style landing page's hero, the card for each recommended style on a field
  // taxonomy page, and the lower-cased clause inside a comparison page's opening
  // sentence ("APA (…) is the most widely used citation style in the social
  // sciences"). Leaving them in the data means an English sentence in the first
  // paragraph of an otherwise translated page. `styleTagline()`
  // (src/lib/i18n/data-labels.ts) falls back to the data file's own value.
  styleTaglines: {
    apa: "Der meistgenutzte Zitierstil in den Sozialwissenschaften",
    mla: "Der Standardzitierstil für die Geisteswissenschaften",
    chicago:
      "Amerikas vertrauenswürdigster Stilratgeber für Autoren, Lektoren und Verlage",
    harvard:
      "Der beliebteste Zitierstil an britischen und australischen Universitäten",
    vancouver:
      "Der Standardzitierstil für medizinische und biomedizinische Fachzeitschriften",
    ieee: "Der bevorzugte Zitierstil für Ingenieurwissenschaften und Informatik",
    ama: "Der Zitierstandard für amerikanische medizinische Fachzeitschriften",
    turabian: "Die studierendenfreundliche Version des Chicago-Stils",
    cse: "Der bevorzugte Zitierstil für die Natur- und physikalischen Wissenschaften",
    bibtex:
      "Das Literaturverzeichnis-Format für LaTeX-Nutzer und den akademischen Satz",
    nature:
      "Der Zitierstil der weltweit führenden interdisziplinären Fachzeitschrift",
    acs: "Der Zitierstandard für Chemie und die chemischen Wissenschaften",
    oscola:
      "Der Standardzitierstil des Rechts an britischen Rechtsfakultäten und Fachzeitschriften",
    bluebook:
      "Das maßgebliche juristische Zitiersystem für US-Gerichte und Rechtszeitschriften",
    aglc: "Der Standardzitierstil des Rechts an australischen Rechtsfakultäten und Gerichten",
    asa: "Der Standardzitierstil für Soziologie und Sozialforschung",
    apsa: "Der Zitierstil für politikwissenschaftliche Forschung und Fachzeitschriften",
    aaa: "Der Standardzitierstil für anthropologische Forschung",
    nlm: "Der Zitierstandard von PubMed und der amerikanischen National Library of Medicine",
    acm: "Der Zitierstil für ACM-Konferenzen und die Informatikforschung",
    aps: "Der Zitierstil für Physical Review und APS-Fachzeitschriften",
    aip: "Der Zitierstil für AIP- und physikalische Fachzeitschriften",
    cell: "Der Zitierstil für Cell Press und führende biologische Fachzeitschriften",
    science:
      "Der Zitierstil für das Magazin Science und AAAS-Fachzeitschriften",
    plos: "Der Zitierstil für frei zugängliche PLOS-Fachzeitschriften",
    bmj: "Der Zitierstil für BMJ und führende britische medizinische Fachzeitschriften",
    lancet: "Der Zitierstil für die Fachzeitschriften-Familie von The Lancet",
    jama: "Der Zitierstil für JAMA und die Fachzeitschriften des JAMA Network",
    elsevierHarvard:
      "Die Harvard-Stilvariante in den Fachzeitschriften von Elsevier",
    springer:
      "Der Zitierstil für Fachzeitschriften und Bücher von Springer Nature",
  },

  // The 105 style-versus-style pages at `/<locale>/compare/<slug>`.
  //
  // The two style names arrive as placeholder values and are never translated. The
  // facts in the table come from `styleFacts` above and the taglines from
  // `styleTaglines`; only `fullName` stays in `src/data/style-pages.ts`, because an
  // official name is the same in every language.
  comparePage: {
    notFoundTitle: "Nicht gefunden",
    metaTitle: "{a} vs {b} - Die wichtigsten Unterschiede erklärt [2026]",
    metaDescription:
      "Vergleichen Sie die Zitierstile {a} und {b} direkt nebeneinander. Erfahren Sie die wichtigsten Unterschiede bei Formatierung, Belegen im Text und Literaturverzeichnis, um den richtigen Stil für Ihre Arbeit zu wählen.",
    ogTitle: "{a} vs {b}",
    ogSubtitle:
      "Format im Text, Einträge im Literaturverzeichnis, Groß-/Kleinschreibung und welche Fachrichtungen welchen Stil verlangen.",
    ogKind: "Stilvergleich",
    keywordVs: "{a} vs {b}",
    keywordVersus: "{a} versus {b}",
    keywordDifference: "Unterschied zwischen {a} und {b}",
    keywordOr: "{a} oder {b}",
    keywordComparison: "Vergleich von Zitierstilen",
    breadcrumbCompare: "Stile vergleichen",
    breadcrumbCurrent: "{a} vs {b}",
    backLink: "← Zurück zum Vergleichstool",
    heading: "{a} vs {b}: Die wichtigsten Unterschiede",
    intro:
      "{a} und {b} gehören zu den meistgenutzten Zitierstilen im akademischen Bereich, unterscheiden sich aber in wichtigen Punkten. {introA} {introB} Wenn Sie die Unterschiede kennen, finden Sie leichter das passende Format für Ihre Arbeit.",
    styleIntro: "{style} ({fullName}) {tagline}.",
    styleIntroFallback:
      "{style} ist ein beliebter Zitierstil, der in vielen akademischen Fachrichtungen verwendet wird.",
    tableHeading: "Direkter Vergleich",
    tableFeature: "Merkmal",
    tableCitationSystem: "Zitiersystem",
    tableInText: "Format im Text",
    tableReferenceList: "Name des Verzeichnisses",
    tableEdition: "Ausgabe",
    tableCommonFields: "Übliche Fachgebiete",
    // Title case, for the table cell. `styleLanding.system*` says "Author–date" for the
    // fact list on the style landing pages; both spellings are deliberate.
    systemAuthorDate: "Autor-Jahr",
    systemNumbered: "Numerisch",
    systemFootnotes: "Fußnoten",
    // The same three systems inside a sentence ("uses a numbered citation system").
    describeAuthorDate: "auf Autor und Jahr basierendes",
    describeNumbered: "nummeriertes",
    describeFootnotes: "fußnotenbasiertes",
    whenToUseHeading: "Wann Sie {style} verwenden",
    whenToUseBody:
      "{style} ({fullName}) verwendet ein {system} Zitiersystem mit {inText}-Belegen im Text; die Quellen erscheinen am Ende der Arbeit unter der Überschrift {list}. Er wird am häufigsten in {fields}{more} verwendet.",
    whenToUseMoreFields: " und verwandten Fachgebieten",
    whenToUseFallback:
      "{style} ist ein weithin anerkannter Zitierstil. Prüfen Sie die Vorgaben Ihrer Aufgabenstellung oder der Zielzeitschrift, um zu bestätigen, ob {style} das erforderliche Format ist.",
    ctaHeading: "Den Unterschied in der Praxis sehen",
    ctaBodyBefore: "Testen Sie unser",
    ctaBodyLink: "interaktives Vergleichstool",
    ctaBodyAfter:
      "und vergleichen Sie {a}- und {b}-Zitate direkt anhand Ihrer eigenen Quellen.",
    ctaCiteIn: "In {style} zitieren →",
    faqDifferenceQ: "Was ist der Unterschied zwischen {a} und {b}?",
    faqDifferenceA:
      "{a} und {b} unterscheiden sich im Format der Belege im Text, im Aufbau des Literaturverzeichnisses und in den akademischen Fachrichtungen, die sie verwenden. {detail}",
    faqDifferenceDetail:
      "{a} verwendet {inTextA}-Belege im Text mit der Überschrift {listA}, {b} dagegen {inTextB} mit der Überschrift {listB}.",
    faqDifferenceDetailFallback:
      "Jeder Stil hat eigene Regeln für die Formatierung von Belegen, den Aufbau des Literaturverzeichnisses und den Umgang mit unterschiedlichen Quellenarten.",
    faqBetterQ: "Was ist besser, {a} oder {b}?",
    faqBetterA:
      "Weder {a} noch {b} ist grundsätzlich besser. Die richtige Wahl hängt von Ihrer Fachrichtung, den Vorgaben Ihrer Lehrperson oder der Zeitschrift ab, bei der Sie einreichen. Prüfen Sie die Vorgaben Ihrer Aufgabenstellung oder Zielpublikation, um den passenden Stil zu bestimmen.",
    faqSwitchQ: "Kann ich zwischen {a} und {b} wechseln?",
    faqSwitchA:
      "Ja, mit dem kostenlosen Zitiergenerator von CitationEasy können Sie zwischen {a} und {b} wechseln. Wählen Sie einfach den gewünschten Stil aus, und Ihre Belege werden automatisch neu formatiert. Verwenden Sie pro Arbeit jedoch nur einen Stil, es sei denn, Ihre Lehrperson gibt etwas anderes vor.",
  },

  // The ~21 converter-pair pages at `/<locale>/convert/<slug>`.
  //
  // Only the shell is here; each pair's own four strings are in `converterCopy` below.
  // The format names (DOI, ISBN, BibTeX, RIS, CSL-JSON, PubMed ID, arXiv ID, Word) are
  // identifiers, never translated.
  converterPage: {
    notFoundTitle: "Konverter nicht gefunden",
    metaTitle: "{title} - Kostenlos & sofort",
    ogTitle: "{title}",
    ogKind: "Konverter",
    breadcrumbConverters: "Konverter",
    breadcrumbLabel: "Brotkrümelnavigation",
    whenHeading: "Wann Sie das brauchen",
    relatedHeading: "Verwandte Konverter",
    allConverters: "Alle Konverter",
    formatConverterTool: "Zitierformat-Konverter",
    faqHowQ: "Wie konvertiere ich {input} in {output}?",
    faqHowA:
      "{intro} Alles ist kostenlos, ohne Konto und ohne Konvertierungslimit.",
    faqPrivacyQ: "Werden meine Daten irgendwohin hochgeladen?",
    faqPrivacyIdentifier:
      "Jede {input}-Angabe wird beim zuständigen öffentlichen Metadatendienst nachgeschlagen (Crossref für DOIs, PubMed für PMIDs, Bibliothekskataloge für ISBNs), wobei nur die Kennung selbst übertragen wird — niemals Ihre Dokumente. Die formatierten Ergebnisse gehören Ihnen zum Kopieren oder Herunterladen; es wird nichts gespeichert.",
    faqPrivacyLocal:
      "Nein — {input}, das Sie einfügen oder hochladen, wird vollständig in Ihrem Browser verarbeitet. {detail}",
    faqPrivacyDetailStyle:
      "Nur die ausgelesenen Angaben (Autor, Titel, Jahr) werden an unseren Formatierer gesendet, um die Belege zu erzeugen; Ihre Datei selbst verlässt Ihr Gerät nie.",
    faqPrivacyDetailOffline:
      "Die Konvertierung läuft vollständig offline auf der Seite ab — es wird nichts an einen Server gesendet.",
    faqUsefulQ: "Wann ist eine Konvertierung von {input} zu {output} sinnvoll?",
    faqOtherQ: "Kann ich auch in andere Stile und Formate konvertieren?",
    faqOtherA:
      "Ja. Der Generator von CitationEasy formatiert jede Quelle in {count} Zitierstilen, das Literaturverzeichnis exportiert nach Word, RTF, BibTeX, RIS und CSL-JSON, und die Konverter-Übersicht listet jedes direkte Konvertierungspaar auf. Wenn Sie bereits formatierten Text von einem Stil in einen anderen umschreiben möchten, nutzen Sie stattdessen das Werkzeug Zitierformat-Konverter.",
  },

  // The 21 converter pairs' own copy (`CONVERTER_PAIRS` in src/data/converters.ts),
  // keyed by slug: title, meta description, the intro paragraph under the H1, and the
  // "when you need this" paragraph.
  //
  // Four hand-written strings per pair, and `src/data/converters.ts` has a quality rule
  // saying they must stay hand-written — no mail-merged sentences, because mass-templated
  // pages are what earned the site a scaled-content penalty. That rule is about how the
  // copy is authored; it says nothing about which language a reader gets it in. Left in
  // the data file they are the entire body of an otherwise translated page.
  // `converterPairCopy()` (src/lib/i18n/data-labels.ts) returns all four at once and
  // falls back to the data file per field, so a converter added upstream ships English
  // copy rather than a blank page.
  converterCopy: {
    doiToApaTitle: "DOI-zu-APA-Zitierkonverter",
    doiToApaMetaDescription:
      "Fügen Sie DOIs ein (eine pro Zeile) und erhalten Sie sofort APA-7-Referenzen, direkt aus den offiziellen Crossref-Metadaten des Verlags. Kostenlos, ohne Konto.",
    doiToApaIntro:
      "Eine DOI verweist bereits auf die maßgeblichen Metadaten des Verlags – es gibt also keinen Grund, eine APA-Referenz von Hand zu tippen. Fügen Sie eine DOI pro Zeile ein, und jede wird zu einer vollständigen APA-7-Referenz aufgelöst: invertierte Autorennamen, Titel in Satzschreibweise, kursiv gesetzte Fachzeitschrift und der von APA vorgeschriebene Link https://doi.org/.",
    doiToApaUseCase:
      "Typischer Moment: Ihr Dozent verlangt ein APA-Literaturverzeichnis, und Sie haben nur den Stapel DOIs, den Sie sich beim Lesen notiert haben. Stapel einfügen, Liste kopieren.",
    doiToMlaTitle: "DOI-zu-MLA-Zitierkonverter",
    doiToMlaMetaDescription:
      "Wandeln Sie DOIs automatisch in MLA-9-Einträge für das Verzeichnis der zitierten Werke um – mithilfe von Crossref-Metadaten. Eine DOI pro Zeile einfügen – kostenlos, nichts zu installieren.",
    doiToMlaIntro:
      "MLA 9 behandelt einen Zeitschriftenartikel als Container-Kette – Artikeltitel in Anführungszeichen, Zeitschrift kursiv, Band, Heft, Jahr, Seiten – was von Hand zusammenzustellen fummelig ist. Ein DOI-Abgleich füllt jedes Feld aus dem Verlagsdatensatz, sodass der Eintrag im Verzeichnis der zitierten Werke gleich beim ersten Mal stimmt.",
    doiToMlaUseCase:
      "Praktisch, wenn ein geisteswissenschaftlicher Kurs MLA verlangt, Ihre Quellen aber aus naturwissenschaftlich geprägten Datenbanken stammen, die nur DOIs liefern.",
    doiToBibtexTitle: "DOI-zu-BibTeX-Konverter",
    doiToBibtexMetaDescription:
      "Wandeln Sie DOIs in BibTeX-Einträge für LaTeX und Overleaf um. Eine DOI pro Zeile einfügen, fertige .bib-Datei herunterladen – kostenlos und unbegrenzt.",
    doiToBibtexIntro:
      "Jede LaTeX-Arbeit braucht irgendwann ihre .bib-Datei. Fügen Sie DOIs zeilenweise ein und erhalten Sie saubere @article-Einträge mit generierten Zitierschlüsseln, bereit für Overleaf, natbib oder biblatex – ohne Felder manuell von Verlagsseiten abzutippen.",
    doiToBibtexUseCase:
      "Der klassische Ablauf: Ein Gutachter schickt Ihnen drei zu zitierende Arbeiten, Sie haben deren DOIs, und die Abgabe ist heute Abend. Einfügen, herunterladen, \\cite verwenden.",
    doiToRisTitle: "DOI-zu-RIS-Konverter",
    doiToRisMetaDescription:
      "Wandeln Sie DOIs in eine RIS-Datei um, die Zotero, Mendeley und EndNote direkt importieren. DOIs zeilenweise einfügen – kostenlos, keine Anmeldung.",
    doiToRisIntro:
      "RIS ist das Austauschformat, das jeder Literaturverwalter importiert. Fügen Sie DOIs ein und laden Sie eine .ris-Datei herunter, deren Felder TY/AU/TI/T2 direkt von Crossref stammen – dann per Datei → Importieren in Zotero, Mendeley oder EndNote laden, und die Einträge kommen vollständig strukturiert an.",
    doiToRisUseCase:
      "Ideal, um eine handgesammelte DOI-Liste in einen richtigen Literaturverwalter zu überführen, ohne auch nur einen Autorennamen erneut zu tippen.",
    isbnToApaTitle: "ISBN-zu-APA-Zitierkonverter",
    isbnToApaMetaDescription:
      "Wandeln Sie ISBNs automatisch in APA-7-Buchreferenzen um. Eine ISBN pro Zeile einfügen und die formatierte Liste kopieren – kostenlos, ohne Konto.",
    isbnToApaIntro:
      "Die ISBN auf der Impressumsseite eines Buches genügt für seine APA-Referenz. Jede ISBN wird mit Bibliotheksmetadaten abgeglichen und im APA-7-Stil formatiert – Autor, Jahr, kursiv gesetzter Titel und Verlag – samt Auflagenangabe, sofern der Datensatz sie enthält.",
    isbnToApaUseCase:
      "Der schnellste Weg, einen Stapel physischer Bibliotheksbücher zu zitieren: bei jedem das Impressum aufschlagen, die ISBNs eintippen, die Liste hier einfügen.",
    isbnToMlaTitle: "ISBN-zu-MLA-Zitierkonverter",
    isbnToMlaMetaDescription:
      "Wandeln Sie ISBNs in einem Rutsch in MLA-9-Einträge für das Verzeichnis der zitierten Werke um. Kostenlose ISBN-Abfrage mit formatierter Ausgabe – ohne Anmeldung.",
    isbnToMlaIntro:
      "MLA-9-Bucheinträge sind kurz – Autor, kursiv gesetzter Titel, Verlag, Jahr –, doch die richtige Autoreninversion und Zeichensetzung bereiten trotzdem Schwierigkeiten. Eine ISBN-Abfrage liefert den tatsächlichen Katalogeintrag des Buches und formatiert ihn nach der Kernelemente-Vorlage des MLA-Handbuchs.",
    isbnToMlaUseCase:
      "Ideal für geisteswissenschaftliche Hausarbeiten: Zitieren Sie jeden Roman und jede kritische Monografie auf Ihrem Schreibtisch, indem Sie nur die Nummern auf der Buchrückseite eintippen.",
    isbnToBibtexTitle: "ISBN-zu-BibTeX-Konverter",
    isbnToBibtexMetaDescription:
      "Wandeln Sie ISBNs in BibTeX-@book-Einträge für LaTeX um. Eine ISBN pro Zeile einfügen, .bib-Datei herunterladen – kostenlos und sofort.",
    isbnToBibtexIntro:
      "Bücher sind die unbequeme Ecke von BibTeX – keine DOI zum Nachschlagen, und Verlagsseiten bieten selten einen .bib-Export an. Dieser Konverter gleicht jede ISBN mit Bibliotheksmetadaten ab und erzeugt @book-Einträge mit ausgefüllten Feldern für Autor, Titel, Verlag und Jahr.",
    isbnToBibtexUseCase:
      "Für Abschlussarbeiten: Die Methodenkapitel zitieren DOIs von Fachartikeln, das Theoriekapitel jedoch zwölf Bücher. Diese zwölf Bücher übernimmt dieser Konverter.",
    pmidToApaTitle: "PMID-zu-APA-Zitierkonverter",
    pmidToApaMetaDescription:
      "Wandeln Sie PubMed-IDs automatisch in APA-7-Referenzen um. PMIDs zeilenweise einfügen und eine formatierte Literaturliste erhalten – kostenlos.",
    pmidToApaIntro:
      "Jeder PubMed-Datensatz besitzt eine PMID, und Psychologie- wie Pflegestudiengänge verlangen ganz überwiegend APA. Fügen Sie PMIDs zeilenweise ein, und jede wird über die eigenen Metadaten von PubMed zu einer vollständigen APA-7-Zeitschriftenreferenz aufgelöst, samt DOI-Link, sofern der Datensatz einen enthält.",
    pmidToApaUseCase:
      "Gemacht für Aufgaben zur evidenzbasierten Praxis: Ihre Recherchestrategie endet mit einer Liste von PMIDs, und Ihre Arbeit braucht sie in APA.",
    pmidToRisTitle: "PMID-zu-RIS-Konverter",
    pmidToRisMetaDescription:
      "Wandeln Sie PubMed-IDs in eine RIS-Datei für Zotero, Mendeley oder EndNote um. PMIDs zeilenweise einfügen, .ris herunterladen – kostenlos.",
    pmidToRisIntro:
      "Systematische Reviews leben von RIS-Dateien. Fügen Sie die PMIDs aus Ihrer PubMed-Suche ein und laden Sie eine .ris-Datei herunter, bereit für Ihr Screening-Tool oder Ihren Literaturverwalter – jeder Datensatz strukturiert aus den kanonischen Metadaten von PubMed statt aus abgegriffenem Text.",
    pmidToRisUseCase:
      "Nützlich mitten im Review, wenn eine Kollegin Ihnen fünf weitere PMIDs mit der Bitte schickt, auch diese Studien noch aufzunehmen, und Ihre Bibliothek in EndNote liegt.",
    arxivToBibtexTitle: "arXiv-zu-BibTeX-Konverter",
    arxivToBibtexMetaDescription:
      "Wandeln Sie arXiv-IDs in BibTeX-Einträge für LaTeX-Arbeiten um. IDs wie 2301.12345 einfügen, eine pro Zeile, und die .bib-Datei herunterladen – kostenlos.",
    arxivToBibtexIntro:
      "In schnelllebigen Fachgebieten werden Preprints ständig zitiert, und arXiv-IDs (wie 2301.12345) sind die Art, wie sie zirkulieren. Fügen Sie die IDs ein und erhalten Sie BibTeX-Einträge mit Autoren, Titel, Jahr und arXiv-Link des Preprints – bereit für die .bib-Datei Ihrer Arbeit.",
    arxivToBibtexUseCase:
      "Für Autorinnen und Autoren aus maschinellem Lernen und Physik, deren Related-Work-Abschnitt zu 80 % aus arXiv besteht: IDs beim Lesen sammeln, beim Schreiben in einem Rutsch umwandeln.",
    bibtexToApaTitle: "BibTeX-zu-APA-Konverter",
    bibtexToApaMetaDescription:
      "Fügen Sie eine .bib-Datei ein oder laden Sie sie hoch und erhalten Sie eine APA-7-Literaturliste. Verarbeitet Dateien mit mehreren Einträgen – kostenlos, die Analyse läuft in Ihrem Browser.",
    bibtexToApaIntro:
      "Manchmal verlässt die Arbeit LaTeX: Eine Fachzeitschrift will eine Word-Einreichung, oder ein Gremium will die Literaturliste als reinen Text. Fügen Sie Ihr BibTeX ein (oder laden Sie die .bib-Datei hoch), und jeder Eintrag wird in Ihrem Browser analysiert und dann als APA-7-Literaturliste formatiert, die Sie in jedes Dokument einfügen können.",
    bibtexToApaUseCase:
      "Der Klassiker bei Förderanträgen: Ihre Publikationsliste liegt als .bib-Datei vor, und das Formular der Förderorganisation verlangt APA-formatierte Referenzen in einem Textfeld.",
    bibtexToMlaTitle: "BibTeX-zu-MLA-Konverter",
    bibtexToMlaMetaDescription:
      "Wandeln Sie BibTeX in MLA-9-Einträge für das Verzeichnis der zitierten Werke um. .bib-Datei einfügen oder hochladen und die formatierte Liste kopieren – kostenlos, ohne Konto.",
    bibtexToMlaIntro:
      "BibTeX speichert Rohfelder; MLA schreibt genau vor, wie diese anzuordnen sind. Dieser Konverter analysiert Ihre .bib-Einträge lokal und gibt jeden über die MLA-9-Vorlage aus – Autoreninversion, Artikeltitel in Anführungszeichen, kursiv gesetzte Container und korrekte Zeichensetzung zwischen den Elementen.",
    bibtexToMlaUseCase:
      "Für fachübergreifend Forschende: Ihre Referenzen liegen als BibTeX aus früherer LaTeX-Arbeit vor, doch die geisteswissenschaftliche Fachzeitschrift, bei der Sie einreichen, verlangt MLA.",
    bibtexToRisTitle: "BibTeX-zu-RIS-Konverter",
    bibtexToRisMetaDescription:
      "Wandeln Sie eine .bib-Datei in RIS für Zotero, Mendeley oder EndNote um. BibTeX einfügen oder hochladen, .ris herunterladen – kostenlos und offline.",
    bibtexToRisIntro:
      "Der Wechsel von LaTeX-Werkzeugen zu einem Literaturverwalter bedeutet, die @article-/@book-Einträge von BibTeX in die TY/AU/TI-Tags von RIS zu übersetzen. Die Umwandlung läuft vollständig in Ihrem Browser – Feldzuordnungen inklusive – und liefert Ihnen eine .ris-Datei, die jeder Literaturverwalter importiert.",
    bibtexToRisUseCase:
      "Typischer Auslöser: Ein neuer Ko-Autor arbeitet mit Zotero, Ihre gemeinsamen Referenzen liegen als .bib im Repository, und jemand muss die Lücke schließen.",
    bibtexToCslJsonTitle: "BibTeX-zu-CSL-JSON-Konverter",
    bibtexToCslJsonMetaDescription:
      "Wandeln Sie BibTeX in CSL-JSON für Pandoc und Quarto um. .bib einfügen oder hochladen, references.json herunterladen – kostenlos, direkt im Browser.",
    bibtexToCslJsonIntro:
      "Pandoc und Quarto zitieren nativ aus CSL-JSON, und die Umwandlung Ihrer bestehenden .bib-Datei entfernt eine Fehlerquelle aus dem Build-Prozess. Einträge werden in Ihrem Browser analysiert und als sauberes CSL-JSON-Array ausgegeben – genau die Form, die --bibliography=references.json erwartet.",
    bibtexToCslJsonUseCase:
      "Für Markdown-orientierte Forschende, die ein altes LaTeX-Projekt zu Quarto umstellen: Bringen Sie die Literaturliste mit, ohne JSON von Hand zu bearbeiten.",
    bibtexToWordTitle: "BibTeX-zu-Word-Konverter",
    bibtexToWordMetaDescription:
      "Wandeln Sie eine .bib-Datei in ein Word-Dokument mit APA-formatierter Literaturliste mit hängendem Einzug um. Kostenlos – die Analyse läuft in Ihrem Browser.",
    bibtexToWordIntro:
      "Wenn eine .bib-Datei zu einem Dokument werden muss – nicht zu einem weiteren Datenformat –, analysiert dieser Konverter Ihr BibTeX lokal, formatiert jeden Eintrag im APA-7-Stil und lädt eine Word-Datei herunter, deren Literaturliste bereits alphabetisch sortiert und mit hängendem Einzug versehen ist.",
    bibtexToWordUseCase:
      "Der letzte Schritt im Ablauf: Die Fachzeitschrift hat die Arbeit angenommen, verlangt die endgültige Literaturliste aber als bearbeitbare Word-Datei, und Ihre verlässliche Quelle ist BibTeX.",
    risToApaTitle: "RIS-zu-APA-Konverter",
    risToApaMetaDescription:
      "Fügen Sie einen RIS-Export ein oder laden Sie ihn hoch und erhalten Sie eine APA-7-Literaturliste. Funktioniert mit Exporten aus Zotero, Mendeley, EndNote und Datenbanken – kostenlos.",
    risToApaIntro:
      "Die Export-Schaltfläche jeder Datenbank erzeugt RIS; Aufgaben verlangen APA. Fügen Sie den Export ein (oder laden Sie die .ris-Datei hoch), und jeder TY/AU/TI-Datensatz wird in Ihrem Browser analysiert und als APA-7-Referenz ausgegeben – keine Installation eines Literaturverwalters nötig.",
    risToApaUseCase:
      "Die Bibliotheksdatenbank-Abkürzung: Exportieren Sie Ihre Suchergebnisse als RIS, konvertieren Sie sie hier, und sparen Sie sich die Einrichtung von Zotero für eine einmalige Arbeit.",
    risToMlaTitle: "RIS-zu-MLA-Konverter",
    risToMlaMetaDescription:
      "Wandeln Sie RIS-Exporte aus Datenbanken oder Literaturverwaltern in MLA-9-Einträge für das Verzeichnis der zitierten Werke um. Einfügen oder hochladen – kostenlos, keine Anmeldung.",
    risToMlaIntro:
      "JSTOR, ProQuest und EBSCO exportieren allesamt RIS, und geisteswissenschaftliche Fachbereiche verlangen allesamt MLA. Diese Seite schließt die Lücke: Datensätze werden lokal analysiert und über die MLA-9-Kernelemente-Vorlage ausgegeben, bereit zum Einfügen in Ihr Verzeichnis der zitierten Werke.",
    risToMlaUseCase:
      "Gemacht für die Nacht vor der Abgabe: zehn aus JSTOR als citations.ris exportierte Quellen, keinerlei Lust, sie von Hand zu formatieren.",
    risToBibtexTitle: "RIS-zu-BibTeX-Konverter",
    risToBibtexMetaDescription:
      "Wandeln Sie RIS in BibTeX für LaTeX und Overleaf um. .ris-Export einfügen oder hochladen, eine saubere .bib-Datei herunterladen – kostenlos, direkt im Browser.",
    risToBibtexIntro:
      "In Richtung LaTeX: Literaturverwalter und Datenbanken sprechen RIS, der Build Ihrer Arbeit braucht .bib. Datensätze werden vollständig in Ihrem Browser umgewandelt – Typen aus TY-Tags zugeordnet, Zitierschlüssel generiert – in eine Datei, die Overleaf unverändert akzeptiert.",
    risToBibtexUseCase:
      "Häufig bei Kooperationen: Die Zotero-Nutzerin exportiert RIS, der LaTeX-Nutzer lässt es über diese Seite laufen, und beide behalten ihre gewohnten Werkzeuge.",
    risToCslJsonTitle: "RIS-zu-CSL-JSON-Konverter",
    risToCslJsonMetaDescription:
      "Wandeln Sie RIS-Exporte in CSL-JSON für Pandoc- und Quarto-Workflows um. .ris einfügen oder hochladen, references.json herunterladen – kostenlos.",
    risToCslJsonIntro:
      "Für Pandoc- und Quarto-Nutzer ist CSL-JSON das native Literaturformat. Geben Sie dieser Seite das RIS, das Ihre Datenbank oder Ihr Literaturverwalter exportiert hat, und erhalten Sie ein strukturiertes JSON-Array zurück – jeder Datensatz lokal analysiert, nichts wird irgendwohin hochgeladen.",
    risToCslJsonUseCase:
      "Fügt sich in Docs-as-Code-Workflows ein: Gutachter schicken RIS-Anhänge, die Zitierdatei Ihres Repositorys ist references.json, diese Seite ist der Adapter dazwischen.",
    cslJsonToBibtexTitle: "CSL-JSON-zu-BibTeX-Konverter",
    cslJsonToBibtexMetaDescription:
      "Wandeln Sie CSL-JSON (Exporte aus Pandoc/Quarto/Zotero) in BibTeX um. .json einfügen oder hochladen, .bib herunterladen – kostenlos, direkt im Browser.",
    cslJsonToBibtexIntro:
      "Manchmal kehrt sich der Ablauf um: Eine in einer Pandoc- oder Zotero-Pipeline entstandene Literaturliste muss einen LaTeX-Build speisen. Dieser Konverter liest das CSL-JSON-Array in Ihrem Browser und erzeugt BibTeX-Einträge, deren Typen und Felder aus dem CSL-Modell zurückübersetzt werden.",
    cslJsonToBibtexUseCase:
      "Für Quarto-Autorinnen, deren Ko-Autor auf Overleaf besteht: Behalten Sie references.json als maßgebliche Quelle und erzeugen Sie die .bib-Datei bei Bedarf.",
    cslJsonToRisTitle: "CSL-JSON-zu-RIS-Konverter",
    cslJsonToRisMetaDescription:
      "Wandeln Sie CSL-JSON in RIS um, damit Zotero, Mendeley oder EndNote es importieren können. .json einfügen oder hochladen, .ris herunterladen – kostenlos.",
    cslJsonToRisIntro:
      "CSL-JSON ist innerhalb von Pandoc-Pipelines hervorragend, aber für die Importdialoge der meisten Literaturverwalter undurchsichtig. Die Umwandlung in RIS macht dieselben Datensätze universell importierbar – jeder Eintrag wird in Ihrem Browser auf TY/AU/TI-Tags abgebildet, nichts wird an einen Server gesendet.",
    cslJsonToRisUseCase:
      "Das Übergabeproblem: Ihre Literaturliste ist eine references.json in einem Git-Repository, und Ihr Betreuer will sie bis Freitag in EndNote haben.",
  },

  // The infinite-scroll style list shared by the country and subject pages and by the
  // /citation-styles hub. It is a client component, so these arrive as a prop rather
  // than through `getDictionary` — pulling all 17 dictionaries into the browser bundle
  // to label a search box is the thing `link-path.ts` exists to avoid.
  stylesBrowser: {
    defaultTitle: "Alle Zitierstile",
    styleSingular: "Stil",
    stylePlural: "Stile",
    found: "gefunden",
    filterPlaceholder: "Stile filtern …",
    noMatches: "Keine Stile entsprechen Ihrer Suche.",
    loadingMore: "Weitere Stile werden geladen …",
    loadMore: "Mehr laden ({count} verbleibend)",
    showingAll: "Alle {count} Stile werden angezeigt",
    internationalLabel: "International",
  },

  footer: {
    tagline:
      "Ein kostenloser Zitiergenerator mit {count} Stilen. Kein Konto, kein kostenpflichtiges Abonnement, keine Grenzen.",
    freeTools: "Kostenlose Werkzeuge",
    resources: "Ressourcen",
    legal: "Rechtliches",
    language: "Sprache",
    languageLabel: "Sprache auswählen",
    rights:
      "Die Quellenangaben werden mit den offiziellen Definitionen der Citation Style Language formatiert.",
    popularStyles: "Beliebte Stile",
    about: "Über uns",
    privacy: "Datenschutz",
    terms: "Nutzungsbedingungen",
    contact: "Kontakt",
    copyright: "© {year} CitationEasy. Kostenlos nutzbar.",
    poweredBy: "Ermöglicht durch",
    allTools: "Alle Schreibwerkzeuge",
    citationGuides: "Zitierratgeber",
    allStyles: "Alle Zitierstile",
    citationGlossary: "Glossar zum Zitieren",
    howToCiteAny: "Jede Quelle richtig zitieren",
  },

  faq: {
    heading: "Häufige Fragen",
    homeQ1: "Ist CitationEasy wirklich kostenlos?",
    homeA1:
      "Ja. CitationEasy ist vollständig kostenlos: keine Anmeldung, keine Begrenzung und keine kostenpflichtige Version. Alle {count} Zitierstile stehen jedem zur Verfügung.",
    homeQ2: "Welche Zitierstile werden unterstützt?",
    homeA2:
      "Alle {count} Stile aus dem Projekt Citation Style Language, darunter APA 7. Auflage, MLA 9. Auflage, Chicago, Harvard, Vancouver, IEEE, AMA und Turabian.",
    homeQ3: "Brauche ich ein Konto?",
    homeA3:
      "Nein. CitationEasy läuft vollständig in Ihrem Browser, und Ihr Literaturverzeichnis wird auf Ihrem eigenen Gerät gespeichert. Es wird nichts hochgeladen, und es gibt nichts zu registrieren.",
    homeQ4: "Kann ich meine Quellenangaben in meiner Sprache erzeugen?",
    homeA4:
      "Ja. Die Sprache der Quellenangaben ist eine eigene Einstellung, unabhängig von der Sprache der Website. Sie können also in Ihrer Sprache arbeiten und die Angaben in der Sprache Ihrer Arbeit ausgeben.",
    styleQ1: "Ist der {style}-Zitiergenerator kostenlos?",
    styleA1:
      "Ja. {style}-Quellenangaben zu erzeugen ist kostenlos und unbegrenzt, ganz ohne Konto. Fügen Sie einen DOI, eine URL, eine ISBN oder einen BibTeX-Eintrag ein und kopieren Sie die fertige Angabe.",
    styleQ2: "Wie zitiere ich eine Quelle nach {style}?",
    styleA2:
      "Fügen Sie DOI, URL, ISBN oder BibTeX-Eintrag der Quelle in den Generator auf dieser Seite ein. {style} ist bereits ausgewählt, sodass die Quellenangabe und der Kurzbeleg sofort erscheinen.",
    styleQ3: "Wie genau ist die {style}-Formatierung?",
    styleA3:
      "Jede Angabe entsteht aus der offiziellen {style}-Definition des Projekts Citation Style Language — derselben Datei, die auch Zotero und Mendeley verwenden — und nicht aus einer Annäherung an die Regeln.",
  },

  tool: {
    modeLabel: "Eingabemodus für den Beleg",
    modeAuto: "URL / DOI / ISBN",
    modeBook: "Bücher suchen",
    modeArticles: "Artikel suchen",
    modeManual: "Manuelle Eingabe",
    errorEnterManually: "Angaben manuell eingeben",
    autoLabel: "URL, DOI, PMID, arXiv-ID, ISBN oder BibTeX einfügen",
    autoHint:
      "Wir erkennen den Typ automatisch und holen die Metadaten. Für eine ganze Liste je einen Bezeichner pro Zeile einfügen.",
    generate: "Beleg erzeugen",
    generating: "Wird erzeugt…",
    bookSearchLabel: "Nach Titel, Autor oder ISBN suchen",
    bookSearchEmpty:
      "Noch keine Treffer. Auf Suchen klicken, um Bücher zu finden.",
    bookSearchSource:
      "Durchsucht den Open-Library-Katalog mit Millionen von Büchern",
    worksSearchLabel: "Nach Artikeltitel oder Autoren suchen",
    worksSearchEmpty:
      "Noch keine Treffer. Auf Suchen klicken, um Artikel zu finden.",
    worksSearchSource:
      "Durchsucht den Crossref-Katalog: Zeitschriftenartikel, Konferenzbeiträge und Buchkapitel",
    showingResults: "{shown} von {total} Treffern",
    search: "Suchen",
    searching: "Suche läuft…",
    bulkResults: "Ergebnisse der Sammelzitation",
    copy: "Kopieren",
    copied: "Kopiert!",
    copyHint: "In die eigene Arbeit kopieren und einfügen",
    resultTitle: "Formatierter Beleg",
    edit: "Bearbeiten",
    completeFields: "Vervollständigen",
    fieldsIncomplete:
      "one:{count} Feld unvollständig|other:{count} Felder unvollständig",
    outputLanguage: "Sprache",
    sourceType: "Quellentyp",
    fieldAuthors: "Autor(en)",
    fieldAuthorsHint: "Schmidt, Anna oder Anna Schmidt",
    fieldTitle: "Titel des Artikels oder Buchs",
    fieldBookTitle: "Buchtitel",
    fieldBookTitleHint: "Titel des Buchs oder Sammelbands",
    fieldEditors: "Herausgeber",
    fieldJournal: "Zeitschrift / Publikation",
    fieldJournalHint: "Name der Zeitschrift oder Publikation",
    fieldPublisher: "Verlag / Website",
    fieldPublisherHint: "Verlagsname oder Website",
    fieldDate: "Datum / Jahr",
    fieldVolume: "Band",
    fieldIssue: "Heft",
    fieldPages: "Seite(n)",
    fieldFormat: "Format / Medium",
    fieldAuthorsGroup: "Körperschaft oder Gruppe als Autor",
    fieldAuthorsGroupHint:
      "Um eine Gruppe neben Personen anzugeben, setzen Sie sie in geschweifte Klammern: {R Core Team}, Anna Schmidt",
    fieldTranslators: "Übersetzer/-in(nen)",
    fieldInterviewers: "Interviewer/-in(nen)",
    fieldVersion: "Version",
    fieldCourt: "Gericht",
    fieldIssuingAuthority: "Erteilende Behörde",
    fieldPatentNumber: "Patentnummer",
    fieldEpisodeNumber: "Episodennummer",
    fieldLawNumber: "Gesetzes- oder Entwurfsnummer",
    fieldReportNumber: "Berichtsnummer",
    fieldReviewedTitle: "Titel des besprochenen Werks",
    fieldReviewedAuthors: "Autor(en) des besprochenen Werks",
    inTextCitation: "Beleg im Text",
    quickEdit: "Schnell bearbeiten",
    quickEditHint: "Metadaten aktualisieren und den Beleg neu erzeugen.",
    update: "Aktualisieren",
    saving: "Wird gespeichert…",
    fieldAuthor: "Autor",
    fieldDateShort: "Datum",
    fieldTitleShort: "Titel",
  },

  toolCollections: {
    "citation-generators": "Zitiergeneratoren",
    "citation-tools": "Zitier- und Literaturwerkzeuge",
    "paper-formatting": "Formatierung wissenschaftlicher Arbeiten",
    "essay-writing": "Aufsätze und Hausarbeiten schreiben",
    "writing-analysis": "Textanalyse und Überarbeitung",
    "word-counters": "Zähler und Lesbarkeit",
    vocabulary: "Wortschatz und Wortsuche",
    "research-tools": "Forschung und Publikation",
    statistics: "Statistikrechner",
    math: "Mathe-Rechner",
    science: "Naturwissenschafts- und Laborrechner",
    charts: "Diagramme und Grafiken",
    economics: "Wirtschaft, Unternehmen und Finanzen",
    grades: "GPA- und Notenrechner",
    study: "Lernen und Produktivität",
    classroom: "Unterricht und Druckvorlagen",
    random: "Zufallsgeneratoren und Auslosung",
    "text-tools": "Textbereinigung und Formatierung",
    "fancy-text": "Schmucktext und Symbole",
    language: "Sprache und Transliteration",
    ciphers: "Codes und Chiffren",
    converters: "Einheiten-, Datums- und Zahlenrechner",
    developer: "Daten, Markup und Code",
  },
  bib: {
    title: "Meine Literaturverzeichnisse",
    studioTitle: "Literaturverzeichnis-Studio",
    studioProjects: "Projekte",
    studioExplore: "Entdecken",
    studioExploreIntro:
      "Leitfäden, Werkzeuge und alle Zitierstile – nur einen Klick entfernt.",
    studioStyleGuide: "Leitfaden zum Stil {style}",
    studioStyleGuideHint: "Regeln, Beispiele und das Format des Kurzbelegs",
    studioGenerator: "Zitatgenerator",
    studioNewProject: "Neues Projekt",
    studioSwitchProject: "Projekt wechseln",
    studioAddSource: "Quelle hinzufügen",
    studioAddSourceHint:
      "DOI, URL oder ISBN einfügen, Bücher und Artikel suchen oder Angaben von Hand eingeben. Alles, was du speicherst, landet in diesem Projekt.",
    studioDoneAdding: "Fertig",
    studioReferences: "Quellen",
    studioReferenceCount: "{count} Quellen",
    studioInspectorTitle: "Details zur Quelle",
    studioInspectorEmpty:
      "Wähle eine Quelle aus, um Kurzbeleg, Notizen und Quellendetails zu sehen.",
    studioSavedLocally: "In diesem Browser gespeichert",
    studioOpen: "Literaturverzeichnis-Studio öffnen",
    studioViewInStudio: "Im Studio ansehen",
    studioFooterSaved:
      "{count} in deinem Literaturverzeichnis-Studio gespeichert",
    studioFooterEmpty:
      "Gespeicherte Zitate ergeben im Studio ein vollständiges Literaturverzeichnis",
    studioClearSelection: "Auswahl aufheben",
    studioDeleteSelected: "Auswahl löschen",
    studioDeleteSelectedBody:
      "{count} Zitate aus „{name}“ löschen? Das lässt sich nicht rückgängig machen.",
    studioDeletedMany: "{count} Zitate gelöscht",
    studioMoveTo: "Verschieben nach",
    studioCopyTo: "Kopieren nach",
    studioSearchShortcut: "Drücke / zum Suchen",
    studioStyle: "Stil",
    studioCopyEntry: "Eintrag kopieren",
    studioEntryCopied: "Eintrag kopiert",
    studioSourceDetails: "Quellendetails",
    studioEmptyTitle: "Dieses Projekt ist leer",
    studioEmptyBody:
      "Füge oben deine erste Quelle hinzu oder importiere eine vorhandene BibTeX-, RIS- oder CSL-JSON-Bibliothek.",
    studioBackup: "Sicherung",
    sharedTitle: "Geteiltes Literaturverzeichnis",
    save: "Speichern",
    saving: "Wird gespeichert…",
    saved: "Gespeichert",
    searchPlaceholder: "Belege durchsuchen…",
    empty: "Noch keine Belege gespeichert",
    emptySearch: "Keine Belege passen zu Ihrer Suche.",
    countIn: "Belege in {name}: {count}",
    savedOn: "Gespeichert am {date}",
    emptyInBib:
      "In diesem Literaturverzeichnis sind noch keine Belege. Erzeugen und speichern Sie Belege, oder importieren Sie eine BibTeX-, RIS- oder CSL-JSON-Bibliothek.",
    savedToast: "Beleg im Literaturverzeichnis gespeichert",
    copyAll: "Alle kopieren",
    copyAllTitle: "Alle Belege kopieren",
    export: "Exportieren",
    exportTitle: "Literaturverzeichnis exportieren",
    import: "Importieren",
    importTitle: "Quellen importieren",
    importLabel: "Quellen importieren (BibTeX, RIS oder CSL-JSON)",
    importing: "Wird importiert…",
    importFailed: "Import fehlgeschlagen. Bitte erneut versuchen.",
    importSummary:
      "Importiert: {added} · Duplikate übersprungen: {duplicates} · Über Limit: {overLimit} · Fehler: {failed}",
    importUnreadable: "Datei konnte nicht gelesen werden.",
    importUnparseable: "Quellen konnten nicht importiert werden.",
    importReviewTitle: "Import prüfen",
    importReviewIntro:
      "Prüfen Sie {total} {format}-Quellen, bevor sie formatiert und gespeichert werden.",
    importReviewCount: "{included} von {total} einbezogen",
    importSelectAll: "Alle einbeziehen",
    importExcludeAll: "Alle ausschließen",
    importLimitNotice:
      "{count} Quellen über dem Limit von 200 Einträgen werden nicht importiert.",
    importDuplicateExisting:
      "Mögliches Duplikat der gespeicherten Quelle „{title}“",
    importDuplicateBatch: "Mögliches Duplikat von „{title}“ in diesem Import",
    importMissingField: "{field} fehlt",
    importUntitled: "Quelle ohne Titel",
    importReview: "Quellen prüfen",
    importCommit: "Formatieren und importieren",
    importBack: "Zurück",
    importOutcomeTitle: "Import abgeschlossen",
    importOutcomeSummary:
      "Hinzugefügt: {added} · Duplikate übersprungen: {duplicates} · Ausgeschlossen: {excluded} · Über Limit: {overLimit} · Fehlgeschlagen: {failed}",
    sortBy: "Sortieren nach",
    sortTitle: "Literaturverzeichnis sortieren",
    sortAuthor: "Autor (A–Z)",
    sortNewest: "Hinzugefügt am (neueste)",
    sortOldest: "Jahr (älteste zuerst)",
    sortManual: "Manuelle Reihenfolge",
    backupAll: "Vollständige Projektsicherung (.json)",
    backupDownloaded: "Vollständige Projektsicherung heruntergeladen",
    backupUnreadable: "Die Sicherungsdatei konnte nicht gelesen werden.",
    restore: "Wiederherstellen",
    restoreTitle: "Alle Bibliografieprojekte wiederherstellen",
    restoreWarning:
      "Die Sicherung wird zuerst geprüft. Danach werden alle in diesem Browser gespeicherten Bibliografien ersetzt. Dies kann nicht rückgängig gemacht werden.",
    restoreChoose: "CitationEasy-Sicherung auswählen (.json)",
    replaceAndRestore: "Ersetzen und wiederherstellen",
    restoreComplete: "Alle Bibliografieprojekte wurden wiederhergestellt",
    restoreInvalid: "Dies ist keine gültige CitationEasy-Projektsicherung.",
    selectAllVisible: "Alle angezeigten auswählen",
    selectedCount: "{count} ausgewählt",
    clusterOpen: "Gemeinsam zitieren",
    clusterTitle: "Zusammengesetzter Kurzbeleg",
    clusterDescription:
      "Erstellen Sie einen einzigen Zitationscluster aus den ausgewählten Quellen. Der gewählte Stil bestimmt Reihenfolge, Zeichensetzung und Nummerierung.",
    clusterTarget: "Ausgabe",
    clusterLocatorOptional: "Optionale Fundstelle",
    clusterBuildCopy: "Zitat erstellen und kopieren",
    clusterBuilding: "Zitat wird erstellt…",
    clusterCopied: "Zusammengesetztes Zitat kopiert",
    clusterFailed:
      "Das zusammengesetzte Zitat konnte nicht erstellt werden. Bitte versuchen Sie es erneut.",
    destination: "Zielbibliografie",
    chooseDestination: "Bibliografie auswählen…",
    copySelected: "Kopieren",
    moveSelected: "Verschieben",
    copyComplete:
      "{count} Zitate kopiert. {skipped} Duplikate wurden übersprungen.",
    moveComplete:
      "{count} Zitate verschoben. {skipped} Duplikate blieben in der Quelle.",
    selectCitation: "{title} auswählen",
    moveUp: "Nach oben",
    moveDown: "Nach unten",
    deleteTitle: "Beleg löschen",
    deleteConfirm: "Beleg löschen?",
    deleted: "Beleg gelöscht",
    editTitle: "Beleg bearbeiten",
    close: "Schließen",
    copiedAll: "Alle Belege kopiert!",
    copiedAnnotated: "Annotiertes Literaturverzeichnis kopiert!",
    copiedBibtex: "BibTeX kopiert!",
    copiedBulleted: "Aufzählung kopiert!",
    copiedNumbered: "Nummerierte Liste kopiert!",
    copiedHtml: "HTML-Ausschnitt kopiert!",
    downloadedWord: "Word-Dokument heruntergeladen",
    downloadedRtf: "RTF-Datei heruntergeladen",
    downloadedRis: "RIS-Datei heruntergeladen",
    downloadedCsl: "CSL-JSON-Datei heruntergeladen",
    downloadedAnnotated: "Annotiertes Literaturverzeichnis heruntergeladen",
    copyFailed: "Kopieren in die Zwischenablage fehlgeschlagen",
    shareCopied:
      "Link kopiert! Wer ihn hat, kann diese Liste ansehen und importieren.",
    shareFailed: "Der Freigabelink konnte nicht erstellt werden",
    regenerateFailed: "Beleg konnte nicht neu erzeugt werden",
    updateFailed: "Beleg konnte nicht aktualisiert werden",
    fieldAuthor: "Autor",
    fieldTitle: "Titel",
    fieldDate: "Datum",
    fieldPublisher: "Verlag",
    fieldVolume: "Band",
    fieldIssue: "Heft",
    fieldPages: "Seite(n)",
    fieldUrl: "URL",
    fieldAccessed: "Zugriffsdatum",
    fieldEditor: "Herausgeber",
    fieldEvent: "Veranstaltungsort",
    noteLabel: "Anmerkung zu dieser Quelle",
    noteEdit: "Diese Anmerkung bearbeiten",
    notePlaceholder:
      "Fassen Sie die Quelle zusammen, bewerten Sie sie und ordnen Sie sie in Ihre Arbeit ein…",
    noteRemoved: "Anmerkung entfernt",
    noteSave: "Anmerkung speichern",
    noteCancel: "Abbrechen",
    noteAdd: "Anmerkung hinzufügen",
    noteWords: "{count} Wörter",
    noteSaved: "Anmerkung gespeichert ({count} Wörter)",
    noteClickToEdit: "zum Bearbeiten klicken",
    inTextCopy: "Beleg im Text für diese Quelle kopieren",
    inTextSelect: "Markieren Sie den Beleg unten, um ihn zu kopieren.",
    inTextFailed:
      "Der Beleg im Text konnte nicht erzeugt werden. Bitte erneut versuchen.",
    locator: "Fundstelle",
    locatorType: "Art der Fundstelle",
    locatorPage: "Seite",
    locatorChapter: "Kapitel",
    locatorSection: "Abschnitt",
    locatorParagraph: "Absatz",
    locatorLine: "Zeile",
    locatorVerse: "Vers",
    reformat: "Neu formatieren",
    reformatTitle: "Alle Belege in einem anderen Stil neu formatieren",
    reformatHeading: "Alles in einem Stil neu formatieren",
    reformatChoose: "Stil auswählen…",
    reformatFailed:
      "Neu formatieren fehlgeschlagen. Ihr Literaturverzeichnis ist unverändert.",
    sortOrder: "Sortierkriterium",
    copyToClipboard: "In die Zwischenablage kopieren",
    numberedList: "Nummerierte Liste",
    bulletedList: "Aufzählung",
    htmlSnippet: "HTML-Ausschnitt",
    copyWithAnnotations: "Mit Anmerkungen kopieren",
    wordAnnotated: "Word-Dokument mit Anmerkungen (.doc)",
    downloadFile: "Datei herunterladen",
    wordDocument: "Word-Dokument (.doc)",
    copyShareLink: "Freigabelink kopieren",
    print: "Drucken…",
    printFailed: "Pop-up blockiert — erlauben Sie Pop-ups zum Drucken",
    font: "Schriftart",
    sitePublication: "Website / Name der Publikation",
    importIntro:
      "Fügen Sie eine BibTeX- (.bib), RIS- (.ris) oder CSL-JSON-Datei (.json) ein oder laden Sie sie hoch",
    importUpload: "Eine .bib-, .ris- oder .json-Datei hochladen",
    importPaste: "Oder BibTeX, RIS oder CSL-JSON einfügen",
    cancel: "Abbrechen",
    done: "Fertig",
    delete: "Löschen",
    deleteBody:
      "Dieser Beleg wird dauerhaft aus Ihrem Literaturverzeichnis entfernt.",
    editBody:
      "Metadaten aktualisieren und speichern, um den Beleg neu zu erzeugen.",
    reformatExplain:
      "Baut alle {count} Einträge aus den gespeicherten Quelldaten neu auf — nichts wird erneut heruntergeladen und keine Quelle geht verloren.",
    inTextHeading: "Beleg im Text",
    inTextLocatorHint:
      "Fügen Sie eine Seite oder eine andere Fundstelle hinzu, um den Beleg an eine Stelle der Quelle zu binden — bei wörtlichen Zitaten von den meisten Stilen verlangt. Für die einfache Form leer lassen.",
    inTextCopyButton: "Beleg im Text kopieren",
    mixedStyles:
      "Dieses Literaturverzeichnis mischt {count} Zitierstile ({styles}).",
    mixedStylesAction:
      "Mit {action} bringen Sie alles in einen Stil, bevor Sie es abgeben.",
    selectBibliography: "Literaturverzeichnis auswählen",
    newBibliography: "Neues Literaturverzeichnis",
    moreOptions: "Weitere Optionen",
    rename: "Umbenennen",
    removeDuplicates: "Duplikate entfernen",
    cannotDeleteOnly:
      "Das einzige Literaturverzeichnis kann nicht gelöscht werden",
    newBibliographyBody:
      "Erstellen Sie ein Literaturverzeichnis für eine andere Arbeit oder ein anderes Projekt.",
    bibliographyName: "Name des Literaturverzeichnisses",
    create: "Erstellen",
    renameBibliography: "Literaturverzeichnis umbenennen",
    renameBibliographyBody: "Geben Sie einen neuen Namen für „{name}“ ein.",
    newName: "Neuer Name",
    duplicateRemovedOne: "{count} Duplikat entfernt",
    duplicateRemovedMany: "{count} Duplikate entfernt",
    noDuplicates: "Keine doppelten Quellen gefunden",
    removeDuplicatesConfirm: "Doppelte Quellen entfernen?",
    removeDuplicatesBody:
      "„{name}“ wird nach derselben Quelle durchsucht (DOI, ISBN oder Titel, Jahr und Autor), und jeweils eine Kopie bleibt erhalten. Nicht vergleichbare Quellen bleiben unverändert.",
    deleteBibliographyConfirm: "Literaturverzeichnis löschen?",
    deleteBibliographyBody:
      "„{name}“ und alle {count} darin enthaltenen Zitate werden dauerhaft gelöscht.",
  },

  bibliographyPage: {
    metaTitle:
      "Literaturverzeichnis-Studio - Zitate ordnen, annotieren und exportieren",
    metaDescription:
      "Erstellen und verwalten Sie private Literaturprojekte im Browser. Suchen, sortieren, annotieren, bearbeiten, formatieren, importieren, exportieren und teilen Sie ohne Konto.",
    keyword1: "Literaturverzeichnis erstellen",
    keyword2: "Zitate verwalten",
    keyword3: "annotierte Bibliografie",
    ogTitle: "Literaturverzeichnis-Studio",
    ogDescription:
      "Ein privater Arbeitsbereich zum Ordnen, Formatieren, Annotieren, Importieren und Exportieren von Zitaten.",
    ogImageTitle: "Literaturverzeichnis-Studio",
    ogImageSubtitle:
      "Quellen ordnen, annotieren, neu formatieren, exportieren und teilen.",
    ogKind: "Zitierarbeitsbereich",
    breadcrumb: "Literaturverzeichnis-Studio",
    eyebrow: "Privater Arbeitsbereich im Browser",
    lead: "Verwalten Sie alle Quellen einer Arbeit an einem Ort. Ihre Projekte bleiben in diesem Browser, sofern Sie sie nicht bewusst exportieren oder teilen.",
    privacyTitle: "Standardmäßig privat",
    privacyBody:
      "Projekte und Anmerkungen werden lokal im Browser gespeichert. Freigabelinks enthalten Zitate, lassen private Notizen aber bewusst weg.",
    workflowTitle: "Von der Quellenliste zur fertigen Arbeit",
    workflowBody:
      "Wechseln Sie Projekte, suchen und sortieren Sie Quellen, ergänzen Sie Anmerkungen oder Fundstellen, korrigieren Sie Metadaten und formatieren Sie die gesamte Liste neu.",
    formatsTitle: "Quellen importieren und überallhin mitnehmen",
    formatsBody:
      "Importieren Sie BibTeX, RIS oder CSL-JSON. Kopieren Sie formatierte Listen oder exportieren Sie Word, RTF, RIS, CSL-JSON, HTML, BibTeX und annotierte Versionen.",
  },

  // The 95-term glossary: /glossary and /glossary/[slug].
  //
  // The chrome only. Each term's own prose — `short`, the `body` paragraphs, the
  // example — is ~10,600 words that cannot live in a flat string map and must not be
  // copied verbatim into sixteen files; it lives in `src/content/glossary/<locale>.json`
  // and is read through `lib/localized-content.ts`.
  glossaryPage: {
    metaTitle: "{term} - Definition und Beispiele",
    notFoundTitle: "Begriff nicht gefunden",
    keywordDefinition: "Definition von {term}",
    keywordWhatIs: "was bedeutet {term}",
    ogTitle: "{term} - CitationEasy-Glossar",
    ogKind: "Glossar zum Zitieren",
    breadcrumb: "Glossar",
    alsoCalled: "Auch genannt: {aliases}",
    doItHeading: "Direkt auf CitationEasy erledigen",
    seeAlsoHeading: "Siehe auch",
    moreInHeading: "Mehr zu {category}",
    navLabel: "Glossar-Navigation",
    allTermsLink: "Alle {count} Glossarbegriffe",
    generatorLink: "Kostenloser Zitiergenerator",
    termSetName: "CitationEasy-Glossar zum Zitieren",
    hubMetaTitle: "Glossar zum Zitieren - {count} Begriffe erklärt",
    hubMetaDescription:
      "Allgemeinverständliche Definitionen von {count} Begriffen rund um Zitate und wissenschaftliches Schreiben: et al., ibid., DOI, hängender Einzug, Patchwriting, Version of Record und mehr — jeweils mit Beispielen.",
    hubKeyword1: "Glossar zum Zitieren",
    hubKeyword2: "Zitierbegriffe",
    hubKeyword3: "Glossar wissenschaftliches Schreiben",
    hubKeyword4: "was bedeutet et al",
    hubKeyword5: "was ist ein DOI",
    hubKeyword6: "Terminologie der Quellenangabe",
    hubOgTitle: "Glossar zum Zitieren",
    hubOgDescription:
      "Definitionen von {count} Begriffen rund um Zitate und wissenschaftliches Schreiben, mit Beispielen.",
    hubOgImageTitle: "Glossar zum Zitieren",
    hubOgImageSubtitle:
      "{count} Begriffe rund um Zitate und wissenschaftliches Schreiben, allgemeinverständlich definiert mit Beispielen.",
    hubOgKind: "Nachschlagewerk",
    hubTermSetDescription:
      "Definitionen von {count} Begriffen rund um Zitate, Quellenangaben und wissenschaftliches Schreiben.",
    hubCollectionName: "Glossar zum Zitieren",
    hubCollectionDescription:
      "Allgemeinverständliche Definitionen von {count} Begriffen rund um Zitate und wissenschaftliches Schreiben.",
    hubHeading: "Glossar zum Zitieren",
    hubIntro:
      "{count} Begriffe aus dem Bereich Zitieren und wissenschaftliches Schreiben, allgemeinverständlich definiert mit Beispielen — die Abkürzungen aus den Stilrichtlinien, die Bestandteile einer Quellenangabe, die Identifikatoren, mit denen sich eine Quelle eindeutig bestimmen lässt, und das Vokabular des wissenschaftlichen Publizierens.",
    hubJumpLabel: "Zum Buchstaben springen",
    hubBrowseHeading: "Nach Thema durchsuchen",
    hubAllHeading: "Alle Begriffe A–Z",
    hubWorkHeading: "Die Begriffe anwenden",
    hubWorkGenerator: "Kostenloser Zitiergenerator",
    hubWorkStyles: "Leitfäden zu Zitierstilen",
    hubWorkHowTo: "So zitieren Sie jede Quelle",
    hubWorkCompare: "Zitierstile vergleichen",
    hubWorkTools: "Werkzeuge zum Schreiben und Zitieren",
    hubWorkFaq: "FAQ zum Zitieren",
  },

  // /faq — the 17-question hub. The questions and answers themselves are in
  // `src/data/faq.ts` (the one source the API, the MCP tool and /md also read); this is
  // the page around them.
  faqPage: {
    metaTitle: "FAQ zum Zitieren - Schnelle Antworten auf häufige Fragen",
    metaDescription:
      "Schnelle, verlässliche Antworten auf die Zitierfragen, die Studierende wirklich stellen: DOIs, Quellen ohne Autor, et al., hängende Einzüge, KI zitieren, Plagiatsregeln, APA gegen MLA und mehr — mit Links zu ausführlichen Ratgebern.",
    ogTitle: "FAQ zum Zitieren",
    ogDescription:
      "Schnelle Antworten auf die häufigsten Zitierfragen, von DOIs und et al. bis zum Zitieren von ChatGPT.",
    ogImageTitle: "Fragen und Antworten zum Zitieren",
    ogImageSubtitle:
      "Kurze, verlässliche Antworten auf die Fragen, die Studierende wirklich zum Zitieren stellen.",
    ogKind: "FAQ",
    breadcrumbLabel: "Brotkrümelnavigation",
    breadcrumbHome: "Startseite",
    breadcrumbCurrent: "FAQ zum Zitieren",
    heading: "FAQ zum Zitieren",
    intro:
      "Schnelle, verlässliche Antworten auf die Zitierfragen, die Studierende wirklich stellen — mit Links zum ausführlichen Ratgeber oder Werkzeug, wenn Sie mehr Tiefe brauchen.",
    contentsLabel: "Inhalt der FAQ",
    stillStuckHeading: "Noch nicht weitergekommen?",
    stillStuckBody:
      "Durchsuchen Sie die [vollständige Ratgeber-Bibliothek](/guides), schauen Sie in die [Stil-Spickzettel](/cheat-sheets), schlagen Sie einen Begriff im [Glossar zum Zitieren](/glossary) nach, [durchsuchen Sie die ganze Website](/search), oder [erzeugen Sie die Quellenangabe direkt](/) und lassen Sie den Generator die Regeln für Sie anwenden.",
  },

  // /cheat-sheets and its three style sheets. The citation FORMATS in them
  // ("Author, A. A. (Year)…") are the styles' own templates and stay as they are in
  // every language, exactly as the per-style examples do; the labels around them do not.
  cheatSheets: {
    hubMetaTitle: "Spickzettel zu Zitierstilen - Schnelle Referenzleitfäden",
    hubMetaDescription:
      "Schnelle Referenz-Spickzettel für die Zitierstile APA, MLA und Chicago. Druckfreundliche Leitfäden mit Beispielen für gängige Quellenarten.",
    hubBreadcrumbHome: "Startseite",
    hubBreadcrumbCurrent: "Spickzettel zu Zitierstilen",
    hubBackLink: "← Zurück zum Generator",
    hubHeading: "Spickzettel zu Zitierstilen",
    hubIntro:
      "Schnelle Referenzleitfäden für die beliebtesten Zitierstile. Druckfreundlich und immer aktuell.",
    hubApaTitle: "APA-7-Spickzettel",
    hubApaDesc:
      "Psychologie, Sozialwissenschaften, Pädagogik, Pflegewissenschaft",
    hubMlaTitle: "MLA-9-Spickzettel",
    hubMlaDesc:
      "Literaturwissenschaft, Sprachen, Geisteswissenschaften, Kulturwissenschaften",
    hubChicagoTitle: "Chicago-Spickzettel",
    hubChicagoDesc: "Geschichte, Kunstgeschichte, Philosophie, Religion",
    backToSheets: "← Zurück zu den Spickzetteln",
    generalRules: "Allgemeine Regeln",
    inTextCitations: "Belege im Text",
    colSituation: "Situation",
    colFormat: "Format",
    colExample: "Beispiel",
    book: "Buch",
    journalArticle: "Zeitschriftenartikel",
    website: "Website",
    apaMetaTitle: "APA-7-Spickzettel - Schneller Referenzleitfaden",
    apaMetaDescription:
      "APA-7-Zitier-Spickzettel mit Beispielen für Bücher, Zeitschriften, Websites und mehr. Druckfreundliche Schnellreferenz.",
    apaHeading: "APA-7-Spickzettel",
    apaLead: "Schnelle Referenz für die gängigsten APA-Zitierformate.",
    apaRule1:
      "Verwenden Sie einen **hängenden Einzug** (erste Zeile linksbündig, folgende Zeilen um 1,27 cm eingerückt)",
    apaRule2: "Alle Einträge im doppelten Zeilenabstand",
    apaRule3: "Alphabetisch nach dem Nachnamen des Autors geordnet",
    apaRule4: "Titel von Büchern, Zeitschriften und Bänden kursiv setzen",
    apaRule5: "DOI verwenden, wenn verfügbar",
    apaRow1Situation: "1 Autor",
    apaRow1Format: "(Author, Year)",
    apaRow1Example: "(Smith, 2024)",
    apaRow2Situation: "2 Autoren",
    apaRow2Format: "(Author & Author, Year)",
    apaRow2Example: "(Smith & Jones, 2024)",
    apaRow3Situation: "3 oder mehr Autoren",
    apaRow3Format: "(First Author et al., Year)",
    apaRow3Example: "(Smith et al., 2024)",
    apaRow4Situation: "Direktes Zitat",
    apaRow4Format: "(Author, Year, p. #)",
    apaRow4Example: "(Smith, 2024, p. 45)",
    apaRow5Situation: "Kein Autor",
    apaRow5Format: '("Title," Year)',
    apaRow5Example: '("Study Results," 2024)',
    apaReferenceHeading: "Beispiele für das Literaturverzeichnis",
    apaBookExample:
      "Author, A. A. (Year). *Title of work: Capital letter also for subtitle*. Publisher. https://doi.org/xxxxx",
    apaJournalExample:
      "Author, A. A., & Author, B. B. (Year). Title of article. *Title of Periodical, Volume*(Issue), Page–Page. https://doi.org/xxxxx",
    apaWebsiteExample:
      "Author, A. A. (Year, Month Day). *Title of page*. Site Name. https://www.example.com/page",
    apaChapterLabel: "Kapitel in einem Sammelband",
    apaChapterExample:
      "Author, A. A. (Year). Title of chapter. In E. E. Editor (Ed.), *Title of book* (pp. #–#). Publisher.",
    apaConferenceLabel: "Konferenzbeitrag",
    apaConferenceExample:
      "Author, A. A. (Year, Month Days). *Title of paper* [Paper presentation]. Conference Name, Location.",
    apaDifferencesHeading: "Wichtigste Unterschiede zur APA 6",
    apaDifference1: 'Kein „Retrieved from" mehr vor URLs',
    apaDifference2: "DOI wird als Link im Format https://doi.org/ angegeben",
    apaDifference3:
      "Bis zu 20 Autoren werden aufgeführt, bevor Auslassungspunkte verwendet werden",
    apaDifference4: "Verlagsort ist nicht mehr erforderlich",
    apaDifference5:
      'Singularisches „they" für geschlechtsneutrale Sprache im Englischen',
    apaCtaText: "Erzeugen Sie automatisch perfekte APA-Quellenangaben",
    apaCtaButton: "APA-Zitiergenerator →",
    mlaMetaTitle: "MLA-9-Spickzettel - Schneller Referenzleitfaden",
    mlaMetaDescription:
      "MLA-9-Zitier-Spickzettel mit Beispielen für Bücher, Artikel, Websites und mehr. Druckfreundliche Schnellreferenz.",
    mlaHeading: "MLA-9-Spickzettel",
    mlaLead: "Schnelle Referenz für die gängigsten MLA-Zitierformate.",
    mlaRule1: "Verwenden Sie einen **hängenden Einzug**",
    mlaRule2: "Alle Einträge im doppelten Zeilenabstand",
    mlaRule3: "Alphabetisch nach dem Nachnamen des Autors geordnet",
    mlaRule4: 'Betiteln Sie die Seite mit „Zitierte Werke"',
    mlaRule5:
      "Titel von Containern (Bücher, Zeitschriften, Websites) kursiv setzen",
    mlaRow1Situation: "1 Autor",
    mlaRow1Format: "(Author Page)",
    mlaRow1Example: "(Smith 45)",
    mlaRow2Situation: "2 Autoren",
    mlaRow2Format: "(Author and Author Page)",
    mlaRow2Example: "(Smith and Jones 45)",
    mlaRow3Situation: "3 oder mehr Autoren",
    mlaRow3Format: "(First Author et al. Page)",
    mlaRow3Example: "(Smith et al. 45)",
    mlaRow4Situation: "Kein Autor",
    mlaRow4Format: '("Title" Page)',
    mlaRow4Example: '("Study Results" 45)',
    mlaRow5Situation: "Ohne Seitenzahl",
    mlaRow5Format: "(Author)",
    mlaRow5Example: "(Smith)",
    mlaWorksCitedHeading: "Beispiele für Zitierte Werke",
    mlaBookExample: "Last, First. *Title of Book*. Publisher, Year.",
    mlaJournalExample:
      'Last, First. "Title of Article." *Journal Title*, vol. #, no. #, Year, pp. #–#.',
    mlaWebsiteExample:
      'Last, First. "Title of Page." *Website Name*, Day Month Year, URL.',
    mlaAnthologyLabel: "Anthologie/Sammelband",
    mlaAnthologyExample:
      'Last, First. "Title of Essay." *Title of Collection*, edited by First Last, Publisher, Year, pp. #–#.',
    mlaCoreHeading: "MLA-Kernelemente (in dieser Reihenfolge)",
    mlaCore1: "Author.",
    mlaCore2: '"Title of Source."',
    mlaCore3: "*Title of Container*,",
    mlaCore4: "Contributors,",
    mlaCore5: "Version,",
    mlaCore6: "Number,",
    mlaCore7: "Publisher,",
    mlaCore8: "Publication date,",
    mlaCore9: "Location (pages, URL, DOI).",
    mlaCtaText: "Erzeugen Sie automatisch perfekte MLA-Quellenangaben",
    mlaCtaButton: "MLA-Zitiergenerator →",
    chicagoMetaTitle: "Chicago-Spickzettel - Schneller Referenzleitfaden",
    chicagoMetaDescription:
      "Chicago-Manual-of-Style-Zitier-Spickzettel mit Beispielen für Notes-Bibliography und Author-Date. Druckfreundliche Schnellreferenz.",
    chicagoHeading: "Chicago-Spickzettel",
    chicagoLead:
      "Schnelle Referenz für Zitierformate nach dem Chicago Manual of Style (18. Auflage).",
    chicagoSystemsHeading: "Die zwei Chicago-Systeme",
    chicagoColNb: "Notes-Bibliography (NB)",
    chicagoColAd: "Author-Date (AD)",
    chicagoRow1Nb: "Fuß-/Endnoten + Bibliografie",
    chicagoRow1Ad: "Quellenangaben in Klammern + Literaturverzeichnis",
    chicagoRow2Nb: "Geschichte, Kunst, Geisteswissenschaften",
    chicagoRow2Ad: "Naturwissenschaften, Sozialwissenschaften",
    chicagoRow3Nb: "Ausführlichere erste Anmerkungen",
    chicagoRow3Ad: "Ähnlich dem APA-Format",
    chicagoExamplesHeading: "Beispiele für Notes-Bibliography",
    chicagoNoteLabel: "Anmerkung:",
    chicagoBibliographyLabel: "Bibliografie:",
    chicagoBookLabel: "Buch (Fußnote)",
    chicagoBookNote: "1. First Last, *Title of Book* (Publisher, Year), page.",
    chicagoBookBibliography: "Last, First. *Title of Book*. Publisher, Year.",
    chicagoJournalLabel: "Zeitschriftenartikel (Fußnote)",
    chicagoJournalNote:
      '1. First Last, "Title of Article," *Journal Title* Vol, no. Issue (Year): pages.',
    chicagoJournalBibliography:
      'Last, First. "Title of Article." *Journal Title* Vol, no. Issue (Year): pages.',
    chicagoWebsiteLabel: "Website (Fußnote)",
    chicagoWebsiteNote:
      '1. First Last, "Title of Page," Site Name, Month Day, Year, URL.',
    chicagoWebsiteBibliography:
      'Last, First. "Title of Page." Site Name. Month Day, Year. URL.',
    chicagoShortHeading: "Verkürzte Anmerkungen",
    chicagoShortIntro:
      "Nach dem ersten vollständigen Beleg verkürzte Fußnoten verwenden:",
    chicagoShortExample: "1. Last, *Short Title*, page.",
    chicagoRulesHeading: "Wichtigste Formatierungsregeln",
    chicagoRule1:
      "Hochgestellte Ziffern für Fußnotenverweise im Text verwenden",
    chicagoRule2: "Bibliografie-Einträge verwenden einen hängenden Einzug",
    chicagoRule3:
      "Für aufeinanderfolgende Zitate derselben Quelle eine verkürzte Fußnote statt „Ibid.“ verwenden",
    chicagoRule4:
      "Titel: Bücher und Zeitschriften kursiv, Artikel und Kapitel in Anführungszeichen",
    chicagoRule5:
      "Zugriffsdaten für Online-Quellen verwenden, die sich ändern können",
    chicagoCtaText: "Erzeugen Sie automatisch perfekte Chicago-Quellenangaben",
    chicagoCtaButton: "Chicago-Zitiergenerator →",
  },

  // The catalog's page-type labels (`CATALOG_TYPE_LABELS`), which the search page shows
  // as facet chips and beside every result. Layer 2: a fact from `src/lib/**` rather
  // than page chrome, and English until it is mapped through here.
  catalogTypes: {
    core: "Kernseiten",
    styleLanding: "Hubs der wichtigsten Zitierstile",
    style: "Referenzseiten zu Zitierstilen",
    styleExamples: "Zitierbeispielseiten",
    styleFormat: "Leitfäden zur Formatierung von Arbeiten",
    styleInText: "Leitfäden für Belege im Text",
    styleAnnotatedBibliography: "Leitfäden zur kommentierten Bibliografie",
    styleReferencePage:
      "Leitfäden zur Literaturverzeichnis-/Zitierte-Werke-Seite",
    tool: "Kostenlose Werkzeuge zum Schreiben und Zitieren",
    guide: "Lehrreiche Ratgeber",
    guideCategory: "Ratgeber-Kategorien",
    toolCollection: "Werkzeug-Sammlungen",
    howToCite: "So zitieren Sie <Quelle> in <Stil>",
    comparison: "Vergleiche von Zitierstilen",
    converter: "Identifikator- und Formatkonverter",
    field: "Zitierstile nach Fachgebiet",
    region: "Zitierstile nach Land/Region",
    category: "Zitierstile nach Themenkategorie",
    cheatSheet: "Einseitige Spickzettel",
    glossary: "Glossar zum Zitieren und wissenschaftlichen Schreiben",
  },

  // /about
  aboutPage: {
    metaTitle: "Über CitationEasy - Kostenloser Zitiergenerator",
    metaDescription:
      "CitationEasy ist ein kostenloser Zitiergenerator, der {count} Stile unterstützt. Entwickelt von Samuel Sergi mit Open-Source-Werkzeugen wie Citation Style Language, Citation.js und Next.js.",
    backLink: "← Zurück zum Generator",
    heading: "Über CitationEasy",
    intro:
      "CitationEasy ist ein kostenloser, frei zugänglicher Zitiergenerator für Studierende, Forschende und wissenschaftliche Autorinnen und Autoren. Er unterstützt **{count} Zitierstile** und bietet eine wachsende Sammlung von Schreibwerkzeugen, Lehrratgebern und Nachschlageressourcen — alles ohne Registrierung oder Abonnement.",
    offerHeading: "Unser Angebot",
    offer1:
      "[Zitiergenerator](/) mit {count} Stilen, darunter APA 7, MLA 9, Chicago 18, Harvard, Vancouver, IEEE und Tausende weitere",
    offer2:
      "[40 kostenlose Schreib- und Zitierwerkzeuge](/tools) — Wortzähler, Lesbarkeitsprüfung, DOI-Validierung, Bibliografie-Alphabetisierer und mehr",
    offer3:
      "[750 Anleitungen zum Zitieren](/how-to-cite), die jede wichtige Quellenart in 6 Zitierstilen abdecken",
    offer4:
      "[Über 100 Lehrratgeber](/guides) zu bewährten Zitierpraktiken, wissenschaftlichem Schreiben und Recherchekompetenzen",
    offer5:
      "[Stilvergleich](/compare), [Zitierstil-Quiz](/quiz) und [Spickzettel](/cheat-sheets) für die schnelle Referenz",
    offer6:
      "7 Exportformate: Klartext, BibTeX, Word, nummerierte Liste, Liste mit Aufzählungszeichen, RIS und CSV",
    openSourceHeading: "Mit Open Source gebaut",
    openSourceIntro:
      "CitationEasy wird durch eine Reihe hervorragender Open-Source-Projekte und APIs ermöglicht. Wir glauben daran, die Anerkennung dorthin zu geben, wo sie hingehört:",
    openSource1:
      "[Citation Style Language (CSL)](https://citationstyles.org/) — der offene Standard, auf dem unsere {count} Zitierstile beruhen. CSL wird von einer Gemeinschaft Freiwilliger gepflegt und von Literaturverwaltungsprogrammen wie Zotero, Mendeley und Papers verwendet. Die Stildefinitionen stammen aus dem [CSL-Styles-Repository](https://github.com/citation-style-language/styles) auf GitHub.",
    openSource2:
      "[Citation.js](https://citation.js.org/) — die JavaScript-Bibliothek, die Zitate verarbeitet und nach CSL-Stilen formatiert. Erstellt von Lars Willighagen.",
    openSource3:
      "[Open Library API](https://openlibrary.org/developers/api) — die kostenlose Buchsuch-API des Internet Archive, verwendet zum Nachschlagen von Buchmetadaten nach Titel oder ISBN.",
    openSource4:
      "[DOI Foundation](https://www.doi.org/) — das System des Digital Object Identifier, das zur Auflösung und Validierung von DOIs verwendet wird.",
    createdHeading: "Erstellt von",
    createdBody:
      "CitationEasy wurde von **Samuel Sergi** erstellt und wird von ihm gepflegt. Das Ziel ist einfach: ein wirklich kostenloses, hochwertiges Zitierwerkzeug bieten, das Funktionen nicht hinter einer Bezahlschranke versteckt oder ein Konto voraussetzt.",
    contactPrompt: "Fragen, Feedback oder Vorschläge? Nehmen Sie Kontakt auf:",
    contactEmail: "E-Mail: [sam@sammysergi.com](mailto:sam@sammysergi.com)",
    missionHeading: "Unsere Mission",
    mission1:
      "Zuverlässige Quellenangaben sollten nichts kosten. Viele Zitierwerkzeuge werben als kostenlos, schränken aber Exportformate ein, begrenzen die Anzahl der Zitate oder drängen Nutzer zu kostenpflichtigen Plänen. CitationEasy verpflichtet sich, vollständig kostenlos zu bleiben — keine Registrierung, keine Grenzen, keine Premium-Stufe.",
    mission2:
      "Jede Funktion dieser Website, vom Zitiergenerator über die Schreibwerkzeuge bis zu den Lehrratgebern, steht allen kostenlos zur Verfügung. Wir glauben, dass Werkzeuge für wissenschaftliche Integrität allen Studierenden und Forschenden zugänglich sein sollten, unabhängig von ihrem Budget.",
  },

  // /contact
  contactPage: {
    metaTitle: "Kontakt",
    metaDescription:
      "Kontaktieren Sie CitationEasy. Fragen, Feedback oder Vorschläge zu unserem kostenlosen Zitiergenerator und unseren Schreibwerkzeugen? Wir freuen uns, von Ihnen zu hören.",
    backLink: "← Zurück zum Generator",
    heading: "Kontakt",
    intro:
      "Fragen, Feedback oder Vorschläge zu CitationEasy? Wir freuen uns, von Ihnen zu hören. Ob Sie einen Fehler gefunden haben, eine Funktion wünschen oder Hilfe bei einer Quellenangabe brauchen — zögern Sie nicht, uns zu schreiben.",
    touchHeading: "Kontakt aufnehmen",
    touchBody:
      "Der beste Weg, uns zu erreichen, ist per E-Mail. Wir bemühen uns, innerhalb von 48 Stunden zu antworten.",
    emailLine: "**E-Mail:** [sam@sammysergi.com](mailto:sam@sammysergi.com)",
    commonHeading: "Häufige Fragen",
    commonIntro:
      "Bevor Sie uns schreiben, finden Sie die Antwort auf Ihre Frage vielleicht bereits in einer dieser Ressourcen:",
    resource1:
      "[Zitier-Ratgeber](/guides) — Schritt-für-Schritt-Anleitungen zum Zitieren von Quellen in jedem wichtigen Stil",
    resource2:
      "[Anleitungen zum Zitieren](/how-to-cite) — 750 Kombinationen aus Quellenart und Stil mit Beispielen",
    resource3:
      "[Spickzettel](/cheat-sheets) — schnelle Referenz für die Formatierung nach APA, MLA und Chicago",
    resource4:
      "[Schreibwerkzeuge](/tools) — Wortzähler, Lesbarkeitsprüfung, DOI-Validierung und mehr",
    issueHeading: "Problem melden",
    issueIntro:
      "Wenn Sie einen Formatierungsfehler, eine defekte Funktion oder einen falschen Stil gefunden haben, geben Sie beim Kontakt bitte so viele Details wie möglich an:",
    issue1: "Den verwendeten Zitierstil",
    issue2: "Die Quellenart (Buch, Website, Zeitschriftenartikel usw.)",
    issue3: "Was Sie erwartet haben im Vergleich zu dem, was erzeugt wurde",
    issue4: "Ihren Browser und Ihr Gerät (falls relevant)",
    issueOutro:
      "Das hilft uns, das Problem schnell zu erkennen und zu beheben.",
    aboutHeading: "Über CitationEasy",
    aboutBody:
      "CitationEasy ist ein kostenloser Zitiergenerator, der über 10.000 Zitierstile unterstützt. Er wurde von Samuel Sergi mit dem Ziel geschaffen, verlässliche Quellenangaben für alle zugänglich zu machen. Mehr dazu auf unserer [Über-uns-Seite](/about).",
  },

  // /ai — the agent and developer docs.
  //
  // The code samples, endpoint paths and MCP tool NAMES are deliberately absent: they are
  // the API's identifiers, served in English by the API itself, and a translated one
  // would document an endpoint that does not exist.
  aiPage: {
    metaTitle:
      "CitationEasy für KI-Assistenten und Agenten - API, MCP-Server, llms.txt",
    metaDescription:
      "Nutzen Sie CitationEasy programmatisch: eine kostenlose, authentifizierungsfreie JSON-API für Quellenangaben in {count} Stilen, ein MCP-Server für Agenten, llms.txt, eine OpenAPI-Spezifikation und Markdown-Ansichten jeder Seite.",
    keyword1: "Zitier-API",
    keyword2: "MCP-Server",
    keyword3: "API für Zitiergenerator",
    keyword4: "llms.txt",
    keyword5: "OpenAPI",
    keyword6: "Quellenangaben für KI-Agenten",
    keyword7: "kostenlose Zitier-API",
    webApiDescription:
      "Kostenlose, authentifizierungsfreie JSON-API, die Quellenangaben in {count} Citation-Style-Language-Stilen aus einer URL, einem DOI, einer ISBN, einer PubMed-ID, einer arXiv-ID oder CSL-JSON-Metadaten formatiert.",
    breadcrumb: "Für KI und Agenten",
    heading: "CitationEasy für KI-Assistenten und Agenten",
    intro:
      "Alles auf dieser Website steht ebenso Software wie Menschen zur Verfügung: eine kostenlose JSON-API, ein MCP-Server, eine OpenAPI-Spezifikation, ein llms.txt-Index und eine Markdown-Ansicht aller {count} Seiten. Kein Schlüssel, kein Konto, kein eigenes Ratenlimit.",
    whyHeading:
      "Warum die API aufrufen, statt die Quellenangabe selbst zu schreiben",
    whyBody:
      "Das Formatieren von Quellenangaben sieht wie ein Textgenerierungsproblem aus, ist aber keines. Jeder Stil kodiert Hunderte Regeln zur Umkehrung von Autorennamen, zu Initialen, zur Groß-/Kleinschreibung von Titeln, zur Container-Zeichensetzung, zu Auflagenangaben und zur Platzierung von Daten, und diese Regeln unterscheiden sich auf eine Weise, die man aus dem Gedächtnis leicht subtil falsch macht. CitationEasy führt **citeproc-js** gegen die offiziellen Citation-Style-Language-Definitionen aus — dieselbe Engine, die auch Zotero und Mendeley verwenden —, sodass die Ausgabe deterministisch ist und dem entspricht, was das Einreichungssystem einer Fachzeitschrift erwartet. Metadaten stammen von Crossref, Open Library und PubMed; nichts wird erfunden, und ein vorgelagert fehlendes Feld bleibt fehlend, statt mit einer plausiblen Vermutung aufgefüllt zu werden.",
    generateHeading: "Eine Quellenangabe erzeugen",
    generateBody:
      "Senden Sie `inputs` statt `input`, um bis zu 10 Quellen in einem Aufruf zu verarbeiten, oder `entry` mit bereits vorliegenden CSL-JSON-Metadaten, um die Suche ganz zu überspringen. Fügen Sie `note: true` für die Fußnotenform hinzu. Fehler einzelner Quellen werden bei dieser Quelle zurückgemeldet, sodass ein Stapel nie als Ganzes fehlschlägt.",
    mcpHeading: "Über MCP verbinden",
    mcpIntro:
      "Der Model-Context-Protocol-Endpunkt unter `/api/mcp` spricht JSON-RPC 2.0 über Streamable HTTP und benötigt keine Zugangsdaten.",
    mcpToolsHeading: "Bereitgestellte Tools",
    endpointsHeading: "Endpunkte",
    colEndpoint: "Endpunkt",
    colWhat: "Was er tut",
    endpointCite:
      "Formatiert bis zu 10 Quellen auf einmal. Nimmt eine URL, einen DOI, eine ISBN, eine PubMed-ID, eine arXiv-ID, BibTeX-/RIS-/CSL-JSON-Text oder ein CSL-JSON-Eintragsobjekt entgegen. Gibt den Literatureintrag zurück und optional den Beleg im Text sowie die Fußnote.",
    endpointStyles:
      "Durchsucht alle {count} Stile nach Name, Zitierformat-Klasse, Fachgebiet oder Region.",
    endpointStyleSlug:
      "Ein Stil im Detail, mit echten formatierten Beispielen aus seiner eigenen CSL-Definition.",
    endpointSearch:
      "Nach Relevanz sortierte Suche über alle Seiten der Website.",
    endpointPages:
      "Der vollständige Website-Katalog als strukturierte Einträge, filterbar nach Seitentyp.",
    endpointHowToCite:
      "Jede Seite mit Anleitungen für Quellenart × Stil, filterbar nach beidem.",
    endpointTools: "Jedes kostenlose Tool, mit Kategorie und Schlagwörtern.",
    endpointGuides: "Jeder Lehrratgeber, mit seiner Kategorie.",
    endpointConverters:
      "Jede vom Angebot der Website unterstützte Identifikator- und Formatumwandlung.",
    endpointComparisons:
      "Direkte Stilvergleiche, verknüpft mit den Datensätzen beider Stile.",
    endpointFields:
      'Fachgebiete, Themenkategorien und Regionen — zur Beantwortung von „Welchen Stil sollte ich verwenden?".',
    endpointFaq:
      "Geprüfte Antworten auf häufige Zitierfragen, filterbar mit q.",
    endpointGlossary:
      "Begriffe rund um Zitieren und wissenschaftliches Schreiben mit ihren vollständigen Definitionen — die Antwort enthält den Text, sodass danach keine weitere Seite abgerufen werden muss.",
    endpointIndex:
      "Dokument zur Endpunkt-Erkennung. Beginnen Sie hier, wenn Sie sich einen Überblick verschaffen.",
    discoveryHeading: "Discovery-Dateien",
    discoveryLlms:
      "Kuratierter Index der Website, geschrieben für Sprachmodelle.",
    discoveryLlmsFull:
      "Jede von der Website veröffentlichte Seite, gruppiert nach Typ, jeweils eine Zeile.",
    discoveryOpenapi: "OpenAPI-3.1-Beschreibung der gesamten öffentlichen API.",
    discoveryMcp:
      "MCP-Endpunkt (Streamable HTTP). Rufen Sie ihn mit GET im Browser auf, um eine Beschreibung zu erhalten.",
    discoveryWellKnownMcp:
      "MCP-Discovery-Dokument für Agenten, die eine Domain abfragen.",
    discoveryAiPlugin:
      "Legacy-Plugin-Manifest, für Frameworks, die noch danach suchen.",
    discoveryMd: "Markdown-Ansicht jeder beliebigen Seite der Website.",
    discoverySitemap: "Jede indexierbare URL.",
    catalogHeading: "Was der Katalog enthält",
    catalogIntro:
      "Jede Seite unten ist als HTML, als Markdown unter `/md/` und als strukturierter Eintrag über `/api/v1/pages` erreichbar.",
    catalogStyles:
      "**{count}** Zitierstile, jeweils mit echten Beispielen, die aus der eigenen CSL-Definition erzeugt wurden",
    catalogTools: "**{count}** kostenlose Browser-Tools",
    catalogGuides: "**{count}** Lehrratgeber",
    catalogHowToCite: "**{count}** Seiten zum Zitieren",
    catalogComparisons: "**{count}** Stilvergleiche",
    catalogConverters: "**{count}** Konverter",
    catalogFields: "**{count}** Fachgebiete",
    catalogStyleLandings:
      "**{count}** Hubs der wichtigsten Stile mit vollständigen Formatierungsregeln",
    faqHeading: "Häufig gestellte Fragen",
    faqFreeQ:
      "Ist die CitationEasy-API kostenlos, und brauche ich einen API-Schlüssel?",
    faqFreeA:
      "Sie ist kostenlos, und es gibt keinen API-Schlüssel. Jeder Endpunkt unter /api/v1 ist authentifizierungsfrei und CORS-offen, sodass Sie ihn ohne Registrierung aus einem Browser, einem Notebook oder einem Agenten aufrufen können. Es gibt kein eigenes Ratenlimit, aber Identifikator-Abfragen werden an Crossref, Open Library und PubMed weitergeleitet, die ihre eigenen Fair-Use-Grenzen anwenden — bündeln Sie Anfragen mit `inputs`, statt viele parallele Anfragen abzusetzen.",
    faqLlmQ: "Werden die Quellenangaben von einem Sprachmodell erzeugt?",
    faqLlmA:
      "Nein. Die Formatierung führt citeproc-js gegen die offiziellen Citation-Style-Language-Definitionen aus, dieselbe Engine, die auch Literaturverwaltungsprogramme verwenden. Dieselbe Eingabe und derselbe Stil erzeugen immer dieselbe Zeichenkette. Bibliografische Metadaten stammen von Crossref (DOIs), Open Library (ISBNs) und PubMed (PMIDs) — CitationEasy erfindet keine Felder, sodass alles, was vorgelagert fehlt, auch in der Ausgabe fehlt, statt geraten zu werden.",
    faqMcpQ: "Wie verbinde ich einen Agenten über MCP mit CitationEasy?",
    faqMcpA:
      "Richten Sie einen beliebigen Model-Context-Protocol-Client mit dem Streamable-HTTP-Transport auf https://www.citationeasy.com/api/mcp. Es werden keine Zugangsdaten benötigt. In Claude Code: `claude mcp add --transport http citationeasy https://www.citationeasy.com/api/mcp`. Der Server stellt Tools bereit, um Quellenangaben zu erzeugen, die {count} Stile zu durchsuchen, Anleitungen nach Quellenart zu erhalten, die Website zu durchsuchen und jede Seite als Markdown zu lesen.",
    faqStylesQ: "Welche Zitierstile kann die API erzeugen?",
    faqStylesA:
      'Alle {count} Stile im Citation-Style-Language-Repository, darunter APA 7, MLA 9, beide Chicago-Systeme, Harvard, Vancouver, IEEE, AMA und Tausende einzelne Zeitschriftenstile. Übergeben Sie den Stil-Slug im Feld `style`. Einfache Slugs wie "chicago" und "harvard" lösen sich zur richtigen CSL-Variante auf, und /api/v1/styles?q= findet den Rest.',
    faqLlmsTxtQ: "Was ist llms.txt, und wo finde ich das von CitationEasy?",
    faqLlmsTxtA:
      "llms.txt ist ein Klartext-Index einer Website, geschrieben für Sprachmodelle statt für Browser. Der von CitationEasy liegt unter /llms.txt; die vollständige Version mit jeder einzelnen Seite der Website liegt unter /llms-full.txt.",
    faqMarkdownQ:
      "Kann ich CitationEasy-Seiten als Markdown statt als HTML lesen?",
    faqMarkdownA:
      "Ja. Stellen Sie einem beliebigen Website-Pfad /md/ voran — zum Beispiel /md/citation-style/apa oder /md/tools/word-counter. Datengetriebene Seiten liefern ihren vollständigen Inhalt, einschließlich der echten formatierten Beispiele, die aus der eigenen CSL-Definition jedes Stils erzeugt wurden. Handgeschriebene Prosaseiten (Ratgeber, Spickzettel) liefern eine Zusammenfassungskarte sowie die kanonische HTML-URL, da ihr Text in der Seite selbst liegt.",
    faqReuseQ:
      "Darf ich die Ausgabe von CitationEasy in meinem eigenen Produkt verwenden?",
    faqReuseA:
      "Ja. Eine formatierte Quellenangabe ist eine Tatsache über eine Quelle; verwenden Sie sie frei. Die CSL-Stildefinitionen selbst stammen aus dem Citation-Style-Language-Projekt unter der Lizenz CC BY-SA 3.0. Eine Nennung von CitationEasy wird geschätzt, ist aber nicht erforderlich.",
    missingHeading: "Fehlt etwas?",
    missingBody:
      "Wenn Ihr Agent einen Endpunkt, ein Feld oder ein Format benötigt, das hier fehlt, [sagen Sie uns Bescheid](/contact). Sie surfen lieber von Hand? [Starten Sie beim Zitiergenerator](/).",
  },

  // /quiz. The client component reads these as a prop rather than through a context:
  // it is one component, not the generator's nine, so there is nothing to drill through.
  quizPage: {
    metaTitle:
      "Welchen Zitierstil sollte ich verwenden? - Kostenloses Stil-Quiz",
    metaDescription:
      "Beantworten Sie ein paar kurze Fragen zu Ihrem Fachgebiet, der Art Ihrer Arbeit und Ihren Präferenzen, um den besten Zitierstil für Ihre Arbeit zu finden. APA, MLA, Chicago, Vancouver und mehr.",
    ogTitle: "Welchen Zitierstil sollte ich verwenden?",
    ogDescription:
      "Machen Sie unser kostenloses Quiz, um den besten Zitierstil für Ihre Arbeit zu finden.",
    breadcrumbHome: "CitationEasy",
    breadcrumbCurrent: "Stil-Quiz",
    backLink: "← Zurück zum Generator",
    heading: "Welchen Zitierstil sollte ich verwenden?",
    intro:
      "Beantworten Sie ein paar kurze Fragen, und wir empfehlen Ihnen den besten Zitierstil für Ihre Arbeit.",
    progress: "Frage {current} von {total}",
    qDiscipline: "Was ist Ihr Studienfach?",
    optSocialScience: "Psychologie / Sozialwissenschaften",
    optHumanities: "Literatur / Geisteswissenschaften",
    optHistory: "Geschichte / Kunstgeschichte",
    optMedical: "Medizin / Pflegewissenschaft / Gesundheitswesen",
    optEngineering: "Ingenieurwissenschaften / Informatik",
    optNaturalScience: "Naturwissenschaften (Biologie, Chemie)",
    optLaw: "Recht",
    optBusiness: "Wirtschaft / Volkswirtschaftslehre",
    qPaperType: "Welche Art von Arbeit schreiben Sie?",
    optResearch: "Forschungsarbeit oder Abschlussarbeit",
    optReview: "Literaturübersicht",
    optReport: "Laborbericht oder technischer Bericht",
    optEssay: "Aufsatz oder Hausaufgabe",
    optJournal: "Einreichung eines Zeitschriftenartikels",
    qRegion: "Wo befindet sich Ihre Hochschule?",
    optUs: "Vereinigte Staaten",
    optUk: "Vereinigtes Königreich / Australien",
    optCanada: "Kanada",
    optEurope: "Europa (außer Vereinigtes Königreich)",
    optOther: "Andere",
    qPreference: "Haben Sie eine Präferenz für das Zitierformat?",
    optAuthorDate: "Belege im Text (Autor, Jahr)",
    optNumeric: "Nummerierte Verweise [1]",
    optFootnotes: "Fußnoten",
    optNoPreference: "Keine Präferenz",
    styleVancouver: "Vancouver",
    styleAma: "AMA (American Medical Association)",
    styleIeee: "IEEE",
    styleBluebook: "Bluebook",
    styleCse: "CSE (Council of Science Editors)",
    styleChicagoNotes: "Chicago (Notes-Bibliography)",
    styleMla: "MLA (9. Auflage)",
    styleApa: "APA (7. Auflage)",
    styleHarvard: "Harvard",
    reasonVancouverMedical:
      "Vancouver ist der Standard für Fachzeitschriften in Medizin und Gesundheitswissenschaften und verwendet nummerierte Verweise.",
    reasonAma:
      "AMA wird in medizinischen Fachzeitschriften häufig verwendet und nutzt nummerierte Zitate.",
    reasonIeee:
      "IEEE ist der Standard-Zitierstil für Ingenieurwissenschaften, Informatik und IT.",
    reasonBluebook:
      "Das Bluebook ist der maßgebliche Zitierstil für juristisches Schreiben. Der Chicago-Stil ist die nächstliegende allgemein einsetzbare Alternative.",
    reasonCse: "CSE ist der Standard für Biologie und Naturwissenschaften.",
    reasonChicagoHistory:
      "Chicago Notes-Bibliography ist der Goldstandard für Geschichte und Kunstgeschichte und verwendet Fußnoten für Belege.",
    reasonChicagoHumanities:
      "Chicago Notes-Bibliography eignet sich gut für die Geisteswissenschaften dank seines auf Fußnoten basierenden Ansatzes.",
    reasonMla:
      "MLA ist der Standard für Literatur, Sprachen und geisteswissenschaftliche Fachgebiete.",
    reasonApaBusiness:
      "APA wird in den Bereichen Wirtschaft, Volkswirtschaftslehre und Management häufig verwendet.",
    reasonHarvard:
      "Die Harvard-Zitierweise ist der gängigste Stil an britischen und australischen Universitäten.",
    reasonChicagoFootnotes:
      "Chicago Notes-Bibliography verwendet Fußnoten, ideal für detaillierte Belege.",
    reasonVancouverNumeric:
      "Vancouver verwendet nummerierte Zitate und hält Ihren Text so übersichtlich und lesbar.",
    reasonApaDefault:
      "APA ist der am weitesten verbreitete Zitierstil in den Sozialwissenschaften, der Pädagogik und vielen anderen Fachgebieten.",
    recommendLabel: "Wir empfehlen",
    generateButton: "{style}-Quellenangaben erzeugen",
    retakeButton: "Quiz erneut starten",
    guidesLink: "Durchsuchen Sie unsere Zitier-Ratgeber für weitere Hilfe →",
    previousButton: "← Vorherige Frage",
  },

  // /search — the server-rendered site search.
  searchPage: {
    metaTitle: "CitationEasy durchsuchen",
    metaDescription:
      "Durchsuchen Sie alle Seiten von CitationEasy auf einmal: {count} Zitierstile, über 250 kostenlose Tools, Ratgeber, Seiten zum Zitieren, Stilvergleiche, Konverter und das Glossar.",
    keyword1: "Zitierstile durchsuchen",
    keyword2: "Zitier-Ratgeber finden",
    keyword3: "Suche nach Zitierwerkzeugen",
    ogDescription:
      "Durchsuchen Sie alle Seiten der Website auf einmal — Stile, Tools, Ratgeber, Seiten zum Zitieren, Vergleiche, Konverter und Glossarbegriffe.",
    ogImageSubtitle:
      "Jeder Zitierstil, jedes Tool, jeder Ratgeber, jede Anleitungsseite, jeder Vergleich, jeder Konverter und jeder Glossarbegriff — gemeinsam durchsucht.",
    ogKind: "Suche",
    breadcrumb: "Suche",
    heading: "CitationEasy durchsuchen",
    intro:
      "Jeder Zitierstil, jedes Tool, jeder Ratgeber, jede Anleitungsseite, jeder Vergleich, jeder Konverter und jeder Glossarbegriff der Website — gemeinsam durchsucht.",
    inputLabel: "Suchanfrage",
    inputPlaceholder:
      "z. B. einen Podcast in APA zitieren, hängender Einzug, DOI",
    submit: "Suchen",
    browseHeading: "Oder nach Bereich durchsuchen",
    browseStyles: "Zitierstile",
    browseStylesNote: "{count} Stile",
    browseTools: "Kostenlose Tools",
    browseToolsNote: "{count} Tools",
    browseHowTo: "So zitieren Sie jede Quelle",
    browseHowToNote: "{count} Seiten",
    browseGuides: "Ratgeber",
    browseGuidesNote: "{count} Ratgeber",
    browseGlossary: "Glossar",
    browseGlossaryNote: "{count} Begriffe",
    browseComparisons: "Stilvergleiche",
    browseComparisonsNote: "{count} Vergleiche",
    browseConverters: "Konverter",
    browseConvertersNote: "{count} Konverter",
    browseCheatSheets: "Spickzettel",
    browseCheatSheetsNote: "{count} Spickzettel",
    facetsLabel: "Nach Seitentyp filtern",
    facetAll: "Alle ({count})",
    noMatches: "Keine Seiten stimmen mit „{query}“ überein.",
    matchesOne: "{count} Seite stimmt mit „{query}“ überein.",
    matchesMany: "{count} Seiten stimmen mit „{query}“ überein.",
    pagerLabel: "Ergebnisseiten",
    pagerPrevious: "← Zurück",
    pagerNext: "Weiter →",
    pagerPosition: "Seite {page} von {total}",
    paletteLoading: "Suche läuft…",
    paletteError:
      "Die Suche ist derzeit nicht verfügbar. Versuchen Sie es erneut.",
    paletteViewAll: "Alle Ergebnisse für „{query}“ anzeigen",
    apiNote:
      "Sie entwickeln etwas? Derselbe Index ist als JSON unter [/api/v1/search](/api/v1/search?q=podcast) und über MCP verfügbar — siehe [die Agenten-Dokumentation](/ai).",
  },

  // /cite-this-page. The six FAQ answers used to exist twice — once in the layout for the
  // FAQPage markup and once in the page for the visible list — which is exactly the drift
  // this dictionary removes: both now read the same keys.
  citeThisPage: {
    metaTitle:
      "Diese Seite zitieren - Zitate mit einem Klick (Bookmarklet & Chrome-Erweiterung)",
    metaDescription:
      "Zitieren Sie jede Webseite mit einem Klick. Ziehen Sie das kostenlose Bookmarklet „Diese Seite zitieren“ in Ihre Lesezeichenleiste, oder installieren Sie die kostenlose Chrome-Erweiterung mit Symbolleisten-Schaltfläche und Zitieren per Rechtsklick. Funktioniert auch auf Chromebooks und eingeschränkten Browsern.",
    ogTitle: "Bookmarklet „Diese Seite zitieren“ und Chrome-Erweiterung",
    ogDescription:
      "Website-Quellenangaben mit einem Klick von jeder Seite — ein Drag-and-drop-Bookmarklet plus eine kostenlose Chrome-Erweiterung mit Zitieren per Rechtsklick.",
    breadcrumbRoot: "CitationEasy",
    breadcrumbSchemaCurrent: "Bookmarklet „Diese Seite zitieren“",
    appName: "Bookmarklet „Diese Seite zitieren“",
    appDescription:
      "Ein kostenloses Bookmarklet, das die Webseite, die Sie gerade lesen, mit einem Klick zitiert — es öffnet CitationEasy mit bereits ausgefüllter URL der Seite und formatiert die Quellenangabe dann in APA, MLA, Chicago und {count} weiteren Stilen.",
    breadcrumbLabel: "Brotkrümelnavigation",
    breadcrumbHome: "Startseite",
    breadcrumbCurrent: "Bookmarklet „Diese Seite zitieren“",
    heading: "Diese Seite zitieren: Bookmarklet und Chrome-Erweiterung",
    intro:
      "Zitieren Sie jede Webseite mit einem Klick — keine Registrierung nötig. Ziehen Sie das Bookmarklet unten in Ihre Lesezeichenleiste (keinerlei Installation), oder holen Sie sich die kostenlose Chrome-Erweiterung für eine Symbolleisten-Schaltfläche und Zitieren per Rechtsklick.",
    dragPrompt: "Ziehen Sie diese Schaltfläche in Ihre Lesezeichenleiste:",
    buttonTitle: "Ziehen Sie mich in Ihre Lesezeichenleiste",
    buttonLabel: "📑 Diese Seite zitieren",
    dontClick:
      "Klicken Sie hier nicht darauf — ziehen Sie sie zuerst in Ihre Lesezeichenleiste.",
    installHeading: "So installieren Sie es",
    step1Title: "Lesezeichenleiste anzeigen",
    step1Body:
      "Drücken Sie in den meisten Browsern Strg+Umschalt+B (Cmd+Umschalt+B auf dem Mac), um die Lesezeichenleiste einzublenden, damit Sie einen Ort haben, an dem Sie die Schaltfläche ablegen können.",
    step2Title: "Schaltfläche nach oben ziehen",
    step2Body:
      "Klicken und halten Sie die Schaltfläche „Diese Seite zitieren“ unten, ziehen Sie sie dann auf Ihre Lesezeichenleiste und lassen Sie los. Sie wird zu einem normalen Lesezeichen.",
    step3Title: "Auf jeder Seite anklicken",
    step3Body:
      "Klicken Sie beim Lesen eines Artikels, einer Nachricht oder einer Zeitschriftenseite auf Ihr neues Lesezeichen. CitationEasy öffnet sich mit bereits ausgefüllter URL der Seite und die Quellenangabe ist bereit zum Kopieren.",
    manualHeading: "Können Sie es nicht ziehen? Fügen Sie es manuell hinzu",
    manualBody:
      "Erstellen Sie ein neues Lesezeichen, nennen Sie es „Diese Seite zitieren“, und fügen Sie Folgendes als URL/Adresse ein:",
    howHeading: "So funktioniert es",
    howBody:
      "Das Bookmarklet führt keinen Code auf der Seite aus, die Sie gerade lesen. Es übergibt lediglich die Webadresse dieser Seite an CitationEasy, wo unser Server die öffentlichen Seitenmetadaten abruft und auswertet — derselbe vertrauenswürdige Weg wie beim manuellen Einfügen einer URL in den Generator. Das macht es sicher und vermeidet die Cross-Origin-Beschränkungen, die ältere seiteninterne Bookmarklets ausgebremst haben.",
    extensionHeading: "Lieber eine Chrome-Erweiterung?",
    extensionBody:
      "Die kostenlose CitationEasy-Erweiterung fügt eine Symbolleisten-Schaltfläche hinzu, die den aktuellen Tab zitiert, sowie Rechtsklick-Optionen, um einen Link — oder einen ausgewählten DOI oder eine ISBN — zu zitieren, ohne ihn zu öffnen. Wie das Bookmarklet führt sie keinen Code auf den Seiten aus, die Sie besuchen: Sie übergibt nur die URL an CitationEasy. Sie benötigt lediglich die Berechtigungen `activeTab` und `contextMenus` und funktioniert in Chrome, Edge, Brave und anderen Chromium-Browsern.",
    downloadButton: "⬇ Chrome-Erweiterung herunterladen (.zip)",
    install1: "Laden Sie die Erweiterung herunter und entpacken Sie sie.",
    install2:
      "Öffnen Sie `chrome://extensions` und aktivieren Sie den **Entwicklermodus** (oben rechts).",
    install3:
      "Klicken Sie auf **Entpackte Erweiterung laden** und wählen Sie den entpackten Ordner aus.",
    install4:
      "Heften Sie die CitationEasy-Schaltfläche an, und klicken Sie dann auf jeder Seite darauf, um sie zu zitieren.",
    faqHeading: "Häufig gestellte Fragen",
    faqWhatQ: "Was ist das Bookmarklet „Diese Seite zitieren“?",
    faqWhatA:
      "Es ist ein kleines Lesezeichen, das Sie in die Lesezeichenleiste Ihres Browsers ziehen. Wenn Sie es auf einer beliebigen Webseite anklicken, öffnet es CitationEasy in einem neuen Tab mit bereits ausgefüllter URL der Seite, sodass Sie mit einem Klick eine formatierte Quellenangabe erhalten — ohne Kopieren und Einfügen.",
    faqExtensionNeededQ: "Muss ich eine Erweiterung installieren?",
    faqExtensionNeededA:
      "Nein. Ein Bookmarklet ist nur ein Lesezeichen, es gibt also nichts zu installieren und keine Berechtigungen zu erteilen. Es funktioniert auf gesperrten Schul-Chromebooks und den meisten mobilen Browsern, in denen Erweiterungen blockiert sind. Wenn Sie eine Erweiterung bevorzugen, ist auf dieser Seite auch eine kostenlose Chrome-Erweiterung verfügbar.",
    faqChromeQ: "Gibt es eine Chrome-Erweiterung?",
    faqChromeA:
      "Ja. Die kostenlose CitationEasy-Chrome-Erweiterung fügt eine Symbolleisten-Schaltfläche hinzu, die den aktuellen Tab zitiert, sowie Rechtsklick-Optionen, um einen Link oder einen ausgewählten DOI/eine ISBN zu zitieren, ohne ihn zu öffnen. Sie führt keinen Code auf den besuchten Seiten aus und benötigt nur die Berechtigungen activeTab und contextMenus. Laden Sie sie von dieser Seite herunter und laden Sie sie über chrome://extensions; sie funktioniert in Chrome, Edge, Brave und anderen Chromium-Browsern.",
    faqAddQ: "Wie füge ich das Bookmarklet hinzu?",
    faqAddA:
      "Stellen Sie sicher, dass Ihre Lesezeichenleiste sichtbar ist, und ziehen Sie dann die Schaltfläche „Diese Seite zitieren“ auf dieser Seite dorthin. Wenn Ziehen nicht möglich ist (zum Beispiel auf dem Handy), erstellen Sie manuell ein neues Lesezeichen und fügen Sie den Bookmarklet-Code als URL ein.",
    faqSafeQ: "Ist es sicher? Liest es die Seite, auf der ich mich befinde?",
    faqSafeA:
      "Ja, es ist sicher. Das Bookmarklet führt keinen Code auf der Seite aus, die Sie gerade lesen — es sendet lediglich die Webadresse dieser Seite an CitationEasy, wo unser Server die öffentlichen Seitenmetadaten abruft und auswertet, genau wie beim manuellen Einfügen einer URL.",
    faqOtherSourcesQ: "Funktioniert es auch für andere Quellen als Websites?",
    faqOtherSourcesA:
      "Das Bookmarklet ist für Webseiten gedacht, aber der zugrunde liegende Link akzeptiert über den Parameter ?source= auch einen DOI, eine ISBN, eine PMID oder eine arXiv-ID — ein teilbarer „Diese zitieren“-Link funktioniert also auch für Zeitschriftenartikel und Bücher.",
    relatedHeading: "Verwandte Werkzeuge",
    relatedGenerator: "Zitiergenerator",
    relatedHowToCite: "So zitieren Sie eine Website",
    relatedTools: "Alle Werkzeuge",
  },

  // /shared-bibliography — opens a bibliography that travelled inside a share link.
  sharedBibliographyPage: {
    metaTitle:
      "Geteilte Bibliografie - Geteilte Quellenangaben ansehen und importieren",
    metaDescription:
      "Öffnen Sie eine Bibliografie, die jemand mit Ihnen über CitationEasy geteilt hat. Sehen Sie sich die formatierten Quellenangaben an, kopieren Sie sie, oder importieren Sie die gesamte Liste in Ihre eigene Bibliografie — kostenlos, ohne Konto, und die Daten liegen im Link selbst.",
    ogTitle: "Geteilte Bibliografie",
    ogDescription:
      "Sehen Sie sich eine geteilte Bibliografie an und importieren Sie sie. Die Quellenangaben reisen im Link selbst — nichts wird auf einem Server gespeichert, und Links laufen nie ab.",
    breadcrumbRoot: "CitationEasy",
    breadcrumbLabel: "Brotkrümelnavigation",
    breadcrumbHome: "Startseite",
    breadcrumbCurrent: "Geteilte Bibliografie",
    heading: "Geteilte Bibliografie",
    intro:
      "Jemand hat diese Quellenangaben mit Ihnen geteilt. Kopieren Sie sie, oder importieren Sie die gesamte Liste mit einem Klick in Ihre eigene CitationEasy-Bibliografie.",
    loading: "Geteilte Bibliografie wird geöffnet…",
    copyAll: "Alle kopieren",
    copied: "Kopiert",
    copyFailed: "Kopieren fehlgeschlagen",
    citationOne: "Quellenangabe",
    citationMany: "Quellenangaben",
    addAll: "Alle zu meiner Bibliografie hinzufügen",
    added: "{count} zu Ihrer Bibliografie hinzugefügt",
    skippedOne: "{count} Duplikat übersprungen",
    skippedMany: "{count} Duplikate übersprungen",
    openGenerator: "den Generator öffnen",
    emptyHeading: "Keine geteilte Bibliografie in diesem Link",
    emptyBody:
      "Diese Seite zeigt Bibliografien, die über CitationEasy geteilt wurden. Um Ihre zu teilen, öffnen Sie Ihre Bibliografie im [Zitiergenerator](/), wählen Sie **Exportieren → Freigabelink kopieren**, und senden Sie den Link an beliebige Personen. Die Quellenangaben reisen im Link selbst — nichts wird auf einem Server gespeichert, sodass Links für immer funktionieren und Ihre Daten privat bleiben.",
    decodeError: "Dieser Freigabelink konnte nicht geöffnet werden.",
  },

  // The 19 source types the 15 detailed styles show worked examples for
  // (`DetailedStyleData.examples[].sourceType`). 75 rows, 19 distinct values — so this
  // is a vocabulary, not per-page copy, and it belongs here rather than repeated in
  // every locale's `src/content/style-pages/<locale>.json`.
  //
  // Two casings for the same reason `howToCitePage.refList*Lower` has two: the pages
  // print the label as a heading ("Book") and inside a sentence ("a sample APA entry
  // for a book"). Lower-casing the translated noun in code is wrong in German, where
  // every one of these is capitalised, so the lower form is a real key a translator
  // fills rather than a `.toLowerCase()` call.
  //
  // The citations themselves — `referenceEntry` and `inTextCitation` — are NOT here and
  // are not translatable anywhere: they are worked examples in a specific style's own
  // punctuation. See `src/lib/localized-content.ts`.
  sourceTypes: {
    book: "Buch",
    bookLower: "Buch",
    bookChapter: "Buchkapitel",
    bookChapterLower: "Buchkapitel",
    case: "Rechtsfall",
    caseLower: "Rechtsfall",
    conferencePaper: "Konferenzbeitrag",
    conferencePaperLower: "Konferenzbeitrag",
    courtCase: "Gerichtsentscheidung",
    courtCaseLower: "Gerichtsentscheidung",
    federalStatute: "Bundesgesetz",
    federalStatuteLower: "Bundesgesetz",
    governmentDocument: "Regierungsdokument",
    governmentDocumentLower: "Regierungsdokument",
    governmentReport: "Regierungsbericht",
    governmentReportLower: "Regierungsbericht",
    journalArticle: "Zeitschriftenartikel",
    journalArticleLower: "Zeitschriftenartikel",
    lawReviewArticle: "Beitrag in einer juristischen Fachzeitschrift",
    lawReviewArticleLower: "Beitrag in einer juristischen Fachzeitschrift",
    legislation: "Gesetzgebung",
    legislationLower: "Gesetzgebung",
    newspaperArticle: "Zeitungsartikel",
    newspaperArticleLower: "Zeitungsartikel",
    patent: "Patent",
    patentLower: "Patent",
    podcast: "Podcast",
    podcastLower: "Podcast",
    preprint: "Preprint",
    preprintLower: "Preprint",
    standard: "Norm",
    standardLower: "Norm",
    thesis: "Abschlussarbeit",
    thesisLower: "Abschlussarbeit",
    website: "Website",
    websiteLower: "Website",
    youtubeVideo: "YouTube-Video",
    youtubeVideoLower: "YouTube-Video",
  },

  // Chrome shared by the five per-style guide routes — /examples, /format,
  // /in-text-citation, /reference-page and /annotated-bibliography, 15 styles each.
  //
  // One section rather than five copies: the CTA, the back link and the cross-links
  // between the five pages are the same sentences on every one of them, and duplicating
  // them per route would be five identical translation units instead of one.
  styleGuidePages: {
    backToStyle: "Zurück zu {style}",
    backToGenerator: "Zurück zum Zitiergenerator",
    ctaHeading: "Automatisch {style}-Quellenangaben erzeugen",
    ctaButton: "Jetzt in {style} zitieren →",
    faqHeading: "Häufig gestellte Fragen",
    resourcesHeading: "Weitere Ressourcen",
    relatedHeading: "Verwandte {style}-Ratgeber",
    resourceGenerator: "Kostenloser Zitiergenerator ({count} Stile)",
    resourceTools: "Kostenlose Schreibwerkzeuge",
    resourceGuides: "Alle Zitier-Ratgeber",
    resourceStyleGenerator: "{style}-Zitiergenerator",
    resourceExamples: "{style}-Zitierbeispiele",
    resourceFormat: "{style}-Formatierungsleitfaden",
    resourceInText: "{style}-Leitfaden für Belege im Text",
    resourceAnnotatedBib: "{style}-Leitfaden für kommentierte Bibliografien",
    resourceReferencePage: "{style}-{list}-Leitfaden",
    notFoundTitle: "Nicht gefunden",
  },

  // /examples/<style> — the copy-ready worked examples for all 15 detailed styles.
  examplesPage: {
    metaTitle: "{style}-Zitierbeispiele - Alle Quellenarten [2026]",
    metaDescription:
      "Sehen Sie formatierte {style}-Zitierbeispiele ({edition}) für Bücher, Zeitschriftenartikel, Websites und mehr. Kopierbereite {list}-Einträge und Belege im Text.",
    ogTitle: "{style}-Zitierbeispiele",
    ogDescription:
      "Kopierbereite {style}-Einträge für jede gängige Quellenart.",
    ogImageTitle: "{style}-Zitierbeispiele",
    ogKind: "{style} {edition}",
    keywordExamples: "{style}-Zitierbeispiele",
    keywordReference: "{style}-Literaturbeispiele",
    keywordInText: "{style}-Beispiele für Belege im Text",
    keywordList: "{style}-{listLower}-Beispiele",
    keywordHowTo: "wie man in {style} zitiert",
    breadcrumb: "{style}-Zitierbeispiele",
    heading: "{style}-Zitierbeispiele",
    lead: "Sehen Sie korrekt formatierte {style}-Zitierbeispiele ({edition}) für {count} gängige Quellenarten. Jedes Beispiel enthält sowohl den {listLower}-Eintrag als auch den zugehörigen Beleg im Text.",
    entryLabel: "{list}-Eintrag:",
    inTextLabel: "Beleg im Text:",
    ctaBody:
      "Sparen Sie sich die manuelle Formatierung. Fügen Sie einen DOI, eine URL oder eine ISBN in CitationEasy ein und erhalten Sie in Sekunden eine perfekt formatierte {style}-Quellenangabe.",
    faqLookQ: "Wie sieht eine {style}-Quellenangabe aus?",
    faqLookA:
      "Eine {style}-Quellenangabe umfasst einen {listLower}-Eintrag mit vollständigen Angaben zur Quelle sowie einen Beleg im Text. Das Format variiert je nach Quellenart. Eine Buchzitation in {style} ({edition}) enthält zum Beispiel Autor, Jahr, Titel und Verlag.",
    faqHowManyQ: "Wie viele {style}-Zitierbeispiele sind verfügbar?",
    faqHowManyA:
      "CitationEasy bietet {style}-Zitierbeispiele für {count} Quellenarten, darunter {types}. Jedes Beispiel enthält sowohl den {listLower}-Eintrag als auch den zugehörigen Beleg im Text.",
    faqCopyQ: "Kann ich diese {style}-Zitierbeispiele kopieren?",
    faqCopyA:
      "Ja, die {style}-Beispiele auf dieser Seite zeigen die genaue Formatierung für jede Quellenart. Sie können sie als Vorlage für Ihre eigenen Quellenangaben verwenden. Für eine automatische Formatierung nutzen Sie unseren kostenlosen {style}-Zitiergenerator.",
  },

  // /format/<style> — font, margins, spacing, title page and headers. The VALUES those
  // labels take are per-style prose and live in the overlay, not here.
  formatPage: {
    metaTitle:
      "{style}-Formatierungsleitfaden - Regeln zur Dokumentformatierung [2026]",
    metaDescription:
      "Vollständiger {style}-Formatierungsleitfaden ({edition}). Erfahren Sie die richtige Schriftart, Ränder, Zeilenabstand, Titelseite, Kopfzeilen und Formatierungsregeln für {listLower}.",
    ogTitle: "{style}-Dokumentformat",
    ogDescription:
      "Ränder, Schriftart, Zeilenabstand und Titelseite für eine {style}-Arbeit.",
    ogImageTitle: "{style}-Dokumentformat",
    ogImageSubtitle: "{margins} Ränder · {spacing}",
    ogKind: "{style} {edition}",
    keywordFormat: "{style}-Format",
    keywordPaperFormat: "{style}-Dokumentformat",
    keywordRules: "{style}-Formatierungsregeln",
    keywordMargins: "{style} Ränder Schriftart Zeilenabstand",
    keywordList: "{style}-{listLower}-Format",
    breadcrumb: "{style}-Formatierungsleitfaden",
    heading: "{style}-Formatierungsleitfaden",
    lead: "Befolgen Sie diese {style}-Formatierungsregeln ({edition}), damit Ihr Dokument den geforderten Standards entspricht. Dieser Leitfaden behandelt Schriftart, Ränder, Zeilenabstand, Titelseite, Kopfzeilen und die Formatierung von {listLower}.",
    paperHeading: "Dokumentformatierung",
    paperIntro:
      "Im Folgenden finden Sie die grundlegenden Formatierungsanforderungen für Arbeiten im {style}-Stil ({edition}).",
    labelFont: "Schriftart",
    labelFontSize: "Schriftgröße",
    labelMargins: "Ränder",
    labelSpacing: "Zeilenabstand",
    labelTitlePage: "Titelseite",
    labelHeaders: "Kopfzeilen / Seitenzahlen",
    listHeading: "{list}-Formatierung",
    listIntro: "Folgende Regeln gelten für {listLower} im {style}-Stil:",
    ctaBody:
      "Überlassen Sie CitationEasy die Formatierung. Fügen Sie einen DOI, eine URL oder eine ISBN ein und erhalten Sie in Sekunden eine perfekt formatierte {style}-Quellenangabe.",
    faqFontQ:
      "Welche Schriftart und -größe sollte ich für das {style}-Format verwenden?",
    faqFontA:
      "{style} ({edition}) verlangt {font}. Die Standardschriftgröße ist {fontSize}.",
    faqMarginsQ: "Welche Randanforderungen gelten für {style}?",
    faqMarginsA:
      "{style}-Arbeiten erfordern {margins}. Das gilt für den oberen, unteren, linken und rechten Rand jeder Seite.",
    faqTitlePageQ: "Erfordert {style} eine Titelseite?",
  },

  // /in-text-citation/<style> — the six in-text forms. The forms themselves
  // ("(Smith, 2024)") are per-style prose in the overlay.
  inTextCitationPage: {
    metaTitle:
      "{style}-Leitfaden für Belege im Text - Regeln und Beispiele [2026]",
    metaDescription:
      "Erfahren Sie, wie Sie {style}-Belege im Text ({edition}) formatieren. Regeln für einen Autor, mehrere Autoren, direkte Zitate, fehlenden Autor und fehlendes Datum, mit Beispielen.",
    ogTitle: "{style}-Belege im Text",
    ogDescription: "Jede Form des {style}-Belegs im Text, mit Beispielen.",
    ogImageTitle: "{style}-Belege im Text",
    ogImageSubtitle: "Ein Autor {singleAuthor} · drei oder mehr {threeOrMore}",
    ogKind: "{style} {edition}",
    keywordInText: "{style}-Beleg im Text",
    keywordExamples: "{style}-Beispiele für Belege im Text",
    keywordParenthetical: "{style}-Klammerbeleg",
    keywordRules: "{style}-Zitierregeln",
    keywordHowTo: "wie man im Text in {style} zitiert",
    breadcrumb: "{style}-Belege im Text",
    heading: "{style}-Belege im Text",
    lead: "Meistern Sie {style}-Belege im Text ({edition}) mit diesem vollständigen Leitfaden. Lernen Sie das richtige Format für jede Situation, einschließlich einem oder mehreren Autoren, direkten Zitaten und Quellen mit fehlenden Angaben.",
    rulesHeading: "Regeln für Belege im Text",
    rulesIntro:
      "Im Folgenden finden Sie die {style}-Regeln für Belege im Text für jede gängige Situation, der Sie beim Schreiben Ihrer Arbeit begegnen werden.",
    labelSingleAuthor: "Ein Autor",
    labelTwoAuthors: "Zwei Autoren",
    labelThreeOrMore: "Drei oder mehr Autoren",
    labelDirectQuote: "Direktes Zitat",
    labelNoAuthor: "Kein Autor",
    labelNoDate: "Kein Datum",
    examplesHeading: "Kurzbeispiele",
    examplesIntro:
      "Hier sind reale {style}-Beispiele für Belege im Text für verschiedene Quellenarten:",
    ctaBody:
      "Machen Sie sich keine Gedanken mehr über die Formatierung von Belegen im Text. Fügen Sie einen DOI, eine URL oder eine ISBN in CitationEasy ein und erhalten Sie sofort sowohl den {listLower}-Eintrag als auch den Beleg im Text.",
    faqFormatQ: "Wie formatiere ich einen {style}-Beleg im Text?",
    faqFormatA:
      "Für einen Autor in {style} verwenden Sie das Format {singleAuthor}. Für zwei Autoren verwenden Sie {twoAuthors}. Für drei oder mehr Autoren verwenden Sie {threeOrMore}.",
    faqQuoteQ: "Wie zitiere ich ein direktes Zitat in {style}?",
    faqQuoteA:
      "Für direkte Zitate in {style} ({edition}) verwenden Sie das Format: {directQuote}.",
    faqMissingQ: "Was, wenn es in {style} keinen Autor oder kein Datum gibt?",
    faqMissingA:
      "Wenn es in {style} keinen Autor gibt, verwenden Sie: {noAuthor}. Wenn es kein Datum gibt, verwenden Sie: {noDate}.",
  },

  // /reference-page/<style> — how to build the list at the back of the paper.
  //
  // The five `mistake*` strings carry `**bold**` inline markup because the lead-in of
  // each bullet is emphasised inside the sentence; they render through `InlineText`.
  referenceListPage: {
    metaTitle: "{style}-{list}-Leitfaden - Formatierungsregeln [2026]",
    metaDescription:
      "Erfahren Sie, wie der Abschnitt {listLower} im {style}-Stil ({edition}) formatiert wird. Schritt-für-Schritt-Formatierungsregeln, Beispiele und häufige Fehler, die Sie bei {fullName} vermeiden sollten.",
    ogTitle: "{style}-{listLower}",
    ogDescription: "So erstellen Sie {list} im {style}-Stil.",
    ogImageTitle: "{style}-{list}",
    ogKind: "{style} {edition}",
    breadcrumb: "So formatieren Sie {list} im {style}-Stil",
    heading: "So formatieren Sie {list} im {style}-Stil",
    lead: "Der Abschnitt {listLower} gehört zu den wichtigsten Bestandteilen jeder wissenschaftlichen Arbeit im {style}-Stil ({edition}) und liefert den Lesern die vollständigen Angaben zu jeder zitierten Quelle, sodass sie Ihre Belege auffinden und überprüfen können. Die korrekte Formatierung ist entscheidend für die wissenschaftliche Glaubwürdigkeit. Dieser Leitfaden behandelt die genauen Regeln, zeigt reale Beispiele und hebt die häufigsten Fehler hervor, die Studierende machen.",
    rulesHeading: "Formatierungsregeln",
    rulesIntro:
      "Beachten Sie folgende Regeln beim Erstellen von {listLower} im {style}-Stil:",
    exampleHeading: "Beispiel für {list}",
    exampleIntro:
      "So sieht der Abschnitt {listLower} im {style}-Stil mit Einträgen für verschiedene Quellenarten aus:",
    mistakesHeading: "Häufige Fehler, die Sie vermeiden sollten",
    mistakesIntro:
      "Das sind die fünf häufigsten Formatierungsfehler, die Studierende beim Erstellen von {listLower} im {style}-Stil machen:",
    mistakeIndent:
      "**Fehlender oder falscher hängender Einzug:** Jeder Eintrag sollte in der ersten Zeile linksbündig beginnen, alle folgenden Zeilen sollten um 1,27 cm eingerückt sein. Verwenden Sie die Absatzeinstellungen Ihres Textverarbeitungsprogramms, statt die Tabulatortaste zu drücken.",
    mistakeOrder:
      "**Falsche alphabetische Sortierung oder Reihenfolge:** Einträge müssen nach den {style}-Regeln geordnet werden. Ignorieren Sie Artikel wie „A“, „An“ und „The“ bei der alphabetischen Sortierung. Bei nummerierten Stilen ordnen Sie die Einträge in der Reihenfolge ihres ersten Auftretens im Text.",
    mistakeSpacing:
      "**Uneinheitlicher Zeilenabstand:** Überprüfen Sie den Zeilenabstand im Abschnitt {listLower} durchgehend. Der Wechsel zwischen einfachem und doppeltem Zeilenabstand ist ein häufiger Fehler, der leicht übersehen wird.",
    mistakeItalics:
      "**Falsche Verwendung von Kursivschrift:** Setzen Sie Titel eigenständiger Werke wie Bücher und Zeitschriften kursiv, aber nicht Artikel- oder Kapiteltitel. Prüfen Sie die {style}-Richtlinien, welche Elemente genau kursiv gesetzt werden sollen.",
    mistakeLinks:
      "**Fehlende oder defekte DOIs und URLs:** Geben Sie immer einen DOI an, wenn verfügbar, formatiert als klickbaren Link. Testen Sie jede URL vor der Abgabe Ihrer Arbeit, um sicherzustellen, dass sie zur richtigen Quelle führt.",
    ctaHeading: "{list} automatisch erzeugen",
    ctaBody:
      "Sparen Sie sich die manuelle Formatierung. Fügen Sie einen DOI, eine URL oder eine ISBN in CitationEasy ein und erhalten Sie in Sekunden eine perfekt formatierte {style}-Quellenangabe, bereit für den Abschnitt {listLower}.",
    faqWhatQ: "Was ist der Begriff {list} im {style}-Stil?",
    faqWhatA:
      "Der Begriff {list} im {style}-Stil bezeichnet den Abschnitt am Ende Ihrer Arbeit, der alle im Text zitierten Quellen auflistet, formatiert nach den Richtlinien von {fullName} ({edition}). So können Leser jede referenzierte Quelle auffinden und überprüfen.",
    faqFormatQ: "Wie formatiere ich den Abschnitt {listLower} im {style}-Stil?",
    faqFormatA:
      "Beginnen Sie auf einer neuen Seite, betiteln Sie sie mit „{list}“, und listen Sie alle in Ihrer Arbeit zitierten Quellen auf. Befolgen Sie die spezifischen {style}-Regeln für hängende Einzüge, Zeilenabstand, alphabetische oder numerische Reihenfolge und Kursivschrift.",
    faqDifferenceQ:
      "Was ist der Unterschied zwischen dem Abschnitt {listLower} und einer Bibliografie?",
    faqDifferenceA:
      "Der Abschnitt {listLower} umfasst nur die Quellen, die Sie direkt in Ihrer Arbeit zitiert haben, während eine Bibliografie auch konsultierte, aber nicht zitierte Quellen enthalten kann. In {style} ({edition}) heißt dieser Abschnitt „{list}“.",
  },

  // /annotated-bibliography/<style>. `whatTypes` carries `**bold**` inline markup.
  //
  // `exampleAnnotation` is a specimen annotation, not a citation: it is ordinary prose
  // about an imaginary source and translates like any other paragraph. The reference
  // entry printed above it does not — that comes from `DETAILED_STYLES` and stays as
  // the style wrote it.
  annotatedBibPage: {
    metaTitle:
      "{style}-Leitfaden für kommentierte Bibliografien & Beispiele [2026]",
    metaDescription:
      "Erfahren Sie, wie Sie eine kommentierte Bibliografie im {style}-Stil ({edition}) schreiben. Enthält Formatierungsregeln, Beispieleinträge und Tipps für kommentierte Bibliografien nach {fullName}.",
    ogTitle: "Kommentierte Bibliografie im {style}-Stil",
    ogDescription:
      "So formatieren Sie eine kommentierte Bibliografie im {style}-Stil.",
    ogImageTitle: "Kommentierte Bibliografie im {style}-Stil",
    ogKind: "{style} {edition}",
    breadcrumb: "Kommentierte Bibliografie im {style}-Stil",
    heading: "Kommentierte Bibliografie im {style}-Stil",
    lead: "Eine kommentierte Bibliografie ist eine Liste von Quellenangaben, die jeweils einen kurzen beschreibenden oder bewertenden Absatz enthalten. Im {style}-Format ({edition}) beginnt jeder Eintrag mit einem korrekt formatierten Beleg, gefolgt von einer Anmerkung, die die Quelle zusammenfasst, bewertet oder kommentiert. Dieser Leitfaden führt Sie durch die Regeln, die Formatierung und die Beispiele, die Sie für eine ausgefeilte kommentierte Bibliografie im {style}-Stil benötigen.",
    whatHeading: "Was ist eine kommentierte Bibliografie?",
    whatBody:
      "Eine kommentierte Bibliografie geht über einen gewöhnlichen Abschnitt {listLower} hinaus, indem sie nach jeder Quellenangabe einen kurzen Absatz hinzufügt. Diese Anmerkung gibt den Lesern Einblick in Inhalt, Qualität und Relevanz der Quelle. Lehrkräfte geben häufig kommentierte Bibliografien als Aufgabe, um Studierenden zu helfen, ihre Quellen kritisch zu bewerten und zu zeigen, wie jede Quelle zu ihrem Forschungsthema beiträgt.",
    whatTypes:
      "Es gibt drei gängige Arten von Anmerkungen: **beschreibende** Anmerkungen, die die Quelle zusammenfassen, **bewertende** Anmerkungen, die Qualität und Zuverlässigkeit des Werks einschätzen, und **kombinierte** Anmerkungen, die beides tun. Ihre Aufgabenstellung sollte angeben, welcher Typ zu verwenden ist.",
    rulesHeading: "Regeln für kommentierte Bibliografien im {style}-Stil",
    rulesIntro:
      "Beachten Sie folgende Regeln beim Formatieren einer kommentierten Bibliografie nach {fullName}:",
    exampleHeading:
      "Beispiel für einen Eintrag einer kommentierten Bibliografie",
    exampleIntro:
      "Im Folgenden sehen Sie ein Beispiel für einen Eintrag einer kommentierten Bibliografie im {style}-Stil für {sourceType}:",
    exampleAnnotation:
      "Diese Quelle bietet einen umfassenden Überblick über das Thema und präsentiert gut belegte Argumente, die durch empirische Belege gestützt werden. Der Autor stützt sich auf ein breites Spektrum an Primär- und Sekundärquellen, um eine überzeugende Argumentation aufzubauen. Die Methodik ist fundiert und die Ergebnisse werden klar dargestellt, was dieses Werk zu einer wertvollen Ressource für das Verständnis des Themas macht. Diese Quelle ist besonders nützlich, um den theoretischen Rahmen des Forschungsprojekts zu etablieren, und bietet Perspektiven, die andere Werke in der Bibliografie ergänzen.",
    tipsHeading: "Tipps zum Schreiben von {style}-Anmerkungen",
    tipSummarize:
      "Fassen Sie die Quelle zunächst in zwei bis drei Sätzen zusammen und fügen Sie dann Ihre Bewertung ihrer Stärken, Grenzen und Relevanz hinzu.",
    tipTone:
      "Verwenden Sie in Ihren Anmerkungen durchgehend die dritte Person und einen wissenschaftlichen Ton. Vermeiden Sie umgangssprachliche Formulierungen oder Überlegungen in der Ich-Form, sofern Ihre Lehrkraft nichts anderes verlangt.",
    tipLength:
      "Halten Sie jede Anmerkung zwischen 100 und 200 Wörtern. Fassen Sie sich kurz und konzentrieren Sie sich auf die wichtigsten Aspekte der Quelle.",
    tipRelevance:
      "Erklären Sie, wie sich die Quelle in Ihre gesamte Forschung einfügt. Beschreiben Sie, welche einzigartige Perspektive oder welche Belege sie beiträgt.",
    tipCitation:
      "Stellen Sie sicher, dass der Zitationsteil jedes Eintrags genau den Formatierungsregeln von {fullName} entspricht, bevor Sie die Anmerkung schreiben.",
    ctaHeading: "Erstellen Sie Ihre kommentierte Bibliografie automatisch",
    ctaBody:
      "Nutzen Sie den CitationEasy-Ersteller für kommentierte Bibliografien, um korrekt formatierte {style}-Quellenangaben zu erzeugen, und fügen Sie dann Ihre Anmerkungen hinzu.",
    ctaButton: "Ersteller für kommentierte Bibliografien öffnen →",
    faqWhatQ: "Was ist eine kommentierte Bibliografie im {style}-Stil?",
    faqWhatA:
      "Eine kommentierte Bibliografie im {style}-Stil ({edition}) ist eine Liste von Quellenangaben im {fullName}-Stil, jeweils gefolgt von einem kurzen Absatz, der die Quelle zusammenfasst, bewertet oder kommentiert. Sie hilft den Lesern, die Relevanz und Qualität jeder Quelle zu verstehen.",
    faqLengthQ: "Wie lang sollten Anmerkungen sein?",
    faqLengthA:
      "Anmerkungen umfassen in der Regel 100 bis 200 Wörter. Manche Aufgaben erfordern je nach erwarteter Analysetiefe kürzere oder längere Anmerkungen. Prüfen Sie stets die Vorgaben Ihrer Lehrkraft für genaue Längenanforderungen.",
    faqIncludeQ: "Was sollte eine Anmerkung enthalten?",
    faqIncludeA:
      "Eine Anmerkung sollte eine kurze Zusammenfassung des Hauptarguments oder der Ergebnisse der Quelle enthalten, eine Bewertung ihrer Glaubwürdigkeit und Nützlichkeit sowie eine Überlegung dazu, wie sie sich in Ihre Forschung einfügt. Manche Anmerkungen sind rein beschreibend, andere enthalten eine kritische Analyse.",
  },

  guidePage: {
    byline: "Von {author}",
    published: "Veröffentlicht am {date}",
    updated: "Aktualisiert am {date}",
    readingTime: "one:{count} Minute Lesezeit|other:{count} Minuten Lesezeit",
    wordCount: "one:{count} Wort|other:{count} Wörter",
  },

  // /guides hub, /guides/<category> index pages, and the chrome around each guide
  guideLibrary: {
    eyebrow: "Ratgeber-Bibliothek",
    browseByCategory: "Nach Kategorie durchsuchen",
    recentlyUpdated: "Kürzlich aktualisiert",
    allGuides: "Alle Ratgeber",
    guideCount: "one:{count} Ratgeber|other:{count} Ratgeber",
    updatedOn: "Aktualisiert am {date}",
    minRead: "one:{count} Min. Lesezeit|other:{count} Min. Lesezeit",
    onThisPage: "Auf dieser Seite",
    viewAllInCategory: "Alle Ratgeber zu {category}",
    moreInCategory: "Mehr zu {category}",
    aboutThisGuide: "Über diesen Ratgeber",
    provenance:
      "Verfasst von {author}. Veröffentlicht am {published}; zuletzt aktualisiert am {updated}.",
    categoryIntroSuffix:
      "Jeder Ratgeber ist kostenlos, für Studierende und Forschende geschrieben und datiert, damit Sie sehen, wann er zuletzt geprüft wurde.",
    photoCredit: "Foto: {author}, {source} ({license})",
    imageAlt: "Foto zur Illustration der Ratgeber zu {category}",
    imageSourceCommons: "Wikimedia Commons",
    apaStyleName: "APA-Stil",
    apaStyleDescription:
      "Websites, Zeitschriftenartikel, Bücher, Videos und mehr nach APA 7 zitieren – mit Beispielen für Literaturverzeichnis und Zitate im Text.",
    mlaStyleName: "MLA-Stil",
    mlaStyleDescription:
      "Alle gängigen Quellen nach MLA 9 zitieren, von Webseiten und Artikeln bis zu Gedichten, Theaterstücken, Filmen und Kursmaterialien.",
    chicagoStyleName: "Chicago-Stil",
    chicagoStyleDescription:
      "Ratgeber zum Chicago Manual of Style mit Fußnoten-Bibliographie und Autor-Jahr-Zitaten für Bücher, Artikel, Archive, Behördendokumente und Medien.",
    styleComparisonsName: "Stilvergleiche",
    styleComparisonsDescription:
      "Direkte Vergleiche von APA, MLA, Chicago, Harvard, Vancouver, IEEE und mehr sowie Rat, welchen Stil Ihr Fach erwartet.",
    researchMethodsName: "Forschungsmethoden",
    researchMethodsDescription:
      "Eine Forschungsarbeit planen, Literatur sichten, Notizen machen, Quellen bewerten, Plagiate vermeiden und ein Literaturverzeichnis aufbauen.",
    academicWritingName: "Wissenschaftliches Schreiben",
    academicWritingDescription:
      "Schreibratgeber für Abstracts, Einleitungen, Schlussfolgerungen, Thesen, Methodenteile und klare wissenschaftliche Prosa.",
    fieldSpecificName: "Fachspezifisch",
    fieldSpecificDescription:
      "Zitierratgeber für einzelne Fächer – von Psychologie, Geschichte und Literatur bis Medizin, Ingenieurwesen, Recht und Pflege.",
    sourceEvaluationName: "Quellenbewertung",
    sourceEvaluationDescription:
      "Glaubwürdigkeit beurteilen, wissenschaftliche von populären Quellen unterscheiden, Google Scholar und Bibliotheksdatenbanken nutzen und Voreingenommenheit erkennen.",
    citationHelpName: "Zitierhilfe",
    citationHelpDescription:
      "Antworten auf knifflige Fälle: fehlende Autoren oder Daten, mehrere Autoren, PDFs, KI-Tools, Gerichtsurteile sowie Import und Export von Zitaten.",
  },

  common: {
    backToGuides: "← Zurück zu den Ratgebern",
    free: "Kostenlos",
    noSignup: "Ohne Anmeldung",
    backToHome: "Zurück zum Zitiergenerator",
    inEnglish: "Auf Englisch",
    viewInEnglish: "Auf Englisch ansehen",
    availableInEnglish:
      "Diese Seite ist auf Englisch mit mehr Details verfügbar.",
    otherLanguages: "Weitere Sprachen",
    skipToContent: "Zum Inhalt springen",
    cookieNotice:
      "Wir verwenden Cookies für Analyse und Werbung, damit CitationEasy kostenlos bleibt.",
    privacyPolicy: "Datenschutzerklärung",
    accept: "Akzeptieren",
    decline: "Ablehnen",
    advertisement: "Anzeige",
  },
  legalPage: {
    backLink: "← Zurück zum Generator",
    effective: "Datum des Inkrafttretens:",
    updated: "Zuletzt aktualisiert:",
  },
};

export default de;
